import * as z from 'zod';

export const CreateProductRequest = z.object({
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the product was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the product was updated.')
          .optional()
          .nullable(),
        name: z.string().describe('Product name. Translatable.').min(1).max(80),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
          )
          .optional(),
        description: z
          .any()
          .describe(
            'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe(
            'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        directCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        allCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        costRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
          )
          .optional(),
        inventory: z
          .object({
            availabilityStatus: z
              .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
              .optional(),
            preorderStatus: z
              .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
              .optional(),
            preorderAvailability: z
              .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
              .optional(),
          })
          .describe(
            'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
          )
          .optional(),
        productType: z.enum(['PHYSICAL', 'DIGITAL']),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
          )
          .optional()
          .nullable(),
        breadcrumbsInfo: z
          .object({
            breadcrumbs: z
              .array(
                z.object({
                  categoryId: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  categoryName: z
                    .string()
                    .describe('Category name.')
                    .min(1)
                    .max(80)
                    .optional(),
                  categorySlug: z
                    .string()
                    .describe('Category slug.')
                    .min(1)
                    .max(100)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe(
            'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        actualPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum current selling prices across all product variants.'
          )
          .optional(),
        compareAtPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
          )
          .optional(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                ),
                            })
                            .describe(
                              "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                            )
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .optional(),
                            })
                            .describe(
                              'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          ),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.'),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    media: z
                      .intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                      .describe('Variant media.')
                      .optional(),
                    subscriptionPricesInfo: z
                      .object({
                        subscriptionPrices: z
                          .array(
                            z.object({
                              subscriptionId: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                )
                                .optional(),
                              pricePerUnit: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                    )
                                    .optional(),
                                  description: z
                                    .string()
                                    .describe(
                                      'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Price per unit info.')
                                .optional(),
                            })
                          )
                          .max(6)
                          .optional(),
                      })
                      .describe(
                        'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    inventoryStatus: z
                      .object({
                        inStock: z
                          .boolean()
                          .describe('Whether the variant is in stock.')
                          .optional(),
                        preorderEnabled: z
                          .boolean()
                          .describe(
                            'Whether preorder is enabled for this variant.'
                          )
                          .optional(),
                      })
                      .describe('Variant inventory status.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000),
          })
          .describe(
            'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
          ),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z.enum([
                      'UNDEFINED',
                      'DAY',
                      'WEEK',
                      'MONTH',
                      'YEAR',
                    ]),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                        z.xor([
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        variantSummary: z
          .object({
            variantCount: z
              .number()
              .int()
              .describe('The total number of variants for the product.')
              .optional(),
          })
          .describe('The total number of variants for the product.')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe(
      'Product to create.\n\nAt least 1 variant must be provided, and each variant must have an item in the `choices` field for every item in `options`.\nIf `options` is empty, 1 default variant must be provided with an empty `choices` list.'
    ),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const CreateProductResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Product ID.')
      .min(1)
      .max(36)
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the product was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the product was updated.')
      .optional()
      .nullable(),
    name: z
      .string()
      .describe('Product name. Translatable.')
      .min(1)
      .max(80)
      .optional()
      .nullable(),
    slug: z
      .string()
      .describe(
        'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
      )
      .optional()
      .nullable(),
    url: z
      .string()
      .describe(
        'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
      )
      .optional(),
    description: z
      .any()
      .describe(
        'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
      )
      .optional(),
    plainDescription: z
      .string()
      .describe(
        'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
      )
      .max(16000)
      .optional()
      .nullable(),
    visible: z
      .boolean()
      .describe(
        "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
      )
      .optional()
      .nullable(),
    visibleInPos: z
      .boolean()
      .describe(
        'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
      )
      .optional()
      .nullable(),
    media: z
      .object({
        main: z
          .intersection(
            z.object({
              altText: z
                .string()
                .describe('Image alt text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              displayName: z
                .string()
                .describe(
                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                )
                .max(80)
                .optional()
                .nullable(),
              mediaType: z
                .enum(['IMAGE', 'VIDEO'])
                .describe('Media type.')
                .optional(),
              thumbnail: z
                .object({
                  url: z.string().describe('Thumbnail url.').url().optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Thumbnail height.')
                    .optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Thumbnail width.')
                    .optional(),
                  altText: z
                    .string()
                    .describe('Thumbnail alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              uploadId: z
                .string()
                .describe('ID used to upload media to Wix Media Manager.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            }),
            z.intersection(
              z.xor([
                z.object({
                  _id: z.never().optional(),
                  url: z.never().optional(),
                }),
                z.object({
                  url: z.never().optional(),
                  _id: z
                    .string()
                    .describe(
                      'Set media by ID of an existing file in Wix Media Manager.'
                    )
                    .min(1)
                    .max(200),
                }),
                z.object({
                  _id: z.never().optional(),
                  url: z
                    .string()
                    .describe('Set media using an external media URL.')
                    .url(),
                }),
              ]),
              z.xor([
                z.object({
                  image: z.never().optional(),
                  video: z.never().optional(),
                }),
                z.object({
                  video: z.never().optional(),
                  image: z
                    .string()
                    .describe(
                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                    ),
                }),
                z.object({
                  image: z.never().optional(),
                  video: z
                    .string()
                    .describe(
                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                    ),
                }),
              ])
            )
          )
          .describe(
            'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
          )
          .optional(),
        itemsInfo: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z
                      .enum(['IMAGE', 'VIDEO'])
                      .describe('Media type.')
                      .optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
              )
              .max(50)
              .optional(),
          })
          .describe(
            'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
      })
      .describe(
        'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
      )
      .optional(),
    seoData: z
      .object({
        tags: z
          .array(
            z.object({
              type: z
                .string()
                .describe(
                  'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                )
                .optional(),
              props: z
                .record(z.string(), z.any())
                .describe(
                  'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                )
                .optional()
                .nullable(),
              meta: z
                .record(z.string(), z.any())
                .describe(
                  'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                )
                .optional()
                .nullable(),
              children: z
                .string()
                .describe(
                  'SEO tag inner content. For example, `<title> inner content </title>`.'
                )
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                )
                .optional(),
              disabled: z
                .boolean()
                .describe(
                  "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                )
                .optional(),
            })
          )
          .optional(),
        settings: z
          .object({
            preventAutoRedirect: z
              .boolean()
              .describe(
                'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
              )
              .optional(),
            keywords: z
              .array(
                z.object({
                  term: z.string().describe('Keyword value.').optional(),
                  isMain: z
                    .boolean()
                    .describe('Whether the keyword is the main focus keyword.')
                    .optional(),
                  origin: z
                    .string()
                    .describe(
                      'The source that added the keyword terms to the SEO settings.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe('SEO general settings.')
          .optional(),
      })
      .describe('Product SEO data.')
      .optional(),
    taxGroupId: z
      .string()
      .describe(
        '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    options: z
      .array(
        z.intersection(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of a customization with `customizationType: PRODUCT_OPTION`.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Option name.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            optionRenderType: z
              .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
              .describe('Option render type.')
              .optional(),
            key: z
              .string()
              .describe(
                'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
              )
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({ choicesSettings: z.never().optional() }),
            z.object({
              choicesSettings: z
                .object({
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          linkedMedia: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(15)
                            .optional(),
                          choiceType: z
                            .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                            .describe('Choice type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          name: z
                            .string()
                            .describe(
                              'Choice name. For text choices, this field also contains the choice value.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          inStock: z
                            .boolean()
                            .describe(
                              'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                            )
                            .optional(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({ colorCode: z.never().optional() }),
                          z.object({
                            colorCode: z
                              .string()
                              .describe(
                                'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                              )
                              .min(3)
                              .max(20),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                })
                .describe('Choices settings.'),
            }),
          ])
        )
      )
      .max(6)
      .optional(),
    modifiers: z
      .array(
        z.intersection(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of a customization with `customizationType: MODIFIER`.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Modifier title.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            modifierRenderType: z
              .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
              .describe('Modifier render type.')
              .optional(),
            mandatory: z
              .boolean()
              .describe('Whether customer input is required for this modifier.')
              .optional(),
            key: z
              .string()
              .describe(
                'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
              )
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({
              freeTextSettings: z.never().optional(),
              choicesSettings: z.never().optional(),
            }),
            z.object({
              choicesSettings: z.never().optional(),
              freeTextSettings: z
                .object({
                  minCharCount: z
                    .number()
                    .int()
                    .describe('Minimum number of characters.')
                    .optional(),
                  maxCharCount: z
                    .number()
                    .int()
                    .describe('Maximum number of characters.')
                    .max(500)
                    .optional(),
                  defaultAddedPrice: z
                    .string()
                    .describe(
                      "Default amount to be added to the product's price."
                    )
                    .optional()
                    .nullable(),
                  title: z
                    .string()
                    .describe('Title of the text to be input by the customer.')
                    .min(1)
                    .max(150)
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(150)
                    .optional(),
                })
                .describe('Free text modifier settings.'),
            }),
            z.object({
              freeTextSettings: z.never().optional(),
              choicesSettings: z
                .object({
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          linkedMedia: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(15)
                            .optional(),
                          choiceType: z
                            .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                            .describe('Choice type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          name: z
                            .string()
                            .describe('Choice name.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          addedPrice: z
                            .string()
                            .describe('Added price.')
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({ colorCode: z.never().optional() }),
                          z.object({
                            colorCode: z
                              .string()
                              .describe(
                                'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                              )
                              .min(3)
                              .max(20),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                })
                .describe('Choice settings.'),
            }),
          ])
        )
      )
      .max(10)
      .optional(),
    brand: z
      .object({
        _id: z
          .string()
          .describe('Brand ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Brand name.').max(50).optional().nullable(),
      })
      .describe(
        "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
      )
      .optional(),
    infoSections: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Info section ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          uniqueName: z
            .string()
            .describe(
              'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
            )
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          description: z
            .any()
            .describe(
              'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
      )
      .max(10)
      .optional(),
    ribbon: z
      .object({
        _id: z
          .string()
          .describe('Ribbon ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        name: z.string().describe('Ribbon name.').max(30).optional().nullable(),
      })
      .describe(
        "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
      )
      .optional(),
    directCategoriesInfo: z
      .object({
        categories: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                )
                .min(0)
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(2000)
          .optional(),
      })
      .describe(
        'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    allCategoriesInfo: z
      .object({
        categories: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                )
                .min(0)
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(2000)
          .optional(),
      })
      .describe(
        'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    mainCategoryId: z
      .string()
      .describe(
        'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    costRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
      )
      .optional(),
    inventory: z
      .object({
        availabilityStatus: z
          .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
          .describe('Current availability status.')
          .optional(),
        preorderStatus: z
          .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
          .describe('Current preorder status.')
          .optional(),
        preorderAvailability: z
          .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
          .describe('Preorder availability status.')
          .optional(),
      })
      .describe(
        'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
      )
      .optional(),
    productType: z
      .enum(['PHYSICAL', 'DIGITAL'])
      .describe(
        'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
      )
      .optional(),
    handle: z
      .string()
      .describe(
        "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
      )
      .min(1)
      .max(100)
      .optional()
      .nullable(),
    currency: z
      .string()
      .describe(
        'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
      )
      .optional()
      .nullable(),
    breadcrumbsInfo: z
      .object({
        breadcrumbs: z
          .array(
            z.object({
              categoryId: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              categoryName: z
                .string()
                .describe('Category name.')
                .min(1)
                .max(80)
                .optional(),
              categorySlug: z
                .string()
                .describe('Category slug.')
                .min(1)
                .max(100)
                .optional(),
            })
          )
          .max(5)
          .optional(),
      })
      .describe(
        'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    actualPriceRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Minimum and maximum current selling prices across all product variants.'
      )
      .optional(),
    compareAtPriceRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
      )
      .optional(),
    variantsInfo: z
      .object({
        variants: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                visible: z
                  .boolean()
                  .describe(
                    "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe('Variant SKU (stock keeping unit).')
                  .min(1)
                  .max(40)
                  .optional()
                  .nullable(),
                barcode: z
                  .string()
                  .describe('Variant barcode.')
                  .min(1)
                  .max(40)
                  .optional()
                  .nullable(),
                choices: z
                  .array(
                    z.object({
                      optionChoiceIds: z
                        .object({
                          optionId: z
                            .string()
                            .describe('Option ID.')
                            .min(1)
                            .max(36)
                            .optional(),
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                        )
                        .optional(),
                      optionChoiceNames: z
                        .object({
                          optionName: z
                            .string()
                            .describe('Option name.')
                            .min(1)
                            .optional(),
                          choiceName: z
                            .string()
                            .describe('Choice name.')
                            .min(1)
                            .optional(),
                          renderType: z
                            .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                            .describe(
                              'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(6)
                  .optional(),
                price: z
                  .object({
                    actualPrice: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        "Variant's current selling price. Must be greater than or equal to 0."
                      )
                      .optional(),
                    compareAtPrice: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                      )
                      .optional(),
                  })
                  .describe('Variant price.')
                  .optional(),
                revenueDetails: z
                  .object({
                    cost: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe('Item cost.')
                      .optional(),
                    profit: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                      )
                      .optional(),
                    profitMargin: z
                      .number()
                      .describe(
                        'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                      )
                      .min(0)
                      .max(1)
                      .optional(),
                  })
                  .describe(
                    'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                  )
                  .optional(),
                media: z
                  .intersection(
                    z.object({
                      altText: z
                        .string()
                        .describe('Image alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                      displayName: z
                        .string()
                        .describe(
                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                        )
                        .max(80)
                        .optional()
                        .nullable(),
                      mediaType: z
                        .enum(['IMAGE', 'VIDEO'])
                        .describe('Media type.')
                        .optional(),
                      thumbnail: z
                        .object({
                          url: z
                            .string()
                            .describe('Thumbnail url.')
                            .url()
                            .optional(),
                          height: z
                            .number()
                            .int()
                            .describe('Thumbnail height.')
                            .optional(),
                          width: z
                            .number()
                            .int()
                            .describe('Thumbnail width.')
                            .optional(),
                          altText: z
                            .string()
                            .describe('Thumbnail alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      uploadId: z
                        .string()
                        .describe(
                          'ID used to upload media to Wix Media Manager.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.intersection(
                      z.xor([
                        z.object({
                          _id: z.never().optional(),
                          url: z.never().optional(),
                        }),
                        z.object({
                          url: z.never().optional(),
                          _id: z
                            .string()
                            .describe(
                              'Set media by ID of an existing file in Wix Media Manager.'
                            )
                            .min(1)
                            .max(200),
                        }),
                        z.object({
                          _id: z.never().optional(),
                          url: z
                            .string()
                            .describe('Set media using an external media URL.')
                            .url(),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          image: z.never().optional(),
                          video: z.never().optional(),
                        }),
                        z.object({
                          video: z.never().optional(),
                          image: z
                            .string()
                            .describe(
                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                            ),
                        }),
                        z.object({
                          image: z.never().optional(),
                          video: z
                            .string()
                            .describe(
                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                            ),
                        }),
                      ])
                    )
                  )
                  .describe('Variant media.')
                  .optional(),
                subscriptionPricesInfo: z
                  .object({
                    subscriptionPrices: z
                      .array(
                        z.object({
                          subscriptionId: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                            )
                            .optional(),
                          pricePerUnit: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Price per unit info.')
                            .optional(),
                        })
                      )
                      .max(6)
                      .optional(),
                  })
                  .describe(
                    'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                inventoryStatus: z
                  .object({
                    inStock: z
                      .boolean()
                      .describe('Whether the variant is in stock.')
                      .optional(),
                    preorderEnabled: z
                      .boolean()
                      .describe('Whether preorder is enabled for this variant.')
                      .optional(),
                  })
                  .describe('Variant inventory status.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  physicalProperties: z.never().optional(),
                  digitalProperties: z.never().optional(),
                }),
                z.object({
                  digitalProperties: z.never().optional(),
                  physicalProperties: z
                    .object({
                      weight: z
                        .number()
                        .describe(
                          'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                        )
                        .min(0)
                        .max(999999999.99)
                        .optional()
                        .nullable(),
                      pricePerUnit: z
                        .object({
                          settings: z
                            .object({
                              quantity: z
                                .number()
                                .describe(
                                  'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                )
                                .min(0.01)
                                .max(999999999.99)
                                .optional(),
                              measurementUnit: z
                                .enum([
                                  'UNSPECIFIED',
                                  'ML',
                                  'CL',
                                  'L',
                                  'CBM',
                                  'MG',
                                  'G',
                                  'KG',
                                  'MM',
                                  'CM',
                                  'M',
                                  'SQM',
                                  'OZ',
                                  'LB',
                                  'FLOZ',
                                  'PT',
                                  'QT',
                                  'GAL',
                                  'IN',
                                  'FT',
                                  'YD',
                                  'SQFT',
                                ])
                                .describe(
                                  'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                )
                                .optional(),
                            })
                            .describe(
                              'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                            )
                            .optional(),
                          value: z
                            .string()
                            .describe(
                              "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                            )
                            .optional(),
                          description: z
                            .string()
                            .describe(
                              'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                        )
                        .optional(),
                      productDimensions: z
                        .object({
                          length: z
                            .string()
                            .describe(
                              'Length. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          width: z
                            .string()
                            .describe(
                              'Width. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          height: z
                            .string()
                            .describe(
                              'Height. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          unit: z
                            .enum([
                              'UNKNOWN_DIMENSIONS_UNIT',
                              'MM',
                              'CM',
                              'M',
                              'IN',
                              'FT',
                              'YD',
                            ])
                            .describe(
                              'Unit of measurement for dimensions (length, width, height).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Product dimensions (length, width, height of the physical product).'
                        )
                        .optional(),
                      packageDimensions: z
                        .object({
                          length: z
                            .string()
                            .describe(
                              'Length. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          width: z
                            .string()
                            .describe(
                              'Width. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          height: z
                            .string()
                            .describe(
                              'Height. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          unit: z
                            .enum([
                              'UNKNOWN_DIMENSIONS_UNIT',
                              'MM',
                              'CM',
                              'M',
                              'IN',
                              'FT',
                              'YD',
                            ])
                            .describe(
                              'Unit of measurement for dimensions (length, width, height).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Package dimensions (length, width, height of the shipping package).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Physical properties. Must be passed when `productType: PHYSICAL`'
                    ),
                }),
                z.object({
                  physicalProperties: z.never().optional(),
                  digitalProperties: z
                    .object({
                      digitalFile: z
                        .object({
                          _id: z
                            .string()
                            .describe('Media ID in media manager.')
                            .min(1)
                            .max(100)
                            .optional(),
                          fileName: z
                            .string()
                            .describe('Original file name.')
                            .min(1)
                            .max(1000)
                            .optional(),
                          fileSize: z
                            .string()
                            .describe('Original file size.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          fileType: z
                            .enum([
                              'UNSPECIFIED',
                              'SECURE_PICTURE',
                              'SECURE_VIDEO',
                              'SECURE_DOCUMENT',
                              'SECURE_MUSIC',
                              'SECURE_ARCHIVE',
                              'SECURE_RAW',
                            ])
                            .describe('File type.')
                            .optional(),
                        })
                        .describe(
                          'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Digital properties. Must be passed when `productType: DIGITAL`'
                    ),
                }),
              ])
            )
          )
          .min(1)
          .max(1000)
          .optional(),
      })
      .describe(
        'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
      )
      .optional(),
    purchaseEligibility: z
      .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
      .describe('Purchase eligibility settings for the product.')
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags')
      .optional(),
    subscriptionDetails: z
      .object({
        subscriptions: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe('Subscription title.')
                  .min(1)
                  .max(20)
                  .optional(),
                description: z
                  .string()
                  .describe('Subscription description.')
                  .max(60)
                  .optional()
                  .nullable(),
                visible: z
                  .boolean()
                  .describe(
                    'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe(
                    'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                discount: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['AMOUNT', 'PERCENT'])
                        .describe('Discount type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        amountOff: z.never().optional(),
                        percentOff: z.never().optional(),
                      }),
                      z.object({
                        percentOff: z.never().optional(),
                        amountOff: z
                          .string()
                          .describe(
                            'Amount to discount from the variant discounted_price.'
                          ),
                      }),
                      z.object({
                        amountOff: z.never().optional(),
                        percentOff: z
                          .number()
                          .describe(
                            'Percentage to discount from variant discounted_price.'
                          )
                          .min(0.1)
                          .max(99.9),
                      }),
                    ])
                  )
                  .describe(
                    'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  autoRenewal: z.never().optional(),
                  billingCycles: z.never().optional(),
                }),
                z.object({
                  billingCycles: z.never().optional(),
                  autoRenewal: z
                    .boolean()
                    .describe(
                      'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                    ),
                }),
                z.object({
                  autoRenewal: z.never().optional(),
                  billingCycles: z
                    .number()
                    .int()
                    .describe(
                      'Number of billing cycles before subscription ends.'
                    )
                    .min(2)
                    .max(999),
                }),
              ])
            )
          )
          .min(1)
          .max(6)
          .optional(),
        allowOneTimePurchases: z
          .boolean()
          .describe(
            'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
          )
          .optional()
          .nullable(),
      })
      .describe(
        "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
      )
      .optional(),
    variantSummary: z
      .object({
        variantCount: z
          .number()
          .int()
          .describe('The total number of variants for the product.')
          .optional(),
      })
      .describe('The total number of variants for the product.')
      .optional(),
  }),
  z.xor([
    z.object({ physicalProperties: z.never().optional() }),
    z.object({
      physicalProperties: z
        .object({
          pricePerUnit: z
            .object({
              quantity: z
                .number()
                .describe(
                  'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                )
                .min(0.01)
                .max(999999999.99)
                .optional(),
              measurementUnit: z
                .enum([
                  'UNSPECIFIED',
                  'ML',
                  'CL',
                  'L',
                  'CBM',
                  'MG',
                  'G',
                  'KG',
                  'MM',
                  'CM',
                  'M',
                  'SQM',
                  'OZ',
                  'LB',
                  'FLOZ',
                  'PT',
                  'QT',
                  'GAL',
                  'IN',
                  'FT',
                  'YD',
                  'SQFT',
                ])
                .describe(
                  'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                )
                .optional(),
            })
            .describe(
              'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
            )
            .optional(),
          fulfillerId: z
            .string()
            .describe('Fulfiller ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          shippingWeightRange: z
            .object({
              minValue: z
                .number()
                .describe(
                  'Minimum weight across all variants associated with this product.'
                )
                .min(0)
                .max(999999999.99)
                .optional(),
              maxValue: z
                .number()
                .describe(
                  'Maximum weight across all variants associated with this product.'
                )
                .min(0)
                .max(999999999.99)
                .optional(),
            })
            .describe(
              'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
            )
            .optional(),
          pricePerUnitRange: z
            .object({
              minValue: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                    )
                    .optional(),
                  description: z
                    .string()
                    .describe(
                      'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum price per unit across all variants.')
                .optional(),
              maxValue: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                    )
                    .optional(),
                  description: z
                    .string()
                    .describe(
                      'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum price per unit across all variants.')
                .optional(),
            })
            .describe(
              'Product price per unit range. The minimum and maximum price per unit of all the variants.'
            )
            .optional(),
          weightMeasurementUnitInfo: z
            .object({
              weightMeasurementUnit: z
                .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                .describe('Weight measurement unit.')
                .optional(),
            })
            .describe(
              'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          deliveryProfileId: z
            .string()
            .describe('Delivery profile ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
        ),
    }),
  ])
);
export const CreateProductWithInventoryRequest = z.object({
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product with inventory.'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Product name. Translatable.').min(1).max(80),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            'Whether the product is visible to site visitors.\n\nDefault: `true`'
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe('Product media items.')
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product brand.\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product ribbon.\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct category, which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productType: z.enum(['PHYSICAL', 'DIGITAL']),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the variant is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          ),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.'),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    inventoryItem: z
                      .intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe(
                              'Inventory item ID.\nIf you want to update existing inventory item you must provide id,\notherwise new inventory item with different id will be created.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          revision: z
                            .string()
                            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                            .describe('Inventory item revision.')
                            .optional()
                            .nullable(),
                          preorderInfo: z
                            .object({
                              enabled: z
                                .boolean()
                                .describe(
                                  "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                                )
                                .optional()
                                .nullable(),
                              message: z
                                .string()
                                .describe(
                                  'Message displayed to customers when the item is out of stock and preorder is enabled.'
                                )
                                .min(1)
                                .max(250)
                                .optional()
                                .nullable(),
                              limit: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                                )
                                .min(0)
                                .max(100000)
                                .optional()
                                .nullable(),
                              counter: z
                                .number()
                                .int()
                                .describe(
                                  'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                                )
                                .min(0)
                                .max(99999)
                                .optional()
                                .nullable(),
                              quantity: z
                                .number()
                                .int()
                                .describe(
                                  'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Item preorder info.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            inStock: z.never().optional(),
                            quantity: z.never().optional(),
                          }),
                          z.object({
                            quantity: z.never().optional(),
                            inStock: z
                              .boolean()
                              .describe(
                                'Tracking method - in stock.\n\nWhen set to `true`, the item is available for sale without a quantity limit.'
                              ),
                          }),
                          z.object({
                            inStock: z.never().optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe(
                                'Tracking method - quantity left in inventory.\n\nQuantity can be negative when decrementing inventory for an order that has already been paid.'
                              )
                              .min(-99999)
                              .max(99999),
                          }),
                        ])
                      )
                      .describe(
                        'Inventory item of the variant on the default location.'
                      )
                      .optional(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                ),
                            })
                            .describe('Option and choice IDs.')
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .optional(),
                            })
                            .describe('Option and choice names.')
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000)
              .optional(),
          })
          .describe(
            "Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nVariants aren't auto-generated. You must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants)."
          ),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z.enum([
                      'UNDEFINED',
                      'DAY',
                      'WEEK',
                      'MONTH',
                      'YEAR',
                    ]),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                        z.xor([
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe(
      'Product to create with inventory.\n\nAt least one variant must be provided and each variant must have relevant item in `choices` field for every item in `options`.\nIf `options` is empty one default variant must be provided with empty `choices` list.'
    ),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return inventory entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const CreateProductWithInventoryResponse = z.object({
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the product was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the product was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Product name. Translatable.')
          .min(1)
          .max(80)
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
          )
          .optional(),
        description: z
          .any()
          .describe(
            'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z
                    .enum(['IMAGE', 'VIDEO'])
                    .describe('Media type.')
                    .optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe(
            'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .describe('Option render type.')
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({ choicesSettings: z.never().optional() }),
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .describe('Media type.')
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .describe('Choice type.')
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .describe('Modifier render type.')
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z.never().optional(),
                }),
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .describe('Media type.')
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .describe('Choice type.')
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        directCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        allCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        costRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
          )
          .optional(),
        inventory: z
          .object({
            availabilityStatus: z
              .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
              .describe('Current availability status.')
              .optional(),
            preorderStatus: z
              .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
              .describe('Current preorder status.')
              .optional(),
            preorderAvailability: z
              .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
              .describe('Preorder availability status.')
              .optional(),
          })
          .describe(
            'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
          )
          .optional(),
        productType: z
          .enum(['PHYSICAL', 'DIGITAL'])
          .describe(
            'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
          )
          .optional(),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
          )
          .optional()
          .nullable(),
        breadcrumbsInfo: z
          .object({
            breadcrumbs: z
              .array(
                z.object({
                  categoryId: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  categoryName: z
                    .string()
                    .describe('Category name.')
                    .min(1)
                    .max(80)
                    .optional(),
                  categorySlug: z
                    .string()
                    .describe('Category slug.')
                    .min(1)
                    .max(100)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe(
            'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        actualPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum current selling prices across all product variants.'
          )
          .optional(),
        compareAtPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
          )
          .optional(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36)
                                .optional(),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            })
                            .describe(
                              "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                            )
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .describe(
                                  'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          )
                          .optional(),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.')
                      .optional(),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    media: z
                      .intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z
                            .enum(['IMAGE', 'VIDEO'])
                            .describe('Media type.')
                            .optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                      .describe('Variant media.')
                      .optional(),
                    subscriptionPricesInfo: z
                      .object({
                        subscriptionPrices: z
                          .array(
                            z.object({
                              subscriptionId: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                )
                                .optional(),
                              pricePerUnit: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                    )
                                    .optional(),
                                  description: z
                                    .string()
                                    .describe(
                                      'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Price per unit info.')
                                .optional(),
                            })
                          )
                          .max(6)
                          .optional(),
                      })
                      .describe(
                        'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    inventoryStatus: z
                      .object({
                        inStock: z
                          .boolean()
                          .describe('Whether the variant is in stock.')
                          .optional(),
                        preorderEnabled: z
                          .boolean()
                          .describe(
                            'Whether preorder is enabled for this variant.'
                          )
                          .optional(),
                      })
                      .describe('Variant inventory status.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000)
              .optional(),
          })
          .describe(
            'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
          )
          .optional(),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20)
                      .optional(),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['AMOUNT', 'PERCENT'])
                            .describe('Discount type.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z.never().optional(),
                          }),
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z.never().optional(),
                    }),
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6)
              .optional(),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        variantSummary: z
          .object({
            variantCount: z
              .number()
              .int()
              .describe('The total number of variants for the product.')
              .optional(),
          })
          .describe('The total number of variants for the product.')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe('Created product.')
    .optional(),
  inventoryResults: z
    .object({
      results: z
        .array(
          z.object({
            itemMetadata: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                originalIndex: z
                  .number()
                  .int()
                  .describe(
                    'Index of the item within the request array. Allows for correlation between request and response items.'
                  )
                  .optional(),
                success: z
                  .boolean()
                  .describe(
                    'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                  )
                  .optional(),
                error: z
                  .object({
                    code: z.string().describe('Error code.').optional(),
                    description: z
                      .string()
                      .describe('Description of the error.')
                      .optional(),
                    data: z
                      .record(z.string(), z.any())
                      .describe('Data related to the error.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Details about the error in case of failure.')
                  .optional(),
              })
              .describe('Bulk action metadata for inventory item.')
              .optional(),
            item: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Inventory item ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the inventory item was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the inventory item was last updated.'
                    )
                    .optional()
                    .nullable(),
                  variantId: z
                    .string()
                    .describe('Variant ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  locationId: z
                    .string()
                    .describe(
                      "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  productId: z
                    .string()
                    .describe('Product ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  trackQuantity: z
                    .boolean()
                    .describe('Whether the quantity is being tracked.')
                    .optional(),
                  availabilityStatus: z
                    .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                    .describe('Inventory item availability status.')
                    .optional(),
                  preorderInfo: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                        )
                        .optional()
                        .nullable(),
                      message: z
                        .string()
                        .describe(
                          'Message displayed to customers when the item is out of stock and preorder is enabled.'
                        )
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      limit: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                        )
                        .min(0)
                        .max(100000)
                        .optional()
                        .nullable(),
                      counter: z
                        .number()
                        .int()
                        .describe(
                          'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                        )
                        .min(0)
                        .max(99999)
                        .optional()
                        .nullable(),
                      quantity: z
                        .number()
                        .int()
                        .describe(
                          'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                    )
                    .optional(),
                  product: z
                    .object({
                      name: z
                        .string()
                        .describe('Product name.')
                        .max(80)
                        .optional()
                        .nullable(),
                      directCategoryIds: z
                        .array(z.string())
                        .max(2000)
                        .optional(),
                      variantName: z
                        .string()
                        .describe('Variant name.')
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      variantSku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      variantVisible: z
                        .boolean()
                        .describe(
                          'Whether the variant is visible in the store.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Associated product and variant details.')
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z.never().optional(),
                  }),
                  z.object({
                    quantity: z.never().optional(),
                    inStock: z
                      .boolean()
                      .describe(
                        "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                      ),
                  }),
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                      )
                      .min(-99999)
                      .max(99999),
                  }),
                ])
              )
              .describe(
                'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(1000)
        .optional(),
      bulkActionMetadata: z
        .object({
          totalSuccesses: z
            .number()
            .int()
            .describe('Number of items that were successfully processed.')
            .optional(),
          totalFailures: z
            .number()
            .int()
            .describe("Number of items that couldn't be processed.")
            .optional(),
          undetailedFailures: z
            .number()
            .int()
            .describe(
              'Number of failures without details because detailed failure threshold was exceeded.'
            )
            .optional(),
        })
        .describe('Bulk action metadata.')
        .optional(),
      error: z
        .object({
          code: z.string().describe('Error code.').optional(),
          description: z
            .string()
            .describe('Description of the error.')
            .optional(),
          data: z
            .record(z.string(), z.any())
            .describe('Data related to the error.')
            .optional()
            .nullable(),
        })
        .describe('Error details in case of failed action.')
        .optional(),
    })
    .describe('Inventories created by bulk action.')
    .optional(),
});
export const UpdateProductRequest = z.object({
  _id: z.string().describe('Product ID.').min(1).max(36),
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
          ),
        _createdDate: z
          .date()
          .describe('Date and time the product was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the product was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Product name. Translatable.')
          .min(1)
          .max(80)
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
          )
          .optional(),
        description: z
          .any()
          .describe(
            'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe(
            'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        directCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        allCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        costRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
          )
          .optional(),
        inventory: z
          .object({
            availabilityStatus: z
              .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
              .optional(),
            preorderStatus: z
              .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
              .optional(),
            preorderAvailability: z
              .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
              .optional(),
          })
          .describe(
            'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
          )
          .optional(),
        productType: z.enum(['PHYSICAL', 'DIGITAL']).optional(),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
          )
          .optional()
          .nullable(),
        breadcrumbsInfo: z
          .object({
            breadcrumbs: z
              .array(
                z.object({
                  categoryId: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  categoryName: z
                    .string()
                    .describe('Category name.')
                    .min(1)
                    .max(80)
                    .optional(),
                  categorySlug: z
                    .string()
                    .describe('Category slug.')
                    .min(1)
                    .max(100)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe(
            'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        actualPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum current selling prices across all product variants.'
          )
          .optional(),
        compareAtPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
          )
          .optional(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                ),
                            })
                            .describe(
                              "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                            )
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .optional(),
                            })
                            .describe(
                              'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          ),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.'),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    media: z
                      .intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                      .describe('Variant media.')
                      .optional(),
                    subscriptionPricesInfo: z
                      .object({
                        subscriptionPrices: z
                          .array(
                            z.object({
                              subscriptionId: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                )
                                .optional(),
                              pricePerUnit: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                    )
                                    .optional(),
                                  description: z
                                    .string()
                                    .describe(
                                      'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Price per unit info.')
                                .optional(),
                            })
                          )
                          .max(6)
                          .optional(),
                      })
                      .describe(
                        'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    inventoryStatus: z
                      .object({
                        inStock: z
                          .boolean()
                          .describe('Whether the variant is in stock.')
                          .optional(),
                        preorderEnabled: z
                          .boolean()
                          .describe(
                            'Whether preorder is enabled for this variant.'
                          )
                          .optional(),
                      })
                      .describe('Variant inventory status.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000)
              .optional(),
          })
          .describe(
            'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
          )
          .optional(),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z.enum([
                      'UNDEFINED',
                      'DAY',
                      'WEEK',
                      'MONTH',
                      'YEAR',
                    ]),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                        z.xor([
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6)
              .optional(),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        variantSummary: z
          .object({
            variantCount: z
              .number()
              .int()
              .describe('The total number of variants for the product.')
              .optional(),
          })
          .describe('The total number of variants for the product.')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe('Product to update.'),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const UpdateProductResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Product ID.')
      .min(1)
      .max(36)
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the product was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the product was updated.')
      .optional()
      .nullable(),
    name: z
      .string()
      .describe('Product name. Translatable.')
      .min(1)
      .max(80)
      .optional()
      .nullable(),
    slug: z
      .string()
      .describe(
        'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
      )
      .optional()
      .nullable(),
    url: z
      .string()
      .describe(
        'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
      )
      .optional(),
    description: z
      .any()
      .describe(
        'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
      )
      .optional(),
    plainDescription: z
      .string()
      .describe(
        'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
      )
      .max(16000)
      .optional()
      .nullable(),
    visible: z
      .boolean()
      .describe(
        "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
      )
      .optional()
      .nullable(),
    visibleInPos: z
      .boolean()
      .describe(
        'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
      )
      .optional()
      .nullable(),
    media: z
      .object({
        main: z
          .intersection(
            z.object({
              altText: z
                .string()
                .describe('Image alt text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              displayName: z
                .string()
                .describe(
                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                )
                .max(80)
                .optional()
                .nullable(),
              mediaType: z
                .enum(['IMAGE', 'VIDEO'])
                .describe('Media type.')
                .optional(),
              thumbnail: z
                .object({
                  url: z.string().describe('Thumbnail url.').url().optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Thumbnail height.')
                    .optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Thumbnail width.')
                    .optional(),
                  altText: z
                    .string()
                    .describe('Thumbnail alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              uploadId: z
                .string()
                .describe('ID used to upload media to Wix Media Manager.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            }),
            z.intersection(
              z.xor([
                z.object({
                  _id: z.never().optional(),
                  url: z.never().optional(),
                }),
                z.object({
                  url: z.never().optional(),
                  _id: z
                    .string()
                    .describe(
                      'Set media by ID of an existing file in Wix Media Manager.'
                    )
                    .min(1)
                    .max(200),
                }),
                z.object({
                  _id: z.never().optional(),
                  url: z
                    .string()
                    .describe('Set media using an external media URL.')
                    .url(),
                }),
              ]),
              z.xor([
                z.object({
                  image: z.never().optional(),
                  video: z.never().optional(),
                }),
                z.object({
                  video: z.never().optional(),
                  image: z
                    .string()
                    .describe(
                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                    ),
                }),
                z.object({
                  image: z.never().optional(),
                  video: z
                    .string()
                    .describe(
                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                    ),
                }),
              ])
            )
          )
          .describe(
            'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
          )
          .optional(),
        itemsInfo: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z
                      .enum(['IMAGE', 'VIDEO'])
                      .describe('Media type.')
                      .optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
              )
              .max(50)
              .optional(),
          })
          .describe(
            'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
      })
      .describe(
        'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
      )
      .optional(),
    seoData: z
      .object({
        tags: z
          .array(
            z.object({
              type: z
                .string()
                .describe(
                  'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                )
                .optional(),
              props: z
                .record(z.string(), z.any())
                .describe(
                  'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                )
                .optional()
                .nullable(),
              meta: z
                .record(z.string(), z.any())
                .describe(
                  'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                )
                .optional()
                .nullable(),
              children: z
                .string()
                .describe(
                  'SEO tag inner content. For example, `<title> inner content </title>`.'
                )
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                )
                .optional(),
              disabled: z
                .boolean()
                .describe(
                  "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                )
                .optional(),
            })
          )
          .optional(),
        settings: z
          .object({
            preventAutoRedirect: z
              .boolean()
              .describe(
                'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
              )
              .optional(),
            keywords: z
              .array(
                z.object({
                  term: z.string().describe('Keyword value.').optional(),
                  isMain: z
                    .boolean()
                    .describe('Whether the keyword is the main focus keyword.')
                    .optional(),
                  origin: z
                    .string()
                    .describe(
                      'The source that added the keyword terms to the SEO settings.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe('SEO general settings.')
          .optional(),
      })
      .describe('Product SEO data.')
      .optional(),
    taxGroupId: z
      .string()
      .describe(
        '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    options: z
      .array(
        z.intersection(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of a customization with `customizationType: PRODUCT_OPTION`.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Option name.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            optionRenderType: z
              .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
              .describe('Option render type.')
              .optional(),
            key: z
              .string()
              .describe(
                'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
              )
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({ choicesSettings: z.never().optional() }),
            z.object({
              choicesSettings: z
                .object({
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          linkedMedia: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(15)
                            .optional(),
                          choiceType: z
                            .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                            .describe('Choice type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          name: z
                            .string()
                            .describe(
                              'Choice name. For text choices, this field also contains the choice value.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          inStock: z
                            .boolean()
                            .describe(
                              'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                            )
                            .optional(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({ colorCode: z.never().optional() }),
                          z.object({
                            colorCode: z
                              .string()
                              .describe(
                                'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                              )
                              .min(3)
                              .max(20),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                })
                .describe('Choices settings.'),
            }),
          ])
        )
      )
      .max(6)
      .optional(),
    modifiers: z
      .array(
        z.intersection(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of a customization with `customizationType: MODIFIER`.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Modifier title.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            modifierRenderType: z
              .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
              .describe('Modifier render type.')
              .optional(),
            mandatory: z
              .boolean()
              .describe('Whether customer input is required for this modifier.')
              .optional(),
            key: z
              .string()
              .describe(
                'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
              )
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({
              freeTextSettings: z.never().optional(),
              choicesSettings: z.never().optional(),
            }),
            z.object({
              choicesSettings: z.never().optional(),
              freeTextSettings: z
                .object({
                  minCharCount: z
                    .number()
                    .int()
                    .describe('Minimum number of characters.')
                    .optional(),
                  maxCharCount: z
                    .number()
                    .int()
                    .describe('Maximum number of characters.')
                    .max(500)
                    .optional(),
                  defaultAddedPrice: z
                    .string()
                    .describe(
                      "Default amount to be added to the product's price."
                    )
                    .optional()
                    .nullable(),
                  title: z
                    .string()
                    .describe('Title of the text to be input by the customer.')
                    .min(1)
                    .max(150)
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(150)
                    .optional(),
                })
                .describe('Free text modifier settings.'),
            }),
            z.object({
              freeTextSettings: z.never().optional(),
              choicesSettings: z
                .object({
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          linkedMedia: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(15)
                            .optional(),
                          choiceType: z
                            .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                            .describe('Choice type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          name: z
                            .string()
                            .describe('Choice name.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          addedPrice: z
                            .string()
                            .describe('Added price.')
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({ colorCode: z.never().optional() }),
                          z.object({
                            colorCode: z
                              .string()
                              .describe(
                                'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                              )
                              .min(3)
                              .max(20),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                })
                .describe('Choice settings.'),
            }),
          ])
        )
      )
      .max(10)
      .optional(),
    brand: z
      .object({
        _id: z
          .string()
          .describe('Brand ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Brand name.').max(50).optional().nullable(),
      })
      .describe(
        "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
      )
      .optional(),
    infoSections: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Info section ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          uniqueName: z
            .string()
            .describe(
              'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
            )
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          description: z
            .any()
            .describe(
              'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
      )
      .max(10)
      .optional(),
    ribbon: z
      .object({
        _id: z
          .string()
          .describe('Ribbon ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        name: z.string().describe('Ribbon name.').max(30).optional().nullable(),
      })
      .describe(
        "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
      )
      .optional(),
    directCategoriesInfo: z
      .object({
        categories: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                )
                .min(0)
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(2000)
          .optional(),
      })
      .describe(
        'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    allCategoriesInfo: z
      .object({
        categories: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                )
                .min(0)
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(2000)
          .optional(),
      })
      .describe(
        'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    mainCategoryId: z
      .string()
      .describe(
        'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    costRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
      )
      .optional(),
    inventory: z
      .object({
        availabilityStatus: z
          .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
          .describe('Current availability status.')
          .optional(),
        preorderStatus: z
          .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
          .describe('Current preorder status.')
          .optional(),
        preorderAvailability: z
          .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
          .describe('Preorder availability status.')
          .optional(),
      })
      .describe(
        'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
      )
      .optional(),
    productType: z
      .enum(['PHYSICAL', 'DIGITAL'])
      .describe(
        'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
      )
      .optional(),
    handle: z
      .string()
      .describe(
        "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
      )
      .min(1)
      .max(100)
      .optional()
      .nullable(),
    currency: z
      .string()
      .describe(
        'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
      )
      .optional()
      .nullable(),
    breadcrumbsInfo: z
      .object({
        breadcrumbs: z
          .array(
            z.object({
              categoryId: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              categoryName: z
                .string()
                .describe('Category name.')
                .min(1)
                .max(80)
                .optional(),
              categorySlug: z
                .string()
                .describe('Category slug.')
                .min(1)
                .max(100)
                .optional(),
            })
          )
          .max(5)
          .optional(),
      })
      .describe(
        'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    actualPriceRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Minimum and maximum current selling prices across all product variants.'
      )
      .optional(),
    compareAtPriceRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
      )
      .optional(),
    variantsInfo: z
      .object({
        variants: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                visible: z
                  .boolean()
                  .describe(
                    "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe('Variant SKU (stock keeping unit).')
                  .min(1)
                  .max(40)
                  .optional()
                  .nullable(),
                barcode: z
                  .string()
                  .describe('Variant barcode.')
                  .min(1)
                  .max(40)
                  .optional()
                  .nullable(),
                choices: z
                  .array(
                    z.object({
                      optionChoiceIds: z
                        .object({
                          optionId: z
                            .string()
                            .describe('Option ID.')
                            .min(1)
                            .max(36)
                            .optional(),
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                        )
                        .optional(),
                      optionChoiceNames: z
                        .object({
                          optionName: z
                            .string()
                            .describe('Option name.')
                            .min(1)
                            .optional(),
                          choiceName: z
                            .string()
                            .describe('Choice name.')
                            .min(1)
                            .optional(),
                          renderType: z
                            .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                            .describe(
                              'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(6)
                  .optional(),
                price: z
                  .object({
                    actualPrice: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        "Variant's current selling price. Must be greater than or equal to 0."
                      )
                      .optional(),
                    compareAtPrice: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                      )
                      .optional(),
                  })
                  .describe('Variant price.')
                  .optional(),
                revenueDetails: z
                  .object({
                    cost: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe('Item cost.')
                      .optional(),
                    profit: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                      )
                      .optional(),
                    profitMargin: z
                      .number()
                      .describe(
                        'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                      )
                      .min(0)
                      .max(1)
                      .optional(),
                  })
                  .describe(
                    'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                  )
                  .optional(),
                media: z
                  .intersection(
                    z.object({
                      altText: z
                        .string()
                        .describe('Image alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                      displayName: z
                        .string()
                        .describe(
                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                        )
                        .max(80)
                        .optional()
                        .nullable(),
                      mediaType: z
                        .enum(['IMAGE', 'VIDEO'])
                        .describe('Media type.')
                        .optional(),
                      thumbnail: z
                        .object({
                          url: z
                            .string()
                            .describe('Thumbnail url.')
                            .url()
                            .optional(),
                          height: z
                            .number()
                            .int()
                            .describe('Thumbnail height.')
                            .optional(),
                          width: z
                            .number()
                            .int()
                            .describe('Thumbnail width.')
                            .optional(),
                          altText: z
                            .string()
                            .describe('Thumbnail alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      uploadId: z
                        .string()
                        .describe(
                          'ID used to upload media to Wix Media Manager.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.intersection(
                      z.xor([
                        z.object({
                          _id: z.never().optional(),
                          url: z.never().optional(),
                        }),
                        z.object({
                          url: z.never().optional(),
                          _id: z
                            .string()
                            .describe(
                              'Set media by ID of an existing file in Wix Media Manager.'
                            )
                            .min(1)
                            .max(200),
                        }),
                        z.object({
                          _id: z.never().optional(),
                          url: z
                            .string()
                            .describe('Set media using an external media URL.')
                            .url(),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          image: z.never().optional(),
                          video: z.never().optional(),
                        }),
                        z.object({
                          video: z.never().optional(),
                          image: z
                            .string()
                            .describe(
                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                            ),
                        }),
                        z.object({
                          image: z.never().optional(),
                          video: z
                            .string()
                            .describe(
                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                            ),
                        }),
                      ])
                    )
                  )
                  .describe('Variant media.')
                  .optional(),
                subscriptionPricesInfo: z
                  .object({
                    subscriptionPrices: z
                      .array(
                        z.object({
                          subscriptionId: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                            )
                            .optional(),
                          pricePerUnit: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Price per unit info.')
                            .optional(),
                        })
                      )
                      .max(6)
                      .optional(),
                  })
                  .describe(
                    'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                inventoryStatus: z
                  .object({
                    inStock: z
                      .boolean()
                      .describe('Whether the variant is in stock.')
                      .optional(),
                    preorderEnabled: z
                      .boolean()
                      .describe('Whether preorder is enabled for this variant.')
                      .optional(),
                  })
                  .describe('Variant inventory status.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  physicalProperties: z.never().optional(),
                  digitalProperties: z.never().optional(),
                }),
                z.object({
                  digitalProperties: z.never().optional(),
                  physicalProperties: z
                    .object({
                      weight: z
                        .number()
                        .describe(
                          'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                        )
                        .min(0)
                        .max(999999999.99)
                        .optional()
                        .nullable(),
                      pricePerUnit: z
                        .object({
                          settings: z
                            .object({
                              quantity: z
                                .number()
                                .describe(
                                  'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                )
                                .min(0.01)
                                .max(999999999.99)
                                .optional(),
                              measurementUnit: z
                                .enum([
                                  'UNSPECIFIED',
                                  'ML',
                                  'CL',
                                  'L',
                                  'CBM',
                                  'MG',
                                  'G',
                                  'KG',
                                  'MM',
                                  'CM',
                                  'M',
                                  'SQM',
                                  'OZ',
                                  'LB',
                                  'FLOZ',
                                  'PT',
                                  'QT',
                                  'GAL',
                                  'IN',
                                  'FT',
                                  'YD',
                                  'SQFT',
                                ])
                                .describe(
                                  'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                )
                                .optional(),
                            })
                            .describe(
                              'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                            )
                            .optional(),
                          value: z
                            .string()
                            .describe(
                              "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                            )
                            .optional(),
                          description: z
                            .string()
                            .describe(
                              'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                        )
                        .optional(),
                      productDimensions: z
                        .object({
                          length: z
                            .string()
                            .describe(
                              'Length. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          width: z
                            .string()
                            .describe(
                              'Width. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          height: z
                            .string()
                            .describe(
                              'Height. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          unit: z
                            .enum([
                              'UNKNOWN_DIMENSIONS_UNIT',
                              'MM',
                              'CM',
                              'M',
                              'IN',
                              'FT',
                              'YD',
                            ])
                            .describe(
                              'Unit of measurement for dimensions (length, width, height).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Product dimensions (length, width, height of the physical product).'
                        )
                        .optional(),
                      packageDimensions: z
                        .object({
                          length: z
                            .string()
                            .describe(
                              'Length. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          width: z
                            .string()
                            .describe(
                              'Width. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          height: z
                            .string()
                            .describe(
                              'Height. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          unit: z
                            .enum([
                              'UNKNOWN_DIMENSIONS_UNIT',
                              'MM',
                              'CM',
                              'M',
                              'IN',
                              'FT',
                              'YD',
                            ])
                            .describe(
                              'Unit of measurement for dimensions (length, width, height).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Package dimensions (length, width, height of the shipping package).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Physical properties. Must be passed when `productType: PHYSICAL`'
                    ),
                }),
                z.object({
                  physicalProperties: z.never().optional(),
                  digitalProperties: z
                    .object({
                      digitalFile: z
                        .object({
                          _id: z
                            .string()
                            .describe('Media ID in media manager.')
                            .min(1)
                            .max(100)
                            .optional(),
                          fileName: z
                            .string()
                            .describe('Original file name.')
                            .min(1)
                            .max(1000)
                            .optional(),
                          fileSize: z
                            .string()
                            .describe('Original file size.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          fileType: z
                            .enum([
                              'UNSPECIFIED',
                              'SECURE_PICTURE',
                              'SECURE_VIDEO',
                              'SECURE_DOCUMENT',
                              'SECURE_MUSIC',
                              'SECURE_ARCHIVE',
                              'SECURE_RAW',
                            ])
                            .describe('File type.')
                            .optional(),
                        })
                        .describe(
                          'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Digital properties. Must be passed when `productType: DIGITAL`'
                    ),
                }),
              ])
            )
          )
          .min(1)
          .max(1000)
          .optional(),
      })
      .describe(
        'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
      )
      .optional(),
    purchaseEligibility: z
      .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
      .describe('Purchase eligibility settings for the product.')
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags')
      .optional(),
    subscriptionDetails: z
      .object({
        subscriptions: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe('Subscription title.')
                  .min(1)
                  .max(20)
                  .optional(),
                description: z
                  .string()
                  .describe('Subscription description.')
                  .max(60)
                  .optional()
                  .nullable(),
                visible: z
                  .boolean()
                  .describe(
                    'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe(
                    'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                discount: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['AMOUNT', 'PERCENT'])
                        .describe('Discount type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        amountOff: z.never().optional(),
                        percentOff: z.never().optional(),
                      }),
                      z.object({
                        percentOff: z.never().optional(),
                        amountOff: z
                          .string()
                          .describe(
                            'Amount to discount from the variant discounted_price.'
                          ),
                      }),
                      z.object({
                        amountOff: z.never().optional(),
                        percentOff: z
                          .number()
                          .describe(
                            'Percentage to discount from variant discounted_price.'
                          )
                          .min(0.1)
                          .max(99.9),
                      }),
                    ])
                  )
                  .describe(
                    'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  autoRenewal: z.never().optional(),
                  billingCycles: z.never().optional(),
                }),
                z.object({
                  billingCycles: z.never().optional(),
                  autoRenewal: z
                    .boolean()
                    .describe(
                      'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                    ),
                }),
                z.object({
                  autoRenewal: z.never().optional(),
                  billingCycles: z
                    .number()
                    .int()
                    .describe(
                      'Number of billing cycles before subscription ends.'
                    )
                    .min(2)
                    .max(999),
                }),
              ])
            )
          )
          .min(1)
          .max(6)
          .optional(),
        allowOneTimePurchases: z
          .boolean()
          .describe(
            'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
          )
          .optional()
          .nullable(),
      })
      .describe(
        "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
      )
      .optional(),
    variantSummary: z
      .object({
        variantCount: z
          .number()
          .int()
          .describe('The total number of variants for the product.')
          .optional(),
      })
      .describe('The total number of variants for the product.')
      .optional(),
  }),
  z.xor([
    z.object({ physicalProperties: z.never().optional() }),
    z.object({
      physicalProperties: z
        .object({
          pricePerUnit: z
            .object({
              quantity: z
                .number()
                .describe(
                  'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                )
                .min(0.01)
                .max(999999999.99)
                .optional(),
              measurementUnit: z
                .enum([
                  'UNSPECIFIED',
                  'ML',
                  'CL',
                  'L',
                  'CBM',
                  'MG',
                  'G',
                  'KG',
                  'MM',
                  'CM',
                  'M',
                  'SQM',
                  'OZ',
                  'LB',
                  'FLOZ',
                  'PT',
                  'QT',
                  'GAL',
                  'IN',
                  'FT',
                  'YD',
                  'SQFT',
                ])
                .describe(
                  'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                )
                .optional(),
            })
            .describe(
              'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
            )
            .optional(),
          fulfillerId: z
            .string()
            .describe('Fulfiller ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          shippingWeightRange: z
            .object({
              minValue: z
                .number()
                .describe(
                  'Minimum weight across all variants associated with this product.'
                )
                .min(0)
                .max(999999999.99)
                .optional(),
              maxValue: z
                .number()
                .describe(
                  'Maximum weight across all variants associated with this product.'
                )
                .min(0)
                .max(999999999.99)
                .optional(),
            })
            .describe(
              'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
            )
            .optional(),
          pricePerUnitRange: z
            .object({
              minValue: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                    )
                    .optional(),
                  description: z
                    .string()
                    .describe(
                      'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum price per unit across all variants.')
                .optional(),
              maxValue: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                    )
                    .optional(),
                  description: z
                    .string()
                    .describe(
                      'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum price per unit across all variants.')
                .optional(),
            })
            .describe(
              'Product price per unit range. The minimum and maximum price per unit of all the variants.'
            )
            .optional(),
          weightMeasurementUnitInfo: z
            .object({
              weightMeasurementUnit: z
                .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                .describe('Weight measurement unit.')
                .optional(),
            })
            .describe(
              'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          deliveryProfileId: z
            .string()
            .describe('Delivery profile ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
        ),
    }),
  ])
);
export const UpdateProductWithInventoryRequest = z.object({
  _id: z.string().describe('Product ID.').min(1).max(36),
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product with inventory.'
          ),
        name: z
          .string()
          .describe('Product name. Translatable.')
          .min(1)
          .max(80)
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            'Whether the product is visible to site visitors.\n\nDefault: `true`'
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe('Product media items.')
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product brand.\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product ribbon.\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct category, which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productType: z.enum(['PHYSICAL', 'DIGITAL']).optional(),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the variant is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          ),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.'),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    inventoryItem: z
                      .intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe(
                              'Inventory item ID.\nIf you want to update existing inventory item you must provide id,\notherwise new inventory item with different id will be created.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          revision: z
                            .string()
                            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                            .describe('Inventory item revision.')
                            .optional()
                            .nullable(),
                          preorderInfo: z
                            .object({
                              enabled: z
                                .boolean()
                                .describe(
                                  "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                                )
                                .optional()
                                .nullable(),
                              message: z
                                .string()
                                .describe(
                                  'Message displayed to customers when the item is out of stock and preorder is enabled.'
                                )
                                .min(1)
                                .max(250)
                                .optional()
                                .nullable(),
                              limit: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                                )
                                .min(0)
                                .max(100000)
                                .optional()
                                .nullable(),
                              counter: z
                                .number()
                                .int()
                                .describe(
                                  'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                                )
                                .min(0)
                                .max(99999)
                                .optional()
                                .nullable(),
                              quantity: z
                                .number()
                                .int()
                                .describe(
                                  'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                                )
                                .optional()
                                .nullable(),
                            })
                            .describe('Item preorder info.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            inStock: z.never().optional(),
                            quantity: z.never().optional(),
                          }),
                          z.object({
                            quantity: z.never().optional(),
                            inStock: z
                              .boolean()
                              .describe(
                                'Tracking method - in stock.\n\nWhen set to `true`, the item is available for sale without a quantity limit.'
                              ),
                          }),
                          z.object({
                            inStock: z.never().optional(),
                            quantity: z
                              .number()
                              .int()
                              .describe(
                                'Tracking method - quantity left in inventory.\n\nQuantity can be negative when decrementing inventory for an order that has already been paid.'
                              )
                              .min(-99999)
                              .max(99999),
                          }),
                        ])
                      )
                      .describe(
                        'Inventory item of the variant on the default location.'
                      )
                      .optional(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                ),
                            })
                            .describe('Option and choice IDs.')
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .optional(),
                            })
                            .describe('Option and choice names.')
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000)
              .optional(),
          })
          .describe(
            "Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nVariants aren't auto-generated. You must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants)."
          )
          .optional(),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z.enum([
                      'UNDEFINED',
                      'DAY',
                      'WEEK',
                      'MONTH',
                      'YEAR',
                    ]),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                        z.xor([
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6)
              .optional(),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe('Product to update.'),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const UpdateProductWithInventoryResponse = z.object({
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the product was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the product was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Product name. Translatable.')
          .min(1)
          .max(80)
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
          )
          .optional(),
        description: z
          .any()
          .describe(
            'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z
                    .enum(['IMAGE', 'VIDEO'])
                    .describe('Media type.')
                    .optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe(
            'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .describe('Option render type.')
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({ choicesSettings: z.never().optional() }),
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .describe('Media type.')
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .describe('Choice type.')
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .describe('Modifier render type.')
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z.never().optional(),
                }),
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .describe('Media type.')
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .describe('Choice type.')
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        directCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        allCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        costRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
          )
          .optional(),
        inventory: z
          .object({
            availabilityStatus: z
              .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
              .describe('Current availability status.')
              .optional(),
            preorderStatus: z
              .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
              .describe('Current preorder status.')
              .optional(),
            preorderAvailability: z
              .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
              .describe('Preorder availability status.')
              .optional(),
          })
          .describe(
            'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
          )
          .optional(),
        productType: z
          .enum(['PHYSICAL', 'DIGITAL'])
          .describe(
            'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
          )
          .optional(),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
          )
          .optional()
          .nullable(),
        breadcrumbsInfo: z
          .object({
            breadcrumbs: z
              .array(
                z.object({
                  categoryId: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  categoryName: z
                    .string()
                    .describe('Category name.')
                    .min(1)
                    .max(80)
                    .optional(),
                  categorySlug: z
                    .string()
                    .describe('Category slug.')
                    .min(1)
                    .max(100)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe(
            'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        actualPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum current selling prices across all product variants.'
          )
          .optional(),
        compareAtPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
          )
          .optional(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36)
                                .optional(),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            })
                            .describe(
                              "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                            )
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .describe(
                                  'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          )
                          .optional(),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.')
                      .optional(),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    media: z
                      .intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z
                            .enum(['IMAGE', 'VIDEO'])
                            .describe('Media type.')
                            .optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                      .describe('Variant media.')
                      .optional(),
                    subscriptionPricesInfo: z
                      .object({
                        subscriptionPrices: z
                          .array(
                            z.object({
                              subscriptionId: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                )
                                .optional(),
                              pricePerUnit: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                    )
                                    .optional(),
                                  description: z
                                    .string()
                                    .describe(
                                      'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Price per unit info.')
                                .optional(),
                            })
                          )
                          .max(6)
                          .optional(),
                      })
                      .describe(
                        'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    inventoryStatus: z
                      .object({
                        inStock: z
                          .boolean()
                          .describe('Whether the variant is in stock.')
                          .optional(),
                        preorderEnabled: z
                          .boolean()
                          .describe(
                            'Whether preorder is enabled for this variant.'
                          )
                          .optional(),
                      })
                      .describe('Variant inventory status.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000)
              .optional(),
          })
          .describe(
            'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
          )
          .optional(),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20)
                      .optional(),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['AMOUNT', 'PERCENT'])
                            .describe('Discount type.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z.never().optional(),
                          }),
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z.never().optional(),
                    }),
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6)
              .optional(),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        variantSummary: z
          .object({
            variantCount: z
              .number()
              .int()
              .describe('The total number of variants for the product.')
              .optional(),
          })
          .describe('The total number of variants for the product.')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe('Updated product.')
    .optional(),
  inventoryResults: z
    .object({
      results: z
        .array(
          z.object({
            itemMetadata: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                originalIndex: z
                  .number()
                  .int()
                  .describe(
                    'Index of the item within the request array. Allows for correlation between request and response items.'
                  )
                  .optional(),
                success: z
                  .boolean()
                  .describe(
                    'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                  )
                  .optional(),
                error: z
                  .object({
                    code: z.string().describe('Error code.').optional(),
                    description: z
                      .string()
                      .describe('Description of the error.')
                      .optional(),
                    data: z
                      .record(z.string(), z.any())
                      .describe('Data related to the error.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Details about the error in case of failure.')
                  .optional(),
              })
              .describe('Bulk action metadata for inventory item.')
              .optional(),
            item: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Inventory item ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the inventory item was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the inventory item was last updated.'
                    )
                    .optional()
                    .nullable(),
                  variantId: z
                    .string()
                    .describe('Variant ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  locationId: z
                    .string()
                    .describe(
                      "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  productId: z
                    .string()
                    .describe('Product ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  trackQuantity: z
                    .boolean()
                    .describe('Whether the quantity is being tracked.')
                    .optional(),
                  availabilityStatus: z
                    .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                    .describe('Inventory item availability status.')
                    .optional(),
                  preorderInfo: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                        )
                        .optional()
                        .nullable(),
                      message: z
                        .string()
                        .describe(
                          'Message displayed to customers when the item is out of stock and preorder is enabled.'
                        )
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      limit: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                        )
                        .min(0)
                        .max(100000)
                        .optional()
                        .nullable(),
                      counter: z
                        .number()
                        .int()
                        .describe(
                          'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                        )
                        .min(0)
                        .max(99999)
                        .optional()
                        .nullable(),
                      quantity: z
                        .number()
                        .int()
                        .describe(
                          'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                    )
                    .optional(),
                  product: z
                    .object({
                      name: z
                        .string()
                        .describe('Product name.')
                        .max(80)
                        .optional()
                        .nullable(),
                      directCategoryIds: z
                        .array(z.string())
                        .max(2000)
                        .optional(),
                      variantName: z
                        .string()
                        .describe('Variant name.')
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      variantSku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      variantVisible: z
                        .boolean()
                        .describe(
                          'Whether the variant is visible in the store.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Associated product and variant details.')
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z.never().optional(),
                  }),
                  z.object({
                    quantity: z.never().optional(),
                    inStock: z
                      .boolean()
                      .describe(
                        "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                      ),
                  }),
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                      )
                      .min(-99999)
                      .max(99999),
                  }),
                ])
              )
              .describe(
                'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(1000)
        .optional(),
      bulkActionMetadata: z
        .object({
          totalSuccesses: z
            .number()
            .int()
            .describe('Number of items that were successfully processed.')
            .optional(),
          totalFailures: z
            .number()
            .int()
            .describe("Number of items that couldn't be processed.")
            .optional(),
          undetailedFailures: z
            .number()
            .int()
            .describe(
              'Number of failures without details because detailed failure threshold was exceeded.'
            )
            .optional(),
        })
        .describe('Bulk action metadata.')
        .optional(),
      error: z
        .object({
          code: z.string().describe('Error code.').optional(),
          description: z
            .string()
            .describe('Description of the error.')
            .optional(),
          data: z
            .record(z.string(), z.any())
            .describe('Data related to the error.')
            .optional()
            .nullable(),
        })
        .describe('Error details in case of failed action.')
        .optional(),
    })
    .describe('Inventories updated by bulk action.')
    .optional(),
});
export const BulkCreateProductsRequest = z.object({
  products: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the product was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the product was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Product name. Translatable.')
            .min(1)
            .max(80),
          slug: z
            .string()
            .describe(
              'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
            )
            .optional()
            .nullable(),
          url: z
            .string()
            .describe(
              'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
            )
            .optional(),
          description: z
            .any()
            .describe(
              'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
          visible: z
            .boolean()
            .describe(
              "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
            )
            .optional()
            .nullable(),
          visibleInPos: z
            .boolean()
            .describe(
              'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
            )
            .optional()
            .nullable(),
          media: z
            .object({
              main: z
                .intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
                .describe(
                  'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                )
                .optional(),
              itemsInfo: z
                .object({
                  items: z
                    .array(
                      z.intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                    )
                    .max(50)
                    .optional(),
                })
                .describe(
                  'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
            })
            .describe(
              'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
            )
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Product SEO data.')
            .optional(),
          taxGroupId: z
            .string()
            .describe(
              '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          options: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Option name.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  optionRenderType: z
                    .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Choice name. For text choices, this field also contains the choice value.'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                inStock: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                  )
                                  .optional(),
                                visible: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choices settings.'),
                  }),
                ])
              )
            )
            .max(6)
            .optional(),
          modifiers: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: MODIFIER`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Modifier title.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  modifierRenderType: z
                    .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .optional(),
                  mandatory: z
                    .boolean()
                    .describe(
                      'Whether customer input is required for this modifier.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    choicesSettings: z.never().optional(),
                    freeTextSettings: z
                      .object({
                        minCharCount: z
                          .number()
                          .int()
                          .describe('Minimum number of characters.')
                          .optional(),
                        maxCharCount: z
                          .number()
                          .int()
                          .describe('Maximum number of characters.')
                          .max(500)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount to be added to the product's price."
                          )
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Title of the text to be input by the customer.'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                      })
                      .describe('Free text modifier settings.'),
                  }),
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                addedPrice: z
                                  .string()
                                  .describe('Added price.')
                                  .optional()
                                  .nullable(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choice settings.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          brand: z
            .object({
              _id: z
                .string()
                .describe('Brand ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Brand name.')
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
            )
            .optional(),
          infoSections: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Info section ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                uniqueName: z
                  .string()
                  .describe(
                    'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                description: z
                  .any()
                  .describe(
                    'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                  )
                  .optional(),
                plainDescription: z
                  .string()
                  .describe(
                    'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                  )
                  .max(16000)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          ribbon: z
            .object({
              _id: z
                .string()
                .describe('Ribbon ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Ribbon name.')
                .max(30)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
            )
            .optional(),
          directCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          allCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          mainCategoryId: z
            .string()
            .describe(
              'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          costRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
            )
            .optional(),
          inventory: z
            .object({
              availabilityStatus: z
                .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
                .optional(),
              preorderStatus: z
                .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                .optional(),
              preorderAvailability: z
                .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                .optional(),
            })
            .describe(
              'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
            )
            .optional(),
          productType: z.enum(['PHYSICAL', 'DIGITAL']),
          handle: z
            .string()
            .describe(
              "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          currency: z
            .string()
            .describe(
              'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
            )
            .optional()
            .nullable(),
          breadcrumbsInfo: z
            .object({
              breadcrumbs: z
                .array(
                  z.object({
                    categoryId: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    categoryName: z
                      .string()
                      .describe('Category name.')
                      .min(1)
                      .max(80)
                      .optional(),
                    categorySlug: z
                      .string()
                      .describe('Category slug.')
                      .min(1)
                      .max(100)
                      .optional(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe(
              'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          actualPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum current selling prices across all product variants.'
            )
            .optional(),
          compareAtPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
            )
            .optional(),
          variantsInfo: z
            .object({
              variants: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      barcode: z
                        .string()
                        .describe('Variant barcode.')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      choices: z
                        .array(
                          z.object({
                            optionChoiceIds: z
                              .object({
                                optionId: z
                                  .string()
                                  .describe('Option ID.')
                                  .min(1)
                                  .max(36),
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  ),
                              })
                              .describe(
                                "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                              )
                              .optional(),
                            optionChoiceNames: z
                              .object({
                                optionName: z
                                  .string()
                                  .describe('Option name.')
                                  .min(1)
                                  .optional(),
                                choiceName: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .optional(),
                                renderType: z
                                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                  .optional(),
                              })
                              .describe(
                                'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                          })
                        )
                        .min(0)
                        .max(6)
                        .optional(),
                      price: z
                        .object({
                          actualPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Variant's current selling price. Must be greater than or equal to 0."
                            ),
                          compareAtPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                            )
                            .optional(),
                        })
                        .describe('Variant price.'),
                      revenueDetails: z
                        .object({
                          cost: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Item cost.')
                            .optional(),
                          profit: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                            )
                            .optional(),
                          profitMargin: z
                            .number()
                            .describe(
                              'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                            )
                            .min(0)
                            .max(1)
                            .optional(),
                        })
                        .describe(
                          'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                        )
                        .optional(),
                      media: z
                        .intersection(
                          z.object({
                            altText: z
                              .string()
                              .describe('Image alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                            displayName: z
                              .string()
                              .describe(
                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                              )
                              .max(80)
                              .optional()
                              .nullable(),
                            mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .url()
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .optional(),
                                altText: z
                                  .string()
                                  .describe('Thumbnail alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                            uploadId: z
                              .string()
                              .describe(
                                'ID used to upload media to Wix Media Manager.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.intersection(
                            z.xor([
                              z.object({
                                _id: z.never().optional(),
                                url: z.never().optional(),
                              }),
                              z.object({
                                url: z.never().optional(),
                                _id: z
                                  .string()
                                  .describe(
                                    'Set media by ID of an existing file in Wix Media Manager.'
                                  )
                                  .min(1)
                                  .max(200),
                              }),
                              z.object({
                                _id: z.never().optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'Set media using an external media URL.'
                                  )
                                  .url(),
                              }),
                            ]),
                            z.xor([
                              z.object({
                                image: z.never().optional(),
                                video: z.never().optional(),
                              }),
                              z.object({
                                video: z.never().optional(),
                                image: z
                                  .string()
                                  .describe(
                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                  ),
                              }),
                              z.object({
                                image: z.never().optional(),
                                video: z
                                  .string()
                                  .describe(
                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                  ),
                              }),
                            ])
                          )
                        )
                        .describe('Variant media.')
                        .optional(),
                      subscriptionPricesInfo: z
                        .object({
                          subscriptionPrices: z
                            .array(
                              z.object({
                                subscriptionId: z
                                  .string()
                                  .describe('Subscription ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                price: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe(
                                        'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                      )
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                  )
                                  .optional(),
                                pricePerUnit: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Price per unit info.')
                                  .optional(),
                              })
                            )
                            .max(6)
                            .optional(),
                        })
                        .describe(
                          'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      inventoryStatus: z
                        .object({
                          inStock: z
                            .boolean()
                            .describe('Whether the variant is in stock.')
                            .optional(),
                          preorderEnabled: z
                            .boolean()
                            .describe(
                              'Whether preorder is enabled for this variant.'
                            )
                            .optional(),
                        })
                        .describe('Variant inventory status.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z.never().optional(),
                      }),
                      z.object({
                        digitalProperties: z.never().optional(),
                        physicalProperties: z
                          .object({
                            weight: z
                              .number()
                              .describe(
                                'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional()
                              .nullable(),
                            pricePerUnit: z
                              .object({
                                settings: z
                                  .object({
                                    quantity: z
                                      .number()
                                      .describe(
                                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                      )
                                      .min(0.01)
                                      .max(999999999.99)
                                      .optional(),
                                    measurementUnit: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'ML',
                                        'CL',
                                        'L',
                                        'CBM',
                                        'MG',
                                        'G',
                                        'KG',
                                        'MM',
                                        'CM',
                                        'M',
                                        'SQM',
                                        'OZ',
                                        'LB',
                                        'FLOZ',
                                        'PT',
                                        'QT',
                                        'GAL',
                                        'IN',
                                        'FT',
                                        'YD',
                                        'SQFT',
                                      ])
                                      .describe(
                                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                  )
                                  .optional(),
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                              )
                              .optional(),
                            productDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Product dimensions (length, width, height of the physical product).'
                              )
                              .optional(),
                            packageDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Package dimensions (length, width, height of the shipping package).'
                              )
                              .optional(),
                          })
                          .describe(
                            'Physical properties. Must be passed when `productType: PHYSICAL`'
                          ),
                      }),
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z
                          .object({
                            digitalFile: z
                              .object({
                                _id: z
                                  .string()
                                  .describe('Media ID in media manager.')
                                  .min(1)
                                  .max(100)
                                  .optional(),
                                fileName: z
                                  .string()
                                  .describe('Original file name.')
                                  .min(1)
                                  .max(1000)
                                  .optional(),
                                fileSize: z
                                  .string()
                                  .describe('Original file size.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                fileType: z
                                  .enum([
                                    'UNSPECIFIED',
                                    'SECURE_PICTURE',
                                    'SECURE_VIDEO',
                                    'SECURE_DOCUMENT',
                                    'SECURE_MUSIC',
                                    'SECURE_ARCHIVE',
                                    'SECURE_RAW',
                                  ])
                                  .describe('File type.')
                                  .optional(),
                              })
                              .describe(
                                'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Digital properties. Must be passed when `productType: DIGITAL`'
                          ),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(1000),
            })
            .describe(
              'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
            ),
          purchaseEligibility: z
            .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
            .describe('Purchase eligibility settings for the product.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              publicTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe('Tags')
            .optional(),
          subscriptionDetails: z
            .object({
              subscriptions: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe('Subscription title.')
                        .min(1)
                        .max(20),
                      description: z
                        .string()
                        .describe('Subscription description.')
                        .max(60)
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                      frequency: z.enum([
                        'UNDEFINED',
                        'DAY',
                        'WEEK',
                        'MONTH',
                        'YEAR',
                      ]),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      discount: z
                        .intersection(
                          z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                          z.xor([
                            z.object({
                              percentOff: z.never().optional(),
                              amountOff: z
                                .string()
                                .describe(
                                  'Amount to discount from the variant discounted_price.'
                                ),
                            }),
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z
                                .number()
                                .describe(
                                  'Percentage to discount from variant discounted_price.'
                                )
                                .min(0.1)
                                .max(99.9),
                            }),
                          ])
                        )
                        .describe(
                          'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        billingCycles: z.never().optional(),
                        autoRenewal: z
                          .boolean()
                          .describe(
                            'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                          ),
                      }),
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z
                          .number()
                          .int()
                          .describe(
                            'Number of billing cycles before subscription ends.'
                          )
                          .min(2)
                          .max(999),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(6),
              allowOneTimePurchases: z
                .boolean()
                .describe(
                  'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
            )
            .optional(),
          variantSummary: z
            .object({
              variantCount: z
                .number()
                .int()
                .describe('The total number of variants for the product.')
                .optional(),
            })
            .describe('The total number of variants for the product.')
            .optional(),
        }),
        z.xor([
          z.object({ physicalProperties: z.never().optional() }),
          z.object({
            physicalProperties: z
              .object({
                pricePerUnit: z
                  .object({
                    quantity: z
                      .number()
                      .describe(
                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                      )
                      .min(0.01)
                      .max(999999999.99)
                      .optional(),
                    measurementUnit: z
                      .enum([
                        'UNSPECIFIED',
                        'ML',
                        'CL',
                        'L',
                        'CBM',
                        'MG',
                        'G',
                        'KG',
                        'MM',
                        'CM',
                        'M',
                        'SQM',
                        'OZ',
                        'LB',
                        'FLOZ',
                        'PT',
                        'QT',
                        'GAL',
                        'IN',
                        'FT',
                        'YD',
                        'SQFT',
                      ])
                      .describe(
                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                      )
                      .optional(),
                  })
                  .describe(
                    'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                  )
                  .optional(),
                fulfillerId: z
                  .string()
                  .describe('Fulfiller ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shippingWeightRange: z
                  .object({
                    minValue: z
                      .number()
                      .describe(
                        'Minimum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                    maxValue: z
                      .number()
                      .describe(
                        'Maximum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                  })
                  .describe(
                    'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                  )
                  .optional(),
                pricePerUnitRange: z
                  .object({
                    minValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Minimum price per unit across all variants.')
                      .optional(),
                    maxValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum price per unit across all variants.')
                      .optional(),
                  })
                  .describe(
                    'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                  )
                  .optional(),
                weightMeasurementUnitInfo: z
                  .object({
                    weightMeasurementUnit: z
                      .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                      .describe('Weight measurement unit.')
                      .optional(),
                  })
                  .describe(
                    'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                deliveryProfileId: z
                  .string()
                  .describe('Delivery profile ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
              ),
          }),
        ])
      )
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full product entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const BulkCreateProductsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Information about successful action or error for failure.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the product was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the product was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              description: z
                .any()
                .describe(
                  'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z
                                .enum(['IMAGE', 'VIDEO'])
                                .describe('Media type.')
                                .optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe(
                  'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Option render type.')
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ choicesSettings: z.never().optional() }),
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Modifier render type.')
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z.never().optional(),
                      }),
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              directCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              allCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              costRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                )
                .optional(),
              inventory: z
                .object({
                  availabilityStatus: z
                    .enum([
                      'IN_STOCK',
                      'OUT_OF_STOCK',
                      'PARTIALLY_OUT_OF_STOCK',
                    ])
                    .describe('Current availability status.')
                    .optional(),
                  preorderStatus: z
                    .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                    .describe('Current preorder status.')
                    .optional(),
                  preorderAvailability: z
                    .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                    .describe('Preorder availability status.')
                    .optional(),
                })
                .describe(
                  'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                )
                .optional(),
              productType: z
                .enum(['PHYSICAL', 'DIGITAL'])
                .describe(
                  'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
                )
                .optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              currency: z
                .string()
                .describe(
                  'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                )
                .optional()
                .nullable(),
              breadcrumbsInfo: z
                .object({
                  breadcrumbs: z
                    .array(
                      z.object({
                        categoryId: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        categoryName: z
                          .string()
                          .describe('Category name.')
                          .min(1)
                          .max(80)
                          .optional(),
                        categorySlug: z
                          .string()
                          .describe('Category slug.')
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe(
                  'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              actualPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum current selling prices across all product variants.'
                )
                .optional(),
              compareAtPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                )
                .optional(),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36)
                                      .optional(),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                  )
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .describe(
                                        'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                )
                                .optional(),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.')
                            .optional(),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          media: z
                            .intersection(
                              z.object({
                                altText: z
                                  .string()
                                  .describe('Image alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                displayName: z
                                  .string()
                                  .describe(
                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                  )
                                  .max(80)
                                  .optional()
                                  .nullable(),
                                mediaType: z
                                  .enum(['IMAGE', 'VIDEO'])
                                  .describe('Media type.')
                                  .optional(),
                                thumbnail: z
                                  .object({
                                    url: z
                                      .string()
                                      .describe('Thumbnail url.')
                                      .url()
                                      .optional(),
                                    height: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail height.')
                                      .optional(),
                                    width: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail width.')
                                      .optional(),
                                    altText: z
                                      .string()
                                      .describe('Thumbnail alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                                uploadId: z
                                  .string()
                                  .describe(
                                    'ID used to upload media to Wix Media Manager.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              }),
                              z.intersection(
                                z.xor([
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z.never().optional(),
                                  }),
                                  z.object({
                                    url: z.never().optional(),
                                    _id: z
                                      .string()
                                      .describe(
                                        'Set media by ID of an existing file in Wix Media Manager.'
                                      )
                                      .min(1)
                                      .max(200),
                                  }),
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z
                                      .string()
                                      .describe(
                                        'Set media using an external media URL.'
                                      )
                                      .url(),
                                  }),
                                ]),
                                z.xor([
                                  z.object({
                                    image: z.never().optional(),
                                    video: z.never().optional(),
                                  }),
                                  z.object({
                                    video: z.never().optional(),
                                    image: z
                                      .string()
                                      .describe(
                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                      ),
                                  }),
                                  z.object({
                                    image: z.never().optional(),
                                    video: z
                                      .string()
                                      .describe(
                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('Variant media.')
                            .optional(),
                          subscriptionPricesInfo: z
                            .object({
                              subscriptionPrices: z
                                .array(
                                  z.object({
                                    subscriptionId: z
                                      .string()
                                      .describe('Subscription ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                    price: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                      )
                                      .optional(),
                                    pricePerUnit: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Price per unit info.')
                                      .optional(),
                                  })
                                )
                                .max(6)
                                .optional(),
                            })
                            .describe(
                              'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          inventoryStatus: z
                            .object({
                              inStock: z
                                .boolean()
                                .describe('Whether the variant is in stock.')
                                .optional(),
                              preorderEnabled: z
                                .boolean()
                                .describe(
                                  'Whether preorder is enabled for this variant.'
                                )
                                .optional(),
                            })
                            .describe('Variant inventory status.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20)
                            .optional(),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe(
                              'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                            )
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({
                                type: z
                                  .enum(['AMOUNT', 'PERCENT'])
                                  .describe('Discount type.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z.never().optional(),
                                }),
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Amount to discount from the variant discounted_price.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage to discount from variant discounted_price.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z.never().optional(),
                          }),
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              variantSummary: z
                .object({
                  variantCount: z
                    .number()
                    .int()
                    .describe('The total number of variants for the product.')
                    .optional(),
                })
                .describe('The total number of variants for the product.')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full product entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkCreateProductsWithInventoryRequest = z.object({
  products: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product with inventory.'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Product name. Translatable.')
            .min(1)
            .max(80),
          slug: z
            .string()
            .describe(
              'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
            )
            .optional()
            .nullable(),
          plainDescription: z
            .string()
            .describe(
              'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
          visible: z
            .boolean()
            .describe(
              'Whether the product is visible to site visitors.\n\nDefault: `true`'
            )
            .optional()
            .nullable(),
          visibleInPos: z
            .boolean()
            .describe(
              'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
            )
            .optional()
            .nullable(),
          media: z
            .object({
              main: z
                .intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
                .describe(
                  'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                )
                .optional(),
              itemsInfo: z
                .object({
                  items: z
                    .array(
                      z.intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                    )
                    .max(50)
                    .optional(),
                })
                .describe(
                  'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
            })
            .describe('Product media items.')
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Product SEO data.')
            .optional(),
          taxGroupId: z
            .string()
            .describe(
              '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          options: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Option name.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  optionRenderType: z
                    .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Choice name. For text choices, this field also contains the choice value.'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                inStock: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                  )
                                  .optional(),
                                visible: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choices settings.'),
                  }),
                ])
              )
            )
            .max(6)
            .optional(),
          modifiers: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: MODIFIER`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Modifier title.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  modifierRenderType: z
                    .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .optional(),
                  mandatory: z
                    .boolean()
                    .describe(
                      'Whether customer input is required for this modifier.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    choicesSettings: z.never().optional(),
                    freeTextSettings: z
                      .object({
                        minCharCount: z
                          .number()
                          .int()
                          .describe('Minimum number of characters.')
                          .optional(),
                        maxCharCount: z
                          .number()
                          .int()
                          .describe('Maximum number of characters.')
                          .max(500)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount to be added to the product's price."
                          )
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Title of the text to be input by the customer.'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                      })
                      .describe('Free text modifier settings.'),
                  }),
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                addedPrice: z
                                  .string()
                                  .describe('Added price.')
                                  .optional()
                                  .nullable(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choice settings.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          brand: z
            .object({
              _id: z
                .string()
                .describe('Brand ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Brand name.')
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Product brand.\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
            )
            .optional(),
          infoSections: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Info section ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                uniqueName: z
                  .string()
                  .describe(
                    'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                description: z
                  .any()
                  .describe(
                    'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                  )
                  .optional(),
                plainDescription: z
                  .string()
                  .describe(
                    'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                  )
                  .max(16000)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          ribbon: z
            .object({
              _id: z
                .string()
                .describe('Ribbon ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Ribbon name.')
                .max(30)
                .optional()
                .nullable(),
            })
            .describe(
              "Product ribbon.\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
            )
            .optional(),
          mainCategoryId: z
            .string()
            .describe(
              'The ID of the product\'s primary direct category, which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          productType: z.enum(['PHYSICAL', 'DIGITAL']),
          handle: z
            .string()
            .describe(
              "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          variantsInfo: z
            .object({
              variants: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          'Whether the variant is visible to site visitors.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      barcode: z
                        .string()
                        .describe('Variant barcode.')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      price: z
                        .object({
                          actualPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Variant's current selling price. Must be greater than or equal to 0."
                            ),
                          compareAtPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                            )
                            .optional(),
                        })
                        .describe('Variant price.'),
                      revenueDetails: z
                        .object({
                          cost: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Item cost.')
                            .optional(),
                          profit: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                            )
                            .optional(),
                          profitMargin: z
                            .number()
                            .describe(
                              'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                            )
                            .min(0)
                            .max(1)
                            .optional(),
                        })
                        .describe(
                          'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                        )
                        .optional(),
                      inventoryItem: z
                        .intersection(
                          z.object({
                            _id: z
                              .string()
                              .describe(
                                'Inventory item ID.\nIf you want to update existing inventory item you must provide id,\notherwise new inventory item with different id will be created.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional()
                              .nullable(),
                            revision: z
                              .string()
                              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                              .describe('Inventory item revision.')
                              .optional()
                              .nullable(),
                            preorderInfo: z
                              .object({
                                enabled: z
                                  .boolean()
                                  .describe(
                                    "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                                  )
                                  .optional()
                                  .nullable(),
                                message: z
                                  .string()
                                  .describe(
                                    'Message displayed to customers when the item is out of stock and preorder is enabled.'
                                  )
                                  .min(1)
                                  .max(250)
                                  .optional()
                                  .nullable(),
                                limit: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                                  )
                                  .min(0)
                                  .max(100000)
                                  .optional()
                                  .nullable(),
                                counter: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                                  )
                                  .min(0)
                                  .max(99999)
                                  .optional()
                                  .nullable(),
                                quantity: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe('Item preorder info.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              inStock: z.never().optional(),
                              quantity: z.never().optional(),
                            }),
                            z.object({
                              quantity: z.never().optional(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Tracking method - in stock.\n\nWhen set to `true`, the item is available for sale without a quantity limit.'
                                ),
                            }),
                            z.object({
                              inStock: z.never().optional(),
                              quantity: z
                                .number()
                                .int()
                                .describe(
                                  'Tracking method - quantity left in inventory.\n\nQuantity can be negative when decrementing inventory for an order that has already been paid.'
                                )
                                .min(-99999)
                                .max(99999),
                            }),
                          ])
                        )
                        .describe(
                          'Inventory item of the variant on the default location.'
                        )
                        .optional(),
                      choices: z
                        .array(
                          z.object({
                            optionChoiceIds: z
                              .object({
                                optionId: z
                                  .string()
                                  .describe('Option ID.')
                                  .min(1)
                                  .max(36),
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  ),
                              })
                              .describe('Option and choice IDs.')
                              .optional(),
                            optionChoiceNames: z
                              .object({
                                optionName: z
                                  .string()
                                  .describe('Option name.')
                                  .min(1)
                                  .optional(),
                                choiceName: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .optional(),
                                renderType: z
                                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                  .optional(),
                              })
                              .describe('Option and choice names.')
                              .optional(),
                          })
                        )
                        .min(0)
                        .max(6)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z.never().optional(),
                      }),
                      z.object({
                        digitalProperties: z.never().optional(),
                        physicalProperties: z
                          .object({
                            weight: z
                              .number()
                              .describe(
                                'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional()
                              .nullable(),
                            pricePerUnit: z
                              .object({
                                settings: z
                                  .object({
                                    quantity: z
                                      .number()
                                      .describe(
                                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                      )
                                      .min(0.01)
                                      .max(999999999.99)
                                      .optional(),
                                    measurementUnit: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'ML',
                                        'CL',
                                        'L',
                                        'CBM',
                                        'MG',
                                        'G',
                                        'KG',
                                        'MM',
                                        'CM',
                                        'M',
                                        'SQM',
                                        'OZ',
                                        'LB',
                                        'FLOZ',
                                        'PT',
                                        'QT',
                                        'GAL',
                                        'IN',
                                        'FT',
                                        'YD',
                                        'SQFT',
                                      ])
                                      .describe(
                                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                  )
                                  .optional(),
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                              )
                              .optional(),
                            productDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Product dimensions (length, width, height of the physical product).'
                              )
                              .optional(),
                            packageDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Package dimensions (length, width, height of the shipping package).'
                              )
                              .optional(),
                          })
                          .describe(
                            'Physical properties. Must be passed when `productType: PHYSICAL`'
                          ),
                      }),
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z
                          .object({
                            digitalFile: z
                              .object({
                                _id: z
                                  .string()
                                  .describe('Media ID in media manager.')
                                  .min(1)
                                  .max(100)
                                  .optional(),
                                fileName: z
                                  .string()
                                  .describe('Original file name.')
                                  .min(1)
                                  .max(1000)
                                  .optional(),
                                fileSize: z
                                  .string()
                                  .describe('Original file size.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                fileType: z
                                  .enum([
                                    'UNSPECIFIED',
                                    'SECURE_PICTURE',
                                    'SECURE_VIDEO',
                                    'SECURE_DOCUMENT',
                                    'SECURE_MUSIC',
                                    'SECURE_ARCHIVE',
                                    'SECURE_RAW',
                                  ])
                                  .describe('File type.')
                                  .optional(),
                              })
                              .describe(
                                'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Digital properties. Must be passed when `productType: DIGITAL`'
                          ),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(1000)
                .optional(),
            })
            .describe(
              "Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nVariants aren't auto-generated. You must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants)."
            ),
          subscriptionDetails: z
            .object({
              subscriptions: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe('Subscription title.')
                        .min(1)
                        .max(20),
                      description: z
                        .string()
                        .describe('Subscription description.')
                        .max(60)
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                      frequency: z.enum([
                        'UNDEFINED',
                        'DAY',
                        'WEEK',
                        'MONTH',
                        'YEAR',
                      ]),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      discount: z
                        .intersection(
                          z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                          z.xor([
                            z.object({
                              percentOff: z.never().optional(),
                              amountOff: z
                                .string()
                                .describe(
                                  'Amount to discount from the variant discounted_price.'
                                ),
                            }),
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z
                                .number()
                                .describe(
                                  'Percentage to discount from variant discounted_price.'
                                )
                                .min(0.1)
                                .max(99.9),
                            }),
                          ])
                        )
                        .describe(
                          'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        billingCycles: z.never().optional(),
                        autoRenewal: z
                          .boolean()
                          .describe(
                            'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                          ),
                      }),
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z
                          .number()
                          .int()
                          .describe(
                            'Number of billing cycles before subscription ends.'
                          )
                          .min(2)
                          .max(999),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(6),
              allowOneTimePurchases: z
                .boolean()
                .describe(
                  'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
            )
            .optional(),
          purchaseEligibility: z
            .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
            .describe('Purchase eligibility settings for the product.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              publicTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe('Tags')
            .optional(),
        }),
        z.xor([
          z.object({ physicalProperties: z.never().optional() }),
          z.object({
            physicalProperties: z
              .object({
                pricePerUnit: z
                  .object({
                    quantity: z
                      .number()
                      .describe(
                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                      )
                      .min(0.01)
                      .max(999999999.99)
                      .optional(),
                    measurementUnit: z
                      .enum([
                        'UNSPECIFIED',
                        'ML',
                        'CL',
                        'L',
                        'CBM',
                        'MG',
                        'G',
                        'KG',
                        'MM',
                        'CM',
                        'M',
                        'SQM',
                        'OZ',
                        'LB',
                        'FLOZ',
                        'PT',
                        'QT',
                        'GAL',
                        'IN',
                        'FT',
                        'YD',
                        'SQFT',
                      ])
                      .describe(
                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                      )
                      .optional(),
                  })
                  .describe(
                    'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                  )
                  .optional(),
                fulfillerId: z
                  .string()
                  .describe('Fulfiller ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shippingWeightRange: z
                  .object({
                    minValue: z
                      .number()
                      .describe(
                        'Minimum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                    maxValue: z
                      .number()
                      .describe(
                        'Maximum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                  })
                  .describe(
                    'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                  )
                  .optional(),
                pricePerUnitRange: z
                  .object({
                    minValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Minimum price per unit across all variants.')
                      .optional(),
                    maxValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum price per unit across all variants.')
                      .optional(),
                  })
                  .describe(
                    'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                  )
                  .optional(),
                weightMeasurementUnitInfo: z
                  .object({
                    weightMeasurementUnit: z
                      .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                      .describe('Weight measurement unit.')
                      .optional(),
                  })
                  .describe(
                    'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                deliveryProfileId: z
                  .string()
                  .describe('Delivery profile ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
              ),
          }),
        ])
      )
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full product entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const BulkCreateProductsWithInventoryResponse = z.object({
  productResults: z
    .object({
      results: z
        .array(
          z.object({
            itemMetadata: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                originalIndex: z
                  .number()
                  .int()
                  .describe(
                    'Index of the item within the request array. Allows for correlation between request and response items.'
                  )
                  .optional(),
                success: z
                  .boolean()
                  .describe(
                    'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                  )
                  .optional(),
                error: z
                  .object({
                    code: z.string().describe('Error code.').optional(),
                    description: z
                      .string()
                      .describe('Description of the error.')
                      .optional(),
                    data: z
                      .record(z.string(), z.any())
                      .describe('Data related to the error.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Details about the error in case of failure.')
                  .optional(),
              })
              .describe(
                'Information about successful action or error for failure.'
              )
              .optional(),
            item: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Product ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the product was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe('Date and time the product was updated.')
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Product name. Translatable.')
                    .min(1)
                    .max(80)
                    .optional()
                    .nullable(),
                  slug: z
                    .string()
                    .describe(
                      'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                    )
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe(
                      'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  description: z
                    .any()
                    .describe(
                      'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                    )
                    .optional(),
                  plainDescription: z
                    .string()
                    .describe(
                      'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                  visible: z
                    .boolean()
                    .describe(
                      "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                    )
                    .optional()
                    .nullable(),
                  visibleInPos: z
                    .boolean()
                    .describe(
                      'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                    )
                    .optional()
                    .nullable(),
                  media: z
                    .object({
                      main: z
                        .intersection(
                          z.object({
                            altText: z
                              .string()
                              .describe('Image alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                            displayName: z
                              .string()
                              .describe(
                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                              )
                              .max(80)
                              .optional()
                              .nullable(),
                            mediaType: z
                              .enum(['IMAGE', 'VIDEO'])
                              .describe('Media type.')
                              .optional(),
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .url()
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .optional(),
                                altText: z
                                  .string()
                                  .describe('Thumbnail alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                            uploadId: z
                              .string()
                              .describe(
                                'ID used to upload media to Wix Media Manager.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.intersection(
                            z.xor([
                              z.object({
                                _id: z.never().optional(),
                                url: z.never().optional(),
                              }),
                              z.object({
                                url: z.never().optional(),
                                _id: z
                                  .string()
                                  .describe(
                                    'Set media by ID of an existing file in Wix Media Manager.'
                                  )
                                  .min(1)
                                  .max(200),
                              }),
                              z.object({
                                _id: z.never().optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'Set media using an external media URL.'
                                  )
                                  .url(),
                              }),
                            ]),
                            z.xor([
                              z.object({
                                image: z.never().optional(),
                                video: z.never().optional(),
                              }),
                              z.object({
                                video: z.never().optional(),
                                image: z
                                  .string()
                                  .describe(
                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                  ),
                              }),
                              z.object({
                                image: z.never().optional(),
                                video: z
                                  .string()
                                  .describe(
                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                  ),
                              }),
                            ])
                          )
                        )
                        .describe(
                          'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                        )
                        .optional(),
                      itemsInfo: z
                        .object({
                          items: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(50)
                            .optional(),
                        })
                        .describe(
                          'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                    )
                    .optional(),
                  seoData: z
                    .object({
                      tags: z
                        .array(
                          z.object({
                            type: z
                              .string()
                              .describe(
                                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                              )
                              .optional(),
                            props: z
                              .record(z.string(), z.any())
                              .describe(
                                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                              )
                              .optional()
                              .nullable(),
                            meta: z
                              .record(z.string(), z.any())
                              .describe(
                                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                              )
                              .optional()
                              .nullable(),
                            children: z
                              .string()
                              .describe(
                                'SEO tag inner content. For example, `<title> inner content </title>`.'
                              )
                              .optional(),
                            custom: z
                              .boolean()
                              .describe(
                                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                              )
                              .optional(),
                            disabled: z
                              .boolean()
                              .describe(
                                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                              )
                              .optional(),
                          })
                        )
                        .optional(),
                      settings: z
                        .object({
                          preventAutoRedirect: z
                            .boolean()
                            .describe(
                              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                            )
                            .optional(),
                          keywords: z
                            .array(
                              z.object({
                                term: z
                                  .string()
                                  .describe('Keyword value.')
                                  .optional(),
                                isMain: z
                                  .boolean()
                                  .describe(
                                    'Whether the keyword is the main focus keyword.'
                                  )
                                  .optional(),
                                origin: z
                                  .string()
                                  .describe(
                                    'The source that added the keyword terms to the SEO settings.'
                                  )
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                            )
                            .max(5)
                            .optional(),
                        })
                        .describe('SEO general settings.')
                        .optional(),
                    })
                    .describe('Product SEO data.')
                    .optional(),
                  taxGroupId: z
                    .string()
                    .describe(
                      '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  options: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe(
                              'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                            )
                            .min(1)
                            .max(36)
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Option name.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          optionRenderType: z
                            .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                            .describe('Option render type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                        }),
                        z.xor([
                          z.object({ choicesSettings: z.never().optional() }),
                          z.object({
                            choicesSettings: z
                              .object({
                                choices: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        choiceId: z
                                          .string()
                                          .describe('Choice ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional()
                                          .nullable(),
                                        linkedMedia: z
                                          .array(
                                            z.intersection(
                                              z.object({
                                                altText: z
                                                  .string()
                                                  .describe('Image alt text.')
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                                displayName: z
                                                  .string()
                                                  .describe(
                                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                                  )
                                                  .max(80)
                                                  .optional()
                                                  .nullable(),
                                                mediaType: z
                                                  .enum(['IMAGE', 'VIDEO'])
                                                  .describe('Media type.')
                                                  .optional(),
                                                thumbnail: z
                                                  .object({
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail url.'
                                                      )
                                                      .url()
                                                      .optional(),
                                                    height: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail height.'
                                                      )
                                                      .optional(),
                                                    width: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail width.'
                                                      )
                                                      .optional(),
                                                    altText: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail alt text.'
                                                      )
                                                      .min(1)
                                                      .max(1000)
                                                      .optional()
                                                      .nullable(),
                                                  })
                                                  .describe(
                                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                                  )
                                                  .optional(),
                                                uploadId: z
                                                  .string()
                                                  .describe(
                                                    'ID used to upload media to Wix Media Manager.'
                                                  )
                                                  .regex(
                                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                    'Must be a valid GUID'
                                                  )
                                                  .optional(),
                                              }),
                                              z.intersection(
                                                z.xor([
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    url: z.never().optional(),
                                                    _id: z
                                                      .string()
                                                      .describe(
                                                        'Set media by ID of an existing file in Wix Media Manager.'
                                                      )
                                                      .min(1)
                                                      .max(200),
                                                  }),
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Set media using an external media URL.'
                                                      )
                                                      .url(),
                                                  }),
                                                ]),
                                                z.xor([
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    video: z.never().optional(),
                                                    image: z
                                                      .string()
                                                      .describe(
                                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z
                                                      .string()
                                                      .describe(
                                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                ])
                                              )
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                        choiceType: z
                                          .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                          .describe('Choice type.')
                                          .optional(),
                                        key: z
                                          .string()
                                          .describe(
                                            'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                          )
                                          .min(1)
                                          .max(50)
                                          .optional(),
                                        name: z
                                          .string()
                                          .describe(
                                            'Choice name. For text choices, this field also contains the choice value.'
                                          )
                                          .min(1)
                                          .max(50)
                                          .optional()
                                          .nullable(),
                                        inStock: z
                                          .boolean()
                                          .describe(
                                            'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                          )
                                          .optional(),
                                        visible: z
                                          .boolean()
                                          .describe(
                                            'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                          )
                                          .optional(),
                                      }),
                                      z.xor([
                                        z.object({
                                          colorCode: z.never().optional(),
                                        }),
                                        z.object({
                                          colorCode: z
                                            .string()
                                            .describe(
                                              'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                            )
                                            .min(3)
                                            .max(20),
                                        }),
                                      ])
                                    )
                                  )
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Choices settings.'),
                          }),
                        ])
                      )
                    )
                    .max(6)
                    .optional(),
                  modifiers: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe(
                              'ID of a customization with `customizationType: MODIFIER`.'
                            )
                            .min(1)
                            .max(36)
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Modifier title.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          modifierRenderType: z
                            .enum([
                              'FREE_TEXT',
                              'TEXT_CHOICES',
                              'SWATCH_CHOICES',
                            ])
                            .describe('Modifier render type.')
                            .optional(),
                          mandatory: z
                            .boolean()
                            .describe(
                              'Whether customer input is required for this modifier.'
                            )
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            freeTextSettings: z.never().optional(),
                            choicesSettings: z.never().optional(),
                          }),
                          z.object({
                            choicesSettings: z.never().optional(),
                            freeTextSettings: z
                              .object({
                                minCharCount: z
                                  .number()
                                  .int()
                                  .describe('Minimum number of characters.')
                                  .optional(),
                                maxCharCount: z
                                  .number()
                                  .int()
                                  .describe('Maximum number of characters.')
                                  .max(500)
                                  .optional(),
                                defaultAddedPrice: z
                                  .string()
                                  .describe(
                                    "Default amount to be added to the product's price."
                                  )
                                  .optional()
                                  .nullable(),
                                title: z
                                  .string()
                                  .describe(
                                    'Title of the text to be input by the customer.'
                                  )
                                  .min(1)
                                  .max(150)
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(150)
                                  .optional(),
                              })
                              .describe('Free text modifier settings.'),
                          }),
                          z.object({
                            freeTextSettings: z.never().optional(),
                            choicesSettings: z
                              .object({
                                choices: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        choiceId: z
                                          .string()
                                          .describe('Choice ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional()
                                          .nullable(),
                                        linkedMedia: z
                                          .array(
                                            z.intersection(
                                              z.object({
                                                altText: z
                                                  .string()
                                                  .describe('Image alt text.')
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                                displayName: z
                                                  .string()
                                                  .describe(
                                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                                  )
                                                  .max(80)
                                                  .optional()
                                                  .nullable(),
                                                mediaType: z
                                                  .enum(['IMAGE', 'VIDEO'])
                                                  .describe('Media type.')
                                                  .optional(),
                                                thumbnail: z
                                                  .object({
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail url.'
                                                      )
                                                      .url()
                                                      .optional(),
                                                    height: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail height.'
                                                      )
                                                      .optional(),
                                                    width: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail width.'
                                                      )
                                                      .optional(),
                                                    altText: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail alt text.'
                                                      )
                                                      .min(1)
                                                      .max(1000)
                                                      .optional()
                                                      .nullable(),
                                                  })
                                                  .describe(
                                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                                  )
                                                  .optional(),
                                                uploadId: z
                                                  .string()
                                                  .describe(
                                                    'ID used to upload media to Wix Media Manager.'
                                                  )
                                                  .regex(
                                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                    'Must be a valid GUID'
                                                  )
                                                  .optional(),
                                              }),
                                              z.intersection(
                                                z.xor([
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    url: z.never().optional(),
                                                    _id: z
                                                      .string()
                                                      .describe(
                                                        'Set media by ID of an existing file in Wix Media Manager.'
                                                      )
                                                      .min(1)
                                                      .max(200),
                                                  }),
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Set media using an external media URL.'
                                                      )
                                                      .url(),
                                                  }),
                                                ]),
                                                z.xor([
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    video: z.never().optional(),
                                                    image: z
                                                      .string()
                                                      .describe(
                                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z
                                                      .string()
                                                      .describe(
                                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                ])
                                              )
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                        choiceType: z
                                          .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                          .describe('Choice type.')
                                          .optional(),
                                        key: z
                                          .string()
                                          .describe(
                                            'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                          )
                                          .min(1)
                                          .max(50)
                                          .optional(),
                                        name: z
                                          .string()
                                          .describe('Choice name.')
                                          .min(1)
                                          .max(50)
                                          .optional()
                                          .nullable(),
                                        addedPrice: z
                                          .string()
                                          .describe('Added price.')
                                          .optional()
                                          .nullable(),
                                      }),
                                      z.xor([
                                        z.object({
                                          colorCode: z.never().optional(),
                                        }),
                                        z.object({
                                          colorCode: z
                                            .string()
                                            .describe(
                                              'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                            )
                                            .min(3)
                                            .max(20),
                                        }),
                                      ])
                                    )
                                  )
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Choice settings.'),
                          }),
                        ])
                      )
                    )
                    .max(10)
                    .optional(),
                  brand: z
                    .object({
                      _id: z
                        .string()
                        .describe('Brand ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Brand name.')
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                    )
                    .optional(),
                  infoSections: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Info section ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        uniqueName: z
                          .string()
                          .describe(
                            'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                          )
                          .min(1)
                          .max(50)
                          .optional()
                          .nullable(),
                        description: z
                          .any()
                          .describe(
                            'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                          )
                          .optional(),
                        plainDescription: z
                          .string()
                          .describe(
                            'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                          )
                          .max(16000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(10)
                    .optional(),
                  ribbon: z
                    .object({
                      _id: z
                        .string()
                        .describe('Ribbon ID.')
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Ribbon name.')
                        .max(30)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                    )
                    .optional(),
                  directCategoriesInfo: z
                    .object({
                      categories: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Category ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            index: z
                              .number()
                              .int()
                              .describe(
                                'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                              )
                              .min(0)
                              .max(200)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(2000)
                        .optional(),
                    })
                    .describe(
                      'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  allCategoriesInfo: z
                    .object({
                      categories: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Category ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            index: z
                              .number()
                              .int()
                              .describe(
                                'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                              )
                              .min(0)
                              .max(200)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(2000)
                        .optional(),
                    })
                    .describe(
                      'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  mainCategoryId: z
                    .string()
                    .describe(
                      'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  costRange: z
                    .object({
                      minValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Minimum value.')
                        .optional(),
                      maxValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Maximum value.')
                        .optional(),
                    })
                    .describe(
                      'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                    )
                    .optional(),
                  inventory: z
                    .object({
                      availabilityStatus: z
                        .enum([
                          'IN_STOCK',
                          'OUT_OF_STOCK',
                          'PARTIALLY_OUT_OF_STOCK',
                        ])
                        .describe('Current availability status.')
                        .optional(),
                      preorderStatus: z
                        .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                        .describe('Current preorder status.')
                        .optional(),
                      preorderAvailability: z
                        .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                        .describe('Preorder availability status.')
                        .optional(),
                    })
                    .describe(
                      'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                    )
                    .optional(),
                  productType: z
                    .enum(['PHYSICAL', 'DIGITAL'])
                    .describe(
                      'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
                    )
                    .optional(),
                  handle: z
                    .string()
                    .describe(
                      "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  currency: z
                    .string()
                    .describe(
                      'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .optional()
                    .nullable(),
                  breadcrumbsInfo: z
                    .object({
                      breadcrumbs: z
                        .array(
                          z.object({
                            categoryId: z
                              .string()
                              .describe('Category ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            categoryName: z
                              .string()
                              .describe('Category name.')
                              .min(1)
                              .max(80)
                              .optional(),
                            categorySlug: z
                              .string()
                              .describe('Category slug.')
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe(
                      'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  actualPriceRange: z
                    .object({
                      minValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Minimum value.')
                        .optional(),
                      maxValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Maximum value.')
                        .optional(),
                    })
                    .describe(
                      'Minimum and maximum current selling prices across all product variants.'
                    )
                    .optional(),
                  compareAtPriceRange: z
                    .object({
                      minValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Minimum value.')
                        .optional(),
                      maxValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Maximum value.')
                        .optional(),
                    })
                    .describe(
                      'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                    )
                    .optional(),
                  variantsInfo: z
                    .object({
                      variants: z
                        .array(
                          z.intersection(
                            z.object({
                              _id: z
                                .string()
                                .describe('Variant ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              visible: z
                                .boolean()
                                .describe(
                                  "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                                )
                                .optional()
                                .nullable(),
                              sku: z
                                .string()
                                .describe('Variant SKU (stock keeping unit).')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                              barcode: z
                                .string()
                                .describe('Variant barcode.')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.object({
                                    optionChoiceIds: z
                                      .object({
                                        optionId: z
                                          .string()
                                          .describe('Option ID.')
                                          .min(1)
                                          .max(36)
                                          .optional(),
                                        choiceId: z
                                          .string()
                                          .describe('Choice ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                      )
                                      .optional(),
                                    optionChoiceNames: z
                                      .object({
                                        optionName: z
                                          .string()
                                          .describe('Option name.')
                                          .min(1)
                                          .optional(),
                                        choiceName: z
                                          .string()
                                          .describe('Choice name.')
                                          .min(1)
                                          .optional(),
                                        renderType: z
                                          .enum([
                                            'TEXT_CHOICES',
                                            'SWATCH_CHOICES',
                                          ])
                                          .describe(
                                            'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                      )
                                      .optional(),
                                  })
                                )
                                .min(0)
                                .max(6)
                                .optional(),
                              price: z
                                .object({
                                  actualPrice: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      "Variant's current selling price. Must be greater than or equal to 0."
                                    )
                                    .optional(),
                                  compareAtPrice: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                    )
                                    .optional(),
                                })
                                .describe('Variant price.')
                                .optional(),
                              revenueDetails: z
                                .object({
                                  cost: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item cost.')
                                    .optional(),
                                  profit: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                    )
                                    .optional(),
                                  profitMargin: z
                                    .number()
                                    .describe(
                                      'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                    )
                                    .min(0)
                                    .max(1)
                                    .optional(),
                                })
                                .describe(
                                  'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                                )
                                .optional(),
                              media: z
                                .intersection(
                                  z.object({
                                    altText: z
                                      .string()
                                      .describe('Image alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    displayName: z
                                      .string()
                                      .describe(
                                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                      )
                                      .max(80)
                                      .optional()
                                      .nullable(),
                                    mediaType: z
                                      .enum(['IMAGE', 'VIDEO'])
                                      .describe('Media type.')
                                      .optional(),
                                    thumbnail: z
                                      .object({
                                        url: z
                                          .string()
                                          .describe('Thumbnail url.')
                                          .url()
                                          .optional(),
                                        height: z
                                          .number()
                                          .int()
                                          .describe('Thumbnail height.')
                                          .optional(),
                                        width: z
                                          .number()
                                          .int()
                                          .describe('Thumbnail width.')
                                          .optional(),
                                        altText: z
                                          .string()
                                          .describe('Thumbnail alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                      )
                                      .optional(),
                                    uploadId: z
                                      .string()
                                      .describe(
                                        'ID used to upload media to Wix Media Manager.'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  }),
                                  z.intersection(
                                    z.xor([
                                      z.object({
                                        _id: z.never().optional(),
                                        url: z.never().optional(),
                                      }),
                                      z.object({
                                        url: z.never().optional(),
                                        _id: z
                                          .string()
                                          .describe(
                                            'Set media by ID of an existing file in Wix Media Manager.'
                                          )
                                          .min(1)
                                          .max(200),
                                      }),
                                      z.object({
                                        _id: z.never().optional(),
                                        url: z
                                          .string()
                                          .describe(
                                            'Set media using an external media URL.'
                                          )
                                          .url(),
                                      }),
                                    ]),
                                    z.xor([
                                      z.object({
                                        image: z.never().optional(),
                                        video: z.never().optional(),
                                      }),
                                      z.object({
                                        video: z.never().optional(),
                                        image: z
                                          .string()
                                          .describe(
                                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                          ),
                                      }),
                                      z.object({
                                        image: z.never().optional(),
                                        video: z
                                          .string()
                                          .describe(
                                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                          ),
                                      }),
                                    ])
                                  )
                                )
                                .describe('Variant media.')
                                .optional(),
                              subscriptionPricesInfo: z
                                .object({
                                  subscriptionPrices: z
                                    .array(
                                      z.object({
                                        subscriptionId: z
                                          .string()
                                          .describe('Subscription ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        price: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe(
                                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                              )
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                              )
                                              .max(20)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                          )
                                          .optional(),
                                        pricePerUnit: z
                                          .object({
                                            value: z
                                              .string()
                                              .describe(
                                                'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                              )
                                              .optional(),
                                            description: z
                                              .string()
                                              .describe(
                                                'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                              )
                                              .max(20)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Price per unit info.')
                                          .optional(),
                                      })
                                    )
                                    .max(6)
                                    .optional(),
                                })
                                .describe(
                                  'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              inventoryStatus: z
                                .object({
                                  inStock: z
                                    .boolean()
                                    .describe(
                                      'Whether the variant is in stock.'
                                    )
                                    .optional(),
                                  preorderEnabled: z
                                    .boolean()
                                    .describe(
                                      'Whether preorder is enabled for this variant.'
                                    )
                                    .optional(),
                                })
                                .describe('Variant inventory status.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                physicalProperties: z.never().optional(),
                                digitalProperties: z.never().optional(),
                              }),
                              z.object({
                                digitalProperties: z.never().optional(),
                                physicalProperties: z
                                  .object({
                                    weight: z
                                      .number()
                                      .describe(
                                        'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                      )
                                      .min(0)
                                      .max(999999999.99)
                                      .optional()
                                      .nullable(),
                                    pricePerUnit: z
                                      .object({
                                        settings: z
                                          .object({
                                            quantity: z
                                              .number()
                                              .describe(
                                                'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                              )
                                              .min(0.01)
                                              .max(999999999.99)
                                              .optional(),
                                            measurementUnit: z
                                              .enum([
                                                'UNSPECIFIED',
                                                'ML',
                                                'CL',
                                                'L',
                                                'CBM',
                                                'MG',
                                                'G',
                                                'KG',
                                                'MM',
                                                'CM',
                                                'M',
                                                'SQM',
                                                'OZ',
                                                'LB',
                                                'FLOZ',
                                                'PT',
                                                'QT',
                                                'GAL',
                                                'IN',
                                                'FT',
                                                'YD',
                                                'SQFT',
                                              ])
                                              .describe(
                                                'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                          )
                                          .optional(),
                                        value: z
                                          .string()
                                          .describe(
                                            "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                      )
                                      .optional(),
                                    productDimensions: z
                                      .object({
                                        length: z
                                          .string()
                                          .describe(
                                            'Length. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        width: z
                                          .string()
                                          .describe(
                                            'Width. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        height: z
                                          .string()
                                          .describe(
                                            'Height. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        unit: z
                                          .enum([
                                            'UNKNOWN_DIMENSIONS_UNIT',
                                            'MM',
                                            'CM',
                                            'M',
                                            'IN',
                                            'FT',
                                            'YD',
                                          ])
                                          .describe(
                                            'Unit of measurement for dimensions (length, width, height).'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Product dimensions (length, width, height of the physical product).'
                                      )
                                      .optional(),
                                    packageDimensions: z
                                      .object({
                                        length: z
                                          .string()
                                          .describe(
                                            'Length. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        width: z
                                          .string()
                                          .describe(
                                            'Width. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        height: z
                                          .string()
                                          .describe(
                                            'Height. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        unit: z
                                          .enum([
                                            'UNKNOWN_DIMENSIONS_UNIT',
                                            'MM',
                                            'CM',
                                            'M',
                                            'IN',
                                            'FT',
                                            'YD',
                                          ])
                                          .describe(
                                            'Unit of measurement for dimensions (length, width, height).'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Package dimensions (length, width, height of the shipping package).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Physical properties. Must be passed when `productType: PHYSICAL`'
                                  ),
                              }),
                              z.object({
                                physicalProperties: z.never().optional(),
                                digitalProperties: z
                                  .object({
                                    digitalFile: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe(
                                            'Media ID in media manager.'
                                          )
                                          .min(1)
                                          .max(100)
                                          .optional(),
                                        fileName: z
                                          .string()
                                          .describe('Original file name.')
                                          .min(1)
                                          .max(1000)
                                          .optional(),
                                        fileSize: z
                                          .string()
                                          .describe('Original file size.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        fileType: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'SECURE_PICTURE',
                                            'SECURE_VIDEO',
                                            'SECURE_DOCUMENT',
                                            'SECURE_MUSIC',
                                            'SECURE_ARCHIVE',
                                            'SECURE_RAW',
                                          ])
                                          .describe('File type.')
                                          .optional(),
                                      })
                                      .describe(
                                        'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital properties. Must be passed when `productType: DIGITAL`'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(1000)
                        .optional(),
                    })
                    .describe(
                      'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                    )
                    .optional(),
                  purchaseEligibility: z
                    .object({
                      userCriteria: z.enum(['MEMBERS_ONLY']).optional(),
                    })
                    .describe('Purchase eligibility settings for the product.')
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  tags: z
                    .object({
                      privateTags: z
                        .object({
                          tagIds: z.array(z.string()).max(100).optional(),
                        })
                        .describe(
                          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                        )
                        .optional(),
                      publicTags: z
                        .object({
                          tagIds: z.array(z.string()).max(100).optional(),
                        })
                        .describe(
                          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                        )
                        .optional(),
                    })
                    .describe('Tags')
                    .optional(),
                  subscriptionDetails: z
                    .object({
                      subscriptions: z
                        .array(
                          z.intersection(
                            z.object({
                              _id: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              title: z
                                .string()
                                .describe('Subscription title.')
                                .min(1)
                                .max(20)
                                .optional(),
                              description: z
                                .string()
                                .describe('Subscription description.')
                                .max(60)
                                .optional()
                                .nullable(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                                )
                                .optional()
                                .nullable(),
                              frequency: z
                                .enum([
                                  'UNDEFINED',
                                  'DAY',
                                  'WEEK',
                                  'MONTH',
                                  'YEAR',
                                ])
                                .describe(
                                  'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                                )
                                .optional(),
                              interval: z
                                .number()
                                .int()
                                .describe(
                                  'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              discount: z
                                .intersection(
                                  z.object({
                                    type: z
                                      .enum(['AMOUNT', 'PERCENT'])
                                      .describe('Discount type.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      amountOff: z.never().optional(),
                                      percentOff: z.never().optional(),
                                    }),
                                    z.object({
                                      percentOff: z.never().optional(),
                                      amountOff: z
                                        .string()
                                        .describe(
                                          'Amount to discount from the variant discounted_price.'
                                        ),
                                    }),
                                    z.object({
                                      amountOff: z.never().optional(),
                                      percentOff: z
                                        .number()
                                        .describe(
                                          'Percentage to discount from variant discounted_price.'
                                        )
                                        .min(0.1)
                                        .max(99.9),
                                    }),
                                  ])
                                )
                                .describe(
                                  'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                autoRenewal: z.never().optional(),
                                billingCycles: z.never().optional(),
                              }),
                              z.object({
                                billingCycles: z.never().optional(),
                                autoRenewal: z
                                  .boolean()
                                  .describe(
                                    'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                                  ),
                              }),
                              z.object({
                                autoRenewal: z.never().optional(),
                                billingCycles: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Number of billing cycles before subscription ends.'
                                  )
                                  .min(2)
                                  .max(999),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(6)
                        .optional(),
                      allowOneTimePurchases: z
                        .boolean()
                        .describe(
                          'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                    )
                    .optional(),
                  variantSummary: z
                    .object({
                      variantCount: z
                        .number()
                        .int()
                        .describe(
                          'The total number of variants for the product.'
                        )
                        .optional(),
                    })
                    .describe('The total number of variants for the product.')
                    .optional(),
                }),
                z.xor([
                  z.object({ physicalProperties: z.never().optional() }),
                  z.object({
                    physicalProperties: z
                      .object({
                        pricePerUnit: z
                          .object({
                            quantity: z
                              .number()
                              .describe(
                                'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                              )
                              .min(0.01)
                              .max(999999999.99)
                              .optional(),
                            measurementUnit: z
                              .enum([
                                'UNSPECIFIED',
                                'ML',
                                'CL',
                                'L',
                                'CBM',
                                'MG',
                                'G',
                                'KG',
                                'MM',
                                'CM',
                                'M',
                                'SQM',
                                'OZ',
                                'LB',
                                'FLOZ',
                                'PT',
                                'QT',
                                'GAL',
                                'IN',
                                'FT',
                                'YD',
                                'SQFT',
                              ])
                              .describe(
                                'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                              )
                              .optional(),
                          })
                          .describe(
                            'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                          )
                          .optional(),
                        fulfillerId: z
                          .string()
                          .describe('Fulfiller ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        shippingWeightRange: z
                          .object({
                            minValue: z
                              .number()
                              .describe(
                                'Minimum weight across all variants associated with this product.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional(),
                            maxValue: z
                              .number()
                              .describe(
                                'Maximum weight across all variants associated with this product.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional(),
                          })
                          .describe(
                            'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                          )
                          .optional(),
                        pricePerUnitRange: z
                          .object({
                            minValue: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Minimum price per unit across all variants.'
                              )
                              .optional(),
                            maxValue: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Maximum price per unit across all variants.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                          )
                          .optional(),
                        weightMeasurementUnitInfo: z
                          .object({
                            weightMeasurementUnit: z
                              .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                              .describe('Weight measurement unit.')
                              .optional(),
                          })
                          .describe(
                            'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        deliveryProfileId: z
                          .string()
                          .describe('Delivery profile ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Full product entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(100)
        .optional(),
      bulkActionMetadata: z
        .object({
          totalSuccesses: z
            .number()
            .int()
            .describe('Number of items that were successfully processed.')
            .optional(),
          totalFailures: z
            .number()
            .int()
            .describe("Number of items that couldn't be processed.")
            .optional(),
          undetailedFailures: z
            .number()
            .int()
            .describe(
              'Number of failures without details because detailed failure threshold was exceeded.'
            )
            .optional(),
        })
        .describe('Bulk action metadata.')
        .optional(),
    })
    .describe('Products created by bulk action.')
    .optional(),
  inventoryResults: z
    .object({
      results: z
        .array(
          z.object({
            itemMetadata: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                originalIndex: z
                  .number()
                  .int()
                  .describe(
                    'Index of the item within the request array. Allows for correlation between request and response items.'
                  )
                  .optional(),
                success: z
                  .boolean()
                  .describe(
                    'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                  )
                  .optional(),
                error: z
                  .object({
                    code: z.string().describe('Error code.').optional(),
                    description: z
                      .string()
                      .describe('Description of the error.')
                      .optional(),
                    data: z
                      .record(z.string(), z.any())
                      .describe('Data related to the error.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Details about the error in case of failure.')
                  .optional(),
              })
              .describe('Bulk action metadata for inventory item.')
              .optional(),
            item: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Inventory item ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the inventory item was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the inventory item was last updated.'
                    )
                    .optional()
                    .nullable(),
                  variantId: z
                    .string()
                    .describe('Variant ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  locationId: z
                    .string()
                    .describe(
                      "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  productId: z
                    .string()
                    .describe('Product ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  trackQuantity: z
                    .boolean()
                    .describe('Whether the quantity is being tracked.')
                    .optional(),
                  availabilityStatus: z
                    .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                    .describe('Inventory item availability status.')
                    .optional(),
                  preorderInfo: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                        )
                        .optional()
                        .nullable(),
                      message: z
                        .string()
                        .describe(
                          'Message displayed to customers when the item is out of stock and preorder is enabled.'
                        )
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      limit: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                        )
                        .min(0)
                        .max(100000)
                        .optional()
                        .nullable(),
                      counter: z
                        .number()
                        .int()
                        .describe(
                          'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                        )
                        .min(0)
                        .max(99999)
                        .optional()
                        .nullable(),
                      quantity: z
                        .number()
                        .int()
                        .describe(
                          'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                    )
                    .optional(),
                  product: z
                    .object({
                      name: z
                        .string()
                        .describe('Product name.')
                        .max(80)
                        .optional()
                        .nullable(),
                      directCategoryIds: z
                        .array(z.string())
                        .max(2000)
                        .optional(),
                      variantName: z
                        .string()
                        .describe('Variant name.')
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      variantSku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      variantVisible: z
                        .boolean()
                        .describe(
                          'Whether the variant is visible in the store.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Associated product and variant details.')
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z.never().optional(),
                  }),
                  z.object({
                    quantity: z.never().optional(),
                    inStock: z
                      .boolean()
                      .describe(
                        "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                      ),
                  }),
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                      )
                      .min(-99999)
                      .max(99999),
                  }),
                ])
              )
              .describe(
                'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(1000)
        .optional(),
      bulkActionMetadata: z
        .object({
          totalSuccesses: z
            .number()
            .int()
            .describe('Number of items that were successfully processed.')
            .optional(),
          totalFailures: z
            .number()
            .int()
            .describe("Number of items that couldn't be processed.")
            .optional(),
          undetailedFailures: z
            .number()
            .int()
            .describe(
              'Number of failures without details because detailed failure threshold was exceeded.'
            )
            .optional(),
        })
        .describe('Bulk action metadata.')
        .optional(),
      error: z
        .object({
          code: z.string().describe('Error code.').optional(),
          description: z
            .string()
            .describe('Description of the error.')
            .optional(),
          data: z
            .record(z.string(), z.any())
            .describe('Data related to the error.')
            .optional()
            .nullable(),
        })
        .describe('Error details in case of failed action.')
        .optional(),
    })
    .describe('Inventories created by bulk action.')
    .optional(),
});
export const BulkUpdateProductsRequest = z.object({
  products: z
    .array(
      z.object({
        product: z
          .intersection(
            z.object({
              _id: z.string().describe('Product ID.').min(1).max(36),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                ),
              _createdDate: z
                .date()
                .describe('Date and time the product was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the product was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              description: z
                .any()
                .describe(
                  'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe(
                  'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              directCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              allCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              costRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                )
                .optional(),
              inventory: z
                .object({
                  availabilityStatus: z
                    .enum([
                      'IN_STOCK',
                      'OUT_OF_STOCK',
                      'PARTIALLY_OUT_OF_STOCK',
                    ])
                    .optional(),
                  preorderStatus: z
                    .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                    .optional(),
                  preorderAvailability: z
                    .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                    .optional(),
                })
                .describe(
                  'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                )
                .optional(),
              productType: z.enum(['PHYSICAL', 'DIGITAL']).optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              currency: z
                .string()
                .describe(
                  'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                )
                .optional()
                .nullable(),
              breadcrumbsInfo: z
                .object({
                  breadcrumbs: z
                    .array(
                      z.object({
                        categoryId: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        categoryName: z
                          .string()
                          .describe('Category name.')
                          .min(1)
                          .max(80)
                          .optional(),
                        categorySlug: z
                          .string()
                          .describe('Category slug.')
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe(
                  'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              actualPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum current selling prices across all product variants.'
                )
                .optional(),
              compareAtPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                )
                .optional(),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      ),
                                  })
                                  .describe(
                                    "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                  )
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .optional(),
                                  })
                                  .describe(
                                    'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                ),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.'),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          media: z
                            .intersection(
                              z.object({
                                altText: z
                                  .string()
                                  .describe('Image alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                displayName: z
                                  .string()
                                  .describe(
                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                  )
                                  .max(80)
                                  .optional()
                                  .nullable(),
                                mediaType: z
                                  .enum(['IMAGE', 'VIDEO'])
                                  .optional(),
                                thumbnail: z
                                  .object({
                                    url: z
                                      .string()
                                      .describe('Thumbnail url.')
                                      .url()
                                      .optional(),
                                    height: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail height.')
                                      .optional(),
                                    width: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail width.')
                                      .optional(),
                                    altText: z
                                      .string()
                                      .describe('Thumbnail alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                                uploadId: z
                                  .string()
                                  .describe(
                                    'ID used to upload media to Wix Media Manager.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              }),
                              z.intersection(
                                z.xor([
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z.never().optional(),
                                  }),
                                  z.object({
                                    url: z.never().optional(),
                                    _id: z
                                      .string()
                                      .describe(
                                        'Set media by ID of an existing file in Wix Media Manager.'
                                      )
                                      .min(1)
                                      .max(200),
                                  }),
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z
                                      .string()
                                      .describe(
                                        'Set media using an external media URL.'
                                      )
                                      .url(),
                                  }),
                                ]),
                                z.xor([
                                  z.object({
                                    image: z.never().optional(),
                                    video: z.never().optional(),
                                  }),
                                  z.object({
                                    video: z.never().optional(),
                                    image: z
                                      .string()
                                      .describe(
                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                      ),
                                  }),
                                  z.object({
                                    image: z.never().optional(),
                                    video: z
                                      .string()
                                      .describe(
                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('Variant media.')
                            .optional(),
                          subscriptionPricesInfo: z
                            .object({
                              subscriptionPrices: z
                                .array(
                                  z.object({
                                    subscriptionId: z
                                      .string()
                                      .describe('Subscription ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                    price: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                      )
                                      .optional(),
                                    pricePerUnit: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Price per unit info.')
                                      .optional(),
                                  })
                                )
                                .max(6)
                                .optional(),
                            })
                            .describe(
                              'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          inventoryStatus: z
                            .object({
                              inStock: z
                                .boolean()
                                .describe('Whether the variant is in stock.')
                                .optional(),
                              preorderEnabled: z
                                .boolean()
                                .describe(
                                  'Whether preorder is enabled for this variant.'
                                )
                                .optional(),
                            })
                            .describe('Variant inventory status.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z.enum([
                            'UNDEFINED',
                            'DAY',
                            'WEEK',
                            'MONTH',
                            'YEAR',
                          ]),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                              z.xor([
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Amount to discount from the variant discounted_price.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage to discount from variant discounted_price.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              variantSummary: z
                .object({
                  variantCount: z
                    .number()
                    .int()
                    .describe('The total number of variants for the product.')
                    .optional(),
                })
                .describe('The total number of variants for the product.')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe('Product to update.'),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full product entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const BulkUpdateProductsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Information about successful action or error for failure.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the product was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the product was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              description: z
                .any()
                .describe(
                  'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z
                                .enum(['IMAGE', 'VIDEO'])
                                .describe('Media type.')
                                .optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe(
                  'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Option render type.')
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ choicesSettings: z.never().optional() }),
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Modifier render type.')
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z.never().optional(),
                      }),
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              directCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              allCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              costRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                )
                .optional(),
              inventory: z
                .object({
                  availabilityStatus: z
                    .enum([
                      'IN_STOCK',
                      'OUT_OF_STOCK',
                      'PARTIALLY_OUT_OF_STOCK',
                    ])
                    .describe('Current availability status.')
                    .optional(),
                  preorderStatus: z
                    .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                    .describe('Current preorder status.')
                    .optional(),
                  preorderAvailability: z
                    .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                    .describe('Preorder availability status.')
                    .optional(),
                })
                .describe(
                  'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                )
                .optional(),
              productType: z
                .enum(['PHYSICAL', 'DIGITAL'])
                .describe(
                  'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
                )
                .optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              currency: z
                .string()
                .describe(
                  'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                )
                .optional()
                .nullable(),
              breadcrumbsInfo: z
                .object({
                  breadcrumbs: z
                    .array(
                      z.object({
                        categoryId: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        categoryName: z
                          .string()
                          .describe('Category name.')
                          .min(1)
                          .max(80)
                          .optional(),
                        categorySlug: z
                          .string()
                          .describe('Category slug.')
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe(
                  'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              actualPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum current selling prices across all product variants.'
                )
                .optional(),
              compareAtPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                )
                .optional(),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36)
                                      .optional(),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                  )
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .describe(
                                        'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                )
                                .optional(),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.')
                            .optional(),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          media: z
                            .intersection(
                              z.object({
                                altText: z
                                  .string()
                                  .describe('Image alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                displayName: z
                                  .string()
                                  .describe(
                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                  )
                                  .max(80)
                                  .optional()
                                  .nullable(),
                                mediaType: z
                                  .enum(['IMAGE', 'VIDEO'])
                                  .describe('Media type.')
                                  .optional(),
                                thumbnail: z
                                  .object({
                                    url: z
                                      .string()
                                      .describe('Thumbnail url.')
                                      .url()
                                      .optional(),
                                    height: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail height.')
                                      .optional(),
                                    width: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail width.')
                                      .optional(),
                                    altText: z
                                      .string()
                                      .describe('Thumbnail alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                                uploadId: z
                                  .string()
                                  .describe(
                                    'ID used to upload media to Wix Media Manager.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              }),
                              z.intersection(
                                z.xor([
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z.never().optional(),
                                  }),
                                  z.object({
                                    url: z.never().optional(),
                                    _id: z
                                      .string()
                                      .describe(
                                        'Set media by ID of an existing file in Wix Media Manager.'
                                      )
                                      .min(1)
                                      .max(200),
                                  }),
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z
                                      .string()
                                      .describe(
                                        'Set media using an external media URL.'
                                      )
                                      .url(),
                                  }),
                                ]),
                                z.xor([
                                  z.object({
                                    image: z.never().optional(),
                                    video: z.never().optional(),
                                  }),
                                  z.object({
                                    video: z.never().optional(),
                                    image: z
                                      .string()
                                      .describe(
                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                      ),
                                  }),
                                  z.object({
                                    image: z.never().optional(),
                                    video: z
                                      .string()
                                      .describe(
                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('Variant media.')
                            .optional(),
                          subscriptionPricesInfo: z
                            .object({
                              subscriptionPrices: z
                                .array(
                                  z.object({
                                    subscriptionId: z
                                      .string()
                                      .describe('Subscription ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                    price: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                      )
                                      .optional(),
                                    pricePerUnit: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Price per unit info.')
                                      .optional(),
                                  })
                                )
                                .max(6)
                                .optional(),
                            })
                            .describe(
                              'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          inventoryStatus: z
                            .object({
                              inStock: z
                                .boolean()
                                .describe('Whether the variant is in stock.')
                                .optional(),
                              preorderEnabled: z
                                .boolean()
                                .describe(
                                  'Whether preorder is enabled for this variant.'
                                )
                                .optional(),
                            })
                            .describe('Variant inventory status.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20)
                            .optional(),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe(
                              'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                            )
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({
                                type: z
                                  .enum(['AMOUNT', 'PERCENT'])
                                  .describe('Discount type.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z.never().optional(),
                                }),
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Amount to discount from the variant discounted_price.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage to discount from variant discounted_price.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z.never().optional(),
                          }),
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              variantSummary: z
                .object({
                  variantCount: z
                    .number()
                    .int()
                    .describe('The total number of variants for the product.')
                    .optional(),
                })
                .describe('The total number of variants for the product.')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full product entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateProductsWithInventoryRequest = z.object({
  products: z
    .array(
      z.object({
        product: z
          .intersection(
            z.object({
              _id: z.string().describe('Product ID.').min(1).max(36),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product with inventory.'
                ),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .optional()
                .nullable(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  'Whether the product is visible to site visitors.\n\nDefault: `true`'
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe('Product media items.')
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product brand.\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product ribbon.\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct category, which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productType: z.enum(['PHYSICAL', 'DIGITAL']).optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the variant is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                ),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.'),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          inventoryItem: z
                            .intersection(
                              z.object({
                                _id: z
                                  .string()
                                  .describe(
                                    'Inventory item ID.\nIf you want to update existing inventory item you must provide id,\notherwise new inventory item with different id will be created.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                revision: z
                                  .string()
                                  .regex(
                                    /^-?\d+$/,
                                    'Must be a valid Int64 string'
                                  )
                                  .describe('Inventory item revision.')
                                  .optional()
                                  .nullable(),
                                preorderInfo: z
                                  .object({
                                    enabled: z
                                      .boolean()
                                      .describe(
                                        "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                                      )
                                      .optional()
                                      .nullable(),
                                    message: z
                                      .string()
                                      .describe(
                                        'Message displayed to customers when the item is out of stock and preorder is enabled.'
                                      )
                                      .min(1)
                                      .max(250)
                                      .optional()
                                      .nullable(),
                                    limit: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                                      )
                                      .min(0)
                                      .max(100000)
                                      .optional()
                                      .nullable(),
                                    counter: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                                      )
                                      .min(0)
                                      .max(99999)
                                      .optional()
                                      .nullable(),
                                    quantity: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                                      )
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Item preorder info.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  inStock: z.never().optional(),
                                  quantity: z.never().optional(),
                                }),
                                z.object({
                                  quantity: z.never().optional(),
                                  inStock: z
                                    .boolean()
                                    .describe(
                                      'Tracking method - in stock.\n\nWhen set to `true`, the item is available for sale without a quantity limit.'
                                    ),
                                }),
                                z.object({
                                  inStock: z.never().optional(),
                                  quantity: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Tracking method - quantity left in inventory.\n\nQuantity can be negative when decrementing inventory for an order that has already been paid.'
                                    )
                                    .min(-99999)
                                    .max(99999),
                                }),
                              ])
                            )
                            .describe(
                              'Inventory item of the variant on the default location.'
                            )
                            .optional(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      ),
                                  })
                                  .describe('Option and choice IDs.')
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .optional(),
                                  })
                                  .describe('Option and choice names.')
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  "Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nVariants aren't auto-generated. You must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants)."
                )
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z.enum([
                            'UNDEFINED',
                            'DAY',
                            'WEEK',
                            'MONTH',
                            'YEAR',
                          ]),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                              z.xor([
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Amount to discount from the variant discounted_price.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage to discount from variant discounted_price.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe('Product to update.'),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full product entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const BulkUpdateProductsWithInventoryResponse = z.object({
  productResults: z
    .object({
      results: z
        .array(
          z.object({
            itemMetadata: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                originalIndex: z
                  .number()
                  .int()
                  .describe(
                    'Index of the item within the request array. Allows for correlation between request and response items.'
                  )
                  .optional(),
                success: z
                  .boolean()
                  .describe(
                    'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                  )
                  .optional(),
                error: z
                  .object({
                    code: z.string().describe('Error code.').optional(),
                    description: z
                      .string()
                      .describe('Description of the error.')
                      .optional(),
                    data: z
                      .record(z.string(), z.any())
                      .describe('Data related to the error.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Details about the error in case of failure.')
                  .optional(),
              })
              .describe(
                'Information about successful action or error for failure.'
              )
              .optional(),
            item: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Product ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the product was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe('Date and time the product was updated.')
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Product name. Translatable.')
                    .min(1)
                    .max(80)
                    .optional()
                    .nullable(),
                  slug: z
                    .string()
                    .describe(
                      'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                    )
                    .optional()
                    .nullable(),
                  url: z
                    .string()
                    .describe(
                      'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  description: z
                    .any()
                    .describe(
                      'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                    )
                    .optional(),
                  plainDescription: z
                    .string()
                    .describe(
                      'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                  visible: z
                    .boolean()
                    .describe(
                      "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                    )
                    .optional()
                    .nullable(),
                  visibleInPos: z
                    .boolean()
                    .describe(
                      'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                    )
                    .optional()
                    .nullable(),
                  media: z
                    .object({
                      main: z
                        .intersection(
                          z.object({
                            altText: z
                              .string()
                              .describe('Image alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                            displayName: z
                              .string()
                              .describe(
                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                              )
                              .max(80)
                              .optional()
                              .nullable(),
                            mediaType: z
                              .enum(['IMAGE', 'VIDEO'])
                              .describe('Media type.')
                              .optional(),
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .url()
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .optional(),
                                altText: z
                                  .string()
                                  .describe('Thumbnail alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                            uploadId: z
                              .string()
                              .describe(
                                'ID used to upload media to Wix Media Manager.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.intersection(
                            z.xor([
                              z.object({
                                _id: z.never().optional(),
                                url: z.never().optional(),
                              }),
                              z.object({
                                url: z.never().optional(),
                                _id: z
                                  .string()
                                  .describe(
                                    'Set media by ID of an existing file in Wix Media Manager.'
                                  )
                                  .min(1)
                                  .max(200),
                              }),
                              z.object({
                                _id: z.never().optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'Set media using an external media URL.'
                                  )
                                  .url(),
                              }),
                            ]),
                            z.xor([
                              z.object({
                                image: z.never().optional(),
                                video: z.never().optional(),
                              }),
                              z.object({
                                video: z.never().optional(),
                                image: z
                                  .string()
                                  .describe(
                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                  ),
                              }),
                              z.object({
                                image: z.never().optional(),
                                video: z
                                  .string()
                                  .describe(
                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                  ),
                              }),
                            ])
                          )
                        )
                        .describe(
                          'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                        )
                        .optional(),
                      itemsInfo: z
                        .object({
                          items: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(50)
                            .optional(),
                        })
                        .describe(
                          'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                    )
                    .optional(),
                  seoData: z
                    .object({
                      tags: z
                        .array(
                          z.object({
                            type: z
                              .string()
                              .describe(
                                'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                              )
                              .optional(),
                            props: z
                              .record(z.string(), z.any())
                              .describe(
                                'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                              )
                              .optional()
                              .nullable(),
                            meta: z
                              .record(z.string(), z.any())
                              .describe(
                                'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                              )
                              .optional()
                              .nullable(),
                            children: z
                              .string()
                              .describe(
                                'SEO tag inner content. For example, `<title> inner content </title>`.'
                              )
                              .optional(),
                            custom: z
                              .boolean()
                              .describe(
                                'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                              )
                              .optional(),
                            disabled: z
                              .boolean()
                              .describe(
                                "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                              )
                              .optional(),
                          })
                        )
                        .optional(),
                      settings: z
                        .object({
                          preventAutoRedirect: z
                            .boolean()
                            .describe(
                              'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                            )
                            .optional(),
                          keywords: z
                            .array(
                              z.object({
                                term: z
                                  .string()
                                  .describe('Keyword value.')
                                  .optional(),
                                isMain: z
                                  .boolean()
                                  .describe(
                                    'Whether the keyword is the main focus keyword.'
                                  )
                                  .optional(),
                                origin: z
                                  .string()
                                  .describe(
                                    'The source that added the keyword terms to the SEO settings.'
                                  )
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                            )
                            .max(5)
                            .optional(),
                        })
                        .describe('SEO general settings.')
                        .optional(),
                    })
                    .describe('Product SEO data.')
                    .optional(),
                  taxGroupId: z
                    .string()
                    .describe(
                      '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  options: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe(
                              'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                            )
                            .min(1)
                            .max(36)
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Option name.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          optionRenderType: z
                            .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                            .describe('Option render type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                        }),
                        z.xor([
                          z.object({ choicesSettings: z.never().optional() }),
                          z.object({
                            choicesSettings: z
                              .object({
                                choices: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        choiceId: z
                                          .string()
                                          .describe('Choice ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional()
                                          .nullable(),
                                        linkedMedia: z
                                          .array(
                                            z.intersection(
                                              z.object({
                                                altText: z
                                                  .string()
                                                  .describe('Image alt text.')
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                                displayName: z
                                                  .string()
                                                  .describe(
                                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                                  )
                                                  .max(80)
                                                  .optional()
                                                  .nullable(),
                                                mediaType: z
                                                  .enum(['IMAGE', 'VIDEO'])
                                                  .describe('Media type.')
                                                  .optional(),
                                                thumbnail: z
                                                  .object({
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail url.'
                                                      )
                                                      .url()
                                                      .optional(),
                                                    height: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail height.'
                                                      )
                                                      .optional(),
                                                    width: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail width.'
                                                      )
                                                      .optional(),
                                                    altText: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail alt text.'
                                                      )
                                                      .min(1)
                                                      .max(1000)
                                                      .optional()
                                                      .nullable(),
                                                  })
                                                  .describe(
                                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                                  )
                                                  .optional(),
                                                uploadId: z
                                                  .string()
                                                  .describe(
                                                    'ID used to upload media to Wix Media Manager.'
                                                  )
                                                  .regex(
                                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                    'Must be a valid GUID'
                                                  )
                                                  .optional(),
                                              }),
                                              z.intersection(
                                                z.xor([
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    url: z.never().optional(),
                                                    _id: z
                                                      .string()
                                                      .describe(
                                                        'Set media by ID of an existing file in Wix Media Manager.'
                                                      )
                                                      .min(1)
                                                      .max(200),
                                                  }),
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Set media using an external media URL.'
                                                      )
                                                      .url(),
                                                  }),
                                                ]),
                                                z.xor([
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    video: z.never().optional(),
                                                    image: z
                                                      .string()
                                                      .describe(
                                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z
                                                      .string()
                                                      .describe(
                                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                ])
                                              )
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                        choiceType: z
                                          .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                          .describe('Choice type.')
                                          .optional(),
                                        key: z
                                          .string()
                                          .describe(
                                            'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                          )
                                          .min(1)
                                          .max(50)
                                          .optional(),
                                        name: z
                                          .string()
                                          .describe(
                                            'Choice name. For text choices, this field also contains the choice value.'
                                          )
                                          .min(1)
                                          .max(50)
                                          .optional()
                                          .nullable(),
                                        inStock: z
                                          .boolean()
                                          .describe(
                                            'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                          )
                                          .optional(),
                                        visible: z
                                          .boolean()
                                          .describe(
                                            'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                          )
                                          .optional(),
                                      }),
                                      z.xor([
                                        z.object({
                                          colorCode: z.never().optional(),
                                        }),
                                        z.object({
                                          colorCode: z
                                            .string()
                                            .describe(
                                              'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                            )
                                            .min(3)
                                            .max(20),
                                        }),
                                      ])
                                    )
                                  )
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Choices settings.'),
                          }),
                        ])
                      )
                    )
                    .max(6)
                    .optional(),
                  modifiers: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe(
                              'ID of a customization with `customizationType: MODIFIER`.'
                            )
                            .min(1)
                            .max(36)
                            .optional()
                            .nullable(),
                          name: z
                            .string()
                            .describe('Modifier title.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          modifierRenderType: z
                            .enum([
                              'FREE_TEXT',
                              'TEXT_CHOICES',
                              'SWATCH_CHOICES',
                            ])
                            .describe('Modifier render type.')
                            .optional(),
                          mandatory: z
                            .boolean()
                            .describe(
                              'Whether customer input is required for this modifier.'
                            )
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            freeTextSettings: z.never().optional(),
                            choicesSettings: z.never().optional(),
                          }),
                          z.object({
                            choicesSettings: z.never().optional(),
                            freeTextSettings: z
                              .object({
                                minCharCount: z
                                  .number()
                                  .int()
                                  .describe('Minimum number of characters.')
                                  .optional(),
                                maxCharCount: z
                                  .number()
                                  .int()
                                  .describe('Maximum number of characters.')
                                  .max(500)
                                  .optional(),
                                defaultAddedPrice: z
                                  .string()
                                  .describe(
                                    "Default amount to be added to the product's price."
                                  )
                                  .optional()
                                  .nullable(),
                                title: z
                                  .string()
                                  .describe(
                                    'Title of the text to be input by the customer.'
                                  )
                                  .min(1)
                                  .max(150)
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(150)
                                  .optional(),
                              })
                              .describe('Free text modifier settings.'),
                          }),
                          z.object({
                            freeTextSettings: z.never().optional(),
                            choicesSettings: z
                              .object({
                                choices: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        choiceId: z
                                          .string()
                                          .describe('Choice ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional()
                                          .nullable(),
                                        linkedMedia: z
                                          .array(
                                            z.intersection(
                                              z.object({
                                                altText: z
                                                  .string()
                                                  .describe('Image alt text.')
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                                displayName: z
                                                  .string()
                                                  .describe(
                                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                                  )
                                                  .max(80)
                                                  .optional()
                                                  .nullable(),
                                                mediaType: z
                                                  .enum(['IMAGE', 'VIDEO'])
                                                  .describe('Media type.')
                                                  .optional(),
                                                thumbnail: z
                                                  .object({
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail url.'
                                                      )
                                                      .url()
                                                      .optional(),
                                                    height: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail height.'
                                                      )
                                                      .optional(),
                                                    width: z
                                                      .number()
                                                      .int()
                                                      .describe(
                                                        'Thumbnail width.'
                                                      )
                                                      .optional(),
                                                    altText: z
                                                      .string()
                                                      .describe(
                                                        'Thumbnail alt text.'
                                                      )
                                                      .min(1)
                                                      .max(1000)
                                                      .optional()
                                                      .nullable(),
                                                  })
                                                  .describe(
                                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                                  )
                                                  .optional(),
                                                uploadId: z
                                                  .string()
                                                  .describe(
                                                    'ID used to upload media to Wix Media Manager.'
                                                  )
                                                  .regex(
                                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                    'Must be a valid GUID'
                                                  )
                                                  .optional(),
                                              }),
                                              z.intersection(
                                                z.xor([
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    url: z.never().optional(),
                                                    _id: z
                                                      .string()
                                                      .describe(
                                                        'Set media by ID of an existing file in Wix Media Manager.'
                                                      )
                                                      .min(1)
                                                      .max(200),
                                                  }),
                                                  z.object({
                                                    _id: z.never().optional(),
                                                    url: z
                                                      .string()
                                                      .describe(
                                                        'Set media using an external media URL.'
                                                      )
                                                      .url(),
                                                  }),
                                                ]),
                                                z.xor([
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z.never().optional(),
                                                  }),
                                                  z.object({
                                                    video: z.never().optional(),
                                                    image: z
                                                      .string()
                                                      .describe(
                                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                  z.object({
                                                    image: z.never().optional(),
                                                    video: z
                                                      .string()
                                                      .describe(
                                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                      ),
                                                  }),
                                                ])
                                              )
                                            )
                                          )
                                          .max(15)
                                          .optional(),
                                        choiceType: z
                                          .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                          .describe('Choice type.')
                                          .optional(),
                                        key: z
                                          .string()
                                          .describe(
                                            'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                          )
                                          .min(1)
                                          .max(50)
                                          .optional(),
                                        name: z
                                          .string()
                                          .describe('Choice name.')
                                          .min(1)
                                          .max(50)
                                          .optional()
                                          .nullable(),
                                        addedPrice: z
                                          .string()
                                          .describe('Added price.')
                                          .optional()
                                          .nullable(),
                                      }),
                                      z.xor([
                                        z.object({
                                          colorCode: z.never().optional(),
                                        }),
                                        z.object({
                                          colorCode: z
                                            .string()
                                            .describe(
                                              'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                            )
                                            .min(3)
                                            .max(20),
                                        }),
                                      ])
                                    )
                                  )
                                  .min(1)
                                  .max(100)
                                  .optional(),
                              })
                              .describe('Choice settings.'),
                          }),
                        ])
                      )
                    )
                    .max(10)
                    .optional(),
                  brand: z
                    .object({
                      _id: z
                        .string()
                        .describe('Brand ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Brand name.')
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                    )
                    .optional(),
                  infoSections: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Info section ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        uniqueName: z
                          .string()
                          .describe(
                            'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                          )
                          .min(1)
                          .max(50)
                          .optional()
                          .nullable(),
                        description: z
                          .any()
                          .describe(
                            'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                          )
                          .optional(),
                        plainDescription: z
                          .string()
                          .describe(
                            'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                          )
                          .max(16000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(10)
                    .optional(),
                  ribbon: z
                    .object({
                      _id: z
                        .string()
                        .describe('Ribbon ID.')
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Ribbon name.')
                        .max(30)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                    )
                    .optional(),
                  directCategoriesInfo: z
                    .object({
                      categories: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Category ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            index: z
                              .number()
                              .int()
                              .describe(
                                'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                              )
                              .min(0)
                              .max(200)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(2000)
                        .optional(),
                    })
                    .describe(
                      'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  allCategoriesInfo: z
                    .object({
                      categories: z
                        .array(
                          z.object({
                            _id: z
                              .string()
                              .describe('Category ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            index: z
                              .number()
                              .int()
                              .describe(
                                'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                              )
                              .min(0)
                              .max(200)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(2000)
                        .optional(),
                    })
                    .describe(
                      'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  mainCategoryId: z
                    .string()
                    .describe(
                      'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  costRange: z
                    .object({
                      minValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Minimum value.')
                        .optional(),
                      maxValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Maximum value.')
                        .optional(),
                    })
                    .describe(
                      'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                    )
                    .optional(),
                  inventory: z
                    .object({
                      availabilityStatus: z
                        .enum([
                          'IN_STOCK',
                          'OUT_OF_STOCK',
                          'PARTIALLY_OUT_OF_STOCK',
                        ])
                        .describe('Current availability status.')
                        .optional(),
                      preorderStatus: z
                        .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                        .describe('Current preorder status.')
                        .optional(),
                      preorderAvailability: z
                        .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                        .describe('Preorder availability status.')
                        .optional(),
                    })
                    .describe(
                      'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                    )
                    .optional(),
                  productType: z
                    .enum(['PHYSICAL', 'DIGITAL'])
                    .describe(
                      'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
                    )
                    .optional(),
                  handle: z
                    .string()
                    .describe(
                      "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                    )
                    .min(1)
                    .max(100)
                    .optional()
                    .nullable(),
                  currency: z
                    .string()
                    .describe(
                      'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .optional()
                    .nullable(),
                  breadcrumbsInfo: z
                    .object({
                      breadcrumbs: z
                        .array(
                          z.object({
                            categoryId: z
                              .string()
                              .describe('Category ID.')
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                            categoryName: z
                              .string()
                              .describe('Category name.')
                              .min(1)
                              .max(80)
                              .optional(),
                            categorySlug: z
                              .string()
                              .describe('Category slug.')
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe(
                      'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  actualPriceRange: z
                    .object({
                      minValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Minimum value.')
                        .optional(),
                      maxValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Maximum value.')
                        .optional(),
                    })
                    .describe(
                      'Minimum and maximum current selling prices across all product variants.'
                    )
                    .optional(),
                  compareAtPriceRange: z
                    .object({
                      minValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Minimum value.')
                        .optional(),
                      maxValue: z
                        .object({
                          amount: z
                            .string()
                            .describe(
                              'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                            )
                            .optional(),
                          formattedAmount: z
                            .string()
                            .describe(
                              'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(20)
                            .optional()
                            .nullable(),
                        })
                        .describe('Maximum value.')
                        .optional(),
                    })
                    .describe(
                      'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                    )
                    .optional(),
                  variantsInfo: z
                    .object({
                      variants: z
                        .array(
                          z.intersection(
                            z.object({
                              _id: z
                                .string()
                                .describe('Variant ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              visible: z
                                .boolean()
                                .describe(
                                  "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                                )
                                .optional()
                                .nullable(),
                              sku: z
                                .string()
                                .describe('Variant SKU (stock keeping unit).')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                              barcode: z
                                .string()
                                .describe('Variant barcode.')
                                .min(1)
                                .max(40)
                                .optional()
                                .nullable(),
                              choices: z
                                .array(
                                  z.object({
                                    optionChoiceIds: z
                                      .object({
                                        optionId: z
                                          .string()
                                          .describe('Option ID.')
                                          .min(1)
                                          .max(36)
                                          .optional(),
                                        choiceId: z
                                          .string()
                                          .describe('Choice ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                      )
                                      .optional(),
                                    optionChoiceNames: z
                                      .object({
                                        optionName: z
                                          .string()
                                          .describe('Option name.')
                                          .min(1)
                                          .optional(),
                                        choiceName: z
                                          .string()
                                          .describe('Choice name.')
                                          .min(1)
                                          .optional(),
                                        renderType: z
                                          .enum([
                                            'TEXT_CHOICES',
                                            'SWATCH_CHOICES',
                                          ])
                                          .describe(
                                            'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                      )
                                      .optional(),
                                  })
                                )
                                .min(0)
                                .max(6)
                                .optional(),
                              price: z
                                .object({
                                  actualPrice: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      "Variant's current selling price. Must be greater than or equal to 0."
                                    )
                                    .optional(),
                                  compareAtPrice: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                    )
                                    .optional(),
                                })
                                .describe('Variant price.')
                                .optional(),
                              revenueDetails: z
                                .object({
                                  cost: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe('Item cost.')
                                    .optional(),
                                  profit: z
                                    .object({
                                      amount: z
                                        .string()
                                        .describe(
                                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                        )
                                        .optional(),
                                      formattedAmount: z
                                        .string()
                                        .describe(
                                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                        )
                                        .max(20)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                    )
                                    .optional(),
                                  profitMargin: z
                                    .number()
                                    .describe(
                                      'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                    )
                                    .min(0)
                                    .max(1)
                                    .optional(),
                                })
                                .describe(
                                  'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                                )
                                .optional(),
                              media: z
                                .intersection(
                                  z.object({
                                    altText: z
                                      .string()
                                      .describe('Image alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    displayName: z
                                      .string()
                                      .describe(
                                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                      )
                                      .max(80)
                                      .optional()
                                      .nullable(),
                                    mediaType: z
                                      .enum(['IMAGE', 'VIDEO'])
                                      .describe('Media type.')
                                      .optional(),
                                    thumbnail: z
                                      .object({
                                        url: z
                                          .string()
                                          .describe('Thumbnail url.')
                                          .url()
                                          .optional(),
                                        height: z
                                          .number()
                                          .int()
                                          .describe('Thumbnail height.')
                                          .optional(),
                                        width: z
                                          .number()
                                          .int()
                                          .describe('Thumbnail width.')
                                          .optional(),
                                        altText: z
                                          .string()
                                          .describe('Thumbnail alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                      )
                                      .optional(),
                                    uploadId: z
                                      .string()
                                      .describe(
                                        'ID used to upload media to Wix Media Manager.'
                                      )
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  }),
                                  z.intersection(
                                    z.xor([
                                      z.object({
                                        _id: z.never().optional(),
                                        url: z.never().optional(),
                                      }),
                                      z.object({
                                        url: z.never().optional(),
                                        _id: z
                                          .string()
                                          .describe(
                                            'Set media by ID of an existing file in Wix Media Manager.'
                                          )
                                          .min(1)
                                          .max(200),
                                      }),
                                      z.object({
                                        _id: z.never().optional(),
                                        url: z
                                          .string()
                                          .describe(
                                            'Set media using an external media URL.'
                                          )
                                          .url(),
                                      }),
                                    ]),
                                    z.xor([
                                      z.object({
                                        image: z.never().optional(),
                                        video: z.never().optional(),
                                      }),
                                      z.object({
                                        video: z.never().optional(),
                                        image: z
                                          .string()
                                          .describe(
                                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                          ),
                                      }),
                                      z.object({
                                        image: z.never().optional(),
                                        video: z
                                          .string()
                                          .describe(
                                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                          ),
                                      }),
                                    ])
                                  )
                                )
                                .describe('Variant media.')
                                .optional(),
                              subscriptionPricesInfo: z
                                .object({
                                  subscriptionPrices: z
                                    .array(
                                      z.object({
                                        subscriptionId: z
                                          .string()
                                          .describe('Subscription ID.')
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                        price: z
                                          .object({
                                            amount: z
                                              .string()
                                              .describe(
                                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                              )
                                              .optional(),
                                            formattedAmount: z
                                              .string()
                                              .describe(
                                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                              )
                                              .max(20)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                          )
                                          .optional(),
                                        pricePerUnit: z
                                          .object({
                                            value: z
                                              .string()
                                              .describe(
                                                'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                              )
                                              .optional(),
                                            description: z
                                              .string()
                                              .describe(
                                                'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                              )
                                              .max(20)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe('Price per unit info.')
                                          .optional(),
                                      })
                                    )
                                    .max(6)
                                    .optional(),
                                })
                                .describe(
                                  'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              inventoryStatus: z
                                .object({
                                  inStock: z
                                    .boolean()
                                    .describe(
                                      'Whether the variant is in stock.'
                                    )
                                    .optional(),
                                  preorderEnabled: z
                                    .boolean()
                                    .describe(
                                      'Whether preorder is enabled for this variant.'
                                    )
                                    .optional(),
                                })
                                .describe('Variant inventory status.')
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                physicalProperties: z.never().optional(),
                                digitalProperties: z.never().optional(),
                              }),
                              z.object({
                                digitalProperties: z.never().optional(),
                                physicalProperties: z
                                  .object({
                                    weight: z
                                      .number()
                                      .describe(
                                        'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                      )
                                      .min(0)
                                      .max(999999999.99)
                                      .optional()
                                      .nullable(),
                                    pricePerUnit: z
                                      .object({
                                        settings: z
                                          .object({
                                            quantity: z
                                              .number()
                                              .describe(
                                                'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                              )
                                              .min(0.01)
                                              .max(999999999.99)
                                              .optional(),
                                            measurementUnit: z
                                              .enum([
                                                'UNSPECIFIED',
                                                'ML',
                                                'CL',
                                                'L',
                                                'CBM',
                                                'MG',
                                                'G',
                                                'KG',
                                                'MM',
                                                'CM',
                                                'M',
                                                'SQM',
                                                'OZ',
                                                'LB',
                                                'FLOZ',
                                                'PT',
                                                'QT',
                                                'GAL',
                                                'IN',
                                                'FT',
                                                'YD',
                                                'SQFT',
                                              ])
                                              .describe(
                                                'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                          )
                                          .optional(),
                                        value: z
                                          .string()
                                          .describe(
                                            "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(100)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                      )
                                      .optional(),
                                    productDimensions: z
                                      .object({
                                        length: z
                                          .string()
                                          .describe(
                                            'Length. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        width: z
                                          .string()
                                          .describe(
                                            'Width. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        height: z
                                          .string()
                                          .describe(
                                            'Height. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        unit: z
                                          .enum([
                                            'UNKNOWN_DIMENSIONS_UNIT',
                                            'MM',
                                            'CM',
                                            'M',
                                            'IN',
                                            'FT',
                                            'YD',
                                          ])
                                          .describe(
                                            'Unit of measurement for dimensions (length, width, height).'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Product dimensions (length, width, height of the physical product).'
                                      )
                                      .optional(),
                                    packageDimensions: z
                                      .object({
                                        length: z
                                          .string()
                                          .describe(
                                            'Length. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        width: z
                                          .string()
                                          .describe(
                                            'Width. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        height: z
                                          .string()
                                          .describe(
                                            'Height. Measurement unit is handled at the system level (metric/imperial).'
                                          )
                                          .optional()
                                          .nullable(),
                                        unit: z
                                          .enum([
                                            'UNKNOWN_DIMENSIONS_UNIT',
                                            'MM',
                                            'CM',
                                            'M',
                                            'IN',
                                            'FT',
                                            'YD',
                                          ])
                                          .describe(
                                            'Unit of measurement for dimensions (length, width, height).'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Package dimensions (length, width, height of the shipping package).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Physical properties. Must be passed when `productType: PHYSICAL`'
                                  ),
                              }),
                              z.object({
                                physicalProperties: z.never().optional(),
                                digitalProperties: z
                                  .object({
                                    digitalFile: z
                                      .object({
                                        _id: z
                                          .string()
                                          .describe(
                                            'Media ID in media manager.'
                                          )
                                          .min(1)
                                          .max(100)
                                          .optional(),
                                        fileName: z
                                          .string()
                                          .describe('Original file name.')
                                          .min(1)
                                          .max(1000)
                                          .optional(),
                                        fileSize: z
                                          .string()
                                          .describe('Original file size.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        fileType: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'SECURE_PICTURE',
                                            'SECURE_VIDEO',
                                            'SECURE_DOCUMENT',
                                            'SECURE_MUSIC',
                                            'SECURE_ARCHIVE',
                                            'SECURE_RAW',
                                          ])
                                          .describe('File type.')
                                          .optional(),
                                      })
                                      .describe(
                                        'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital properties. Must be passed when `productType: DIGITAL`'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(1000)
                        .optional(),
                    })
                    .describe(
                      'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                    )
                    .optional(),
                  purchaseEligibility: z
                    .object({
                      userCriteria: z.enum(['MEMBERS_ONLY']).optional(),
                    })
                    .describe('Purchase eligibility settings for the product.')
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                  tags: z
                    .object({
                      privateTags: z
                        .object({
                          tagIds: z.array(z.string()).max(100).optional(),
                        })
                        .describe(
                          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                        )
                        .optional(),
                      publicTags: z
                        .object({
                          tagIds: z.array(z.string()).max(100).optional(),
                        })
                        .describe(
                          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                        )
                        .optional(),
                    })
                    .describe('Tags')
                    .optional(),
                  subscriptionDetails: z
                    .object({
                      subscriptions: z
                        .array(
                          z.intersection(
                            z.object({
                              _id: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              title: z
                                .string()
                                .describe('Subscription title.')
                                .min(1)
                                .max(20)
                                .optional(),
                              description: z
                                .string()
                                .describe('Subscription description.')
                                .max(60)
                                .optional()
                                .nullable(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                                )
                                .optional()
                                .nullable(),
                              frequency: z
                                .enum([
                                  'UNDEFINED',
                                  'DAY',
                                  'WEEK',
                                  'MONTH',
                                  'YEAR',
                                ])
                                .describe(
                                  'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                                )
                                .optional(),
                              interval: z
                                .number()
                                .int()
                                .describe(
                                  'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              discount: z
                                .intersection(
                                  z.object({
                                    type: z
                                      .enum(['AMOUNT', 'PERCENT'])
                                      .describe('Discount type.')
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      amountOff: z.never().optional(),
                                      percentOff: z.never().optional(),
                                    }),
                                    z.object({
                                      percentOff: z.never().optional(),
                                      amountOff: z
                                        .string()
                                        .describe(
                                          'Amount to discount from the variant discounted_price.'
                                        ),
                                    }),
                                    z.object({
                                      amountOff: z.never().optional(),
                                      percentOff: z
                                        .number()
                                        .describe(
                                          'Percentage to discount from variant discounted_price.'
                                        )
                                        .min(0.1)
                                        .max(99.9),
                                    }),
                                  ])
                                )
                                .describe(
                                  'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                autoRenewal: z.never().optional(),
                                billingCycles: z.never().optional(),
                              }),
                              z.object({
                                billingCycles: z.never().optional(),
                                autoRenewal: z
                                  .boolean()
                                  .describe(
                                    'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                                  ),
                              }),
                              z.object({
                                autoRenewal: z.never().optional(),
                                billingCycles: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Number of billing cycles before subscription ends.'
                                  )
                                  .min(2)
                                  .max(999),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(6)
                        .optional(),
                      allowOneTimePurchases: z
                        .boolean()
                        .describe(
                          'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                    )
                    .optional(),
                  variantSummary: z
                    .object({
                      variantCount: z
                        .number()
                        .int()
                        .describe(
                          'The total number of variants for the product.'
                        )
                        .optional(),
                    })
                    .describe('The total number of variants for the product.')
                    .optional(),
                }),
                z.xor([
                  z.object({ physicalProperties: z.never().optional() }),
                  z.object({
                    physicalProperties: z
                      .object({
                        pricePerUnit: z
                          .object({
                            quantity: z
                              .number()
                              .describe(
                                'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                              )
                              .min(0.01)
                              .max(999999999.99)
                              .optional(),
                            measurementUnit: z
                              .enum([
                                'UNSPECIFIED',
                                'ML',
                                'CL',
                                'L',
                                'CBM',
                                'MG',
                                'G',
                                'KG',
                                'MM',
                                'CM',
                                'M',
                                'SQM',
                                'OZ',
                                'LB',
                                'FLOZ',
                                'PT',
                                'QT',
                                'GAL',
                                'IN',
                                'FT',
                                'YD',
                                'SQFT',
                              ])
                              .describe(
                                'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                              )
                              .optional(),
                          })
                          .describe(
                            'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                          )
                          .optional(),
                        fulfillerId: z
                          .string()
                          .describe('Fulfiller ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        shippingWeightRange: z
                          .object({
                            minValue: z
                              .number()
                              .describe(
                                'Minimum weight across all variants associated with this product.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional(),
                            maxValue: z
                              .number()
                              .describe(
                                'Maximum weight across all variants associated with this product.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional(),
                          })
                          .describe(
                            'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                          )
                          .optional(),
                        pricePerUnitRange: z
                          .object({
                            minValue: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Minimum price per unit across all variants.'
                              )
                              .optional(),
                            maxValue: z
                              .object({
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Maximum price per unit across all variants.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                          )
                          .optional(),
                        weightMeasurementUnitInfo: z
                          .object({
                            weightMeasurementUnit: z
                              .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                              .describe('Weight measurement unit.')
                              .optional(),
                          })
                          .describe(
                            'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        deliveryProfileId: z
                          .string()
                          .describe('Delivery profile ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                      ),
                  }),
                ])
              )
              .describe(
                'Full product entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(100)
        .optional(),
      bulkActionMetadata: z
        .object({
          totalSuccesses: z
            .number()
            .int()
            .describe('Number of items that were successfully processed.')
            .optional(),
          totalFailures: z
            .number()
            .int()
            .describe("Number of items that couldn't be processed.")
            .optional(),
          undetailedFailures: z
            .number()
            .int()
            .describe(
              'Number of failures without details because detailed failure threshold was exceeded.'
            )
            .optional(),
        })
        .describe('Bulk action metadata.')
        .optional(),
    })
    .describe('Products updated by bulk action.')
    .optional(),
  inventoryResults: z
    .object({
      results: z
        .array(
          z.object({
            itemMetadata: z
              .object({
                _id: z
                  .string()
                  .describe(
                    "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                originalIndex: z
                  .number()
                  .int()
                  .describe(
                    'Index of the item within the request array. Allows for correlation between request and response items.'
                  )
                  .optional(),
                success: z
                  .boolean()
                  .describe(
                    'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                  )
                  .optional(),
                error: z
                  .object({
                    code: z.string().describe('Error code.').optional(),
                    description: z
                      .string()
                      .describe('Description of the error.')
                      .optional(),
                    data: z
                      .record(z.string(), z.any())
                      .describe('Data related to the error.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Details about the error in case of failure.')
                  .optional(),
              })
              .describe('Bulk action metadata for inventory item.')
              .optional(),
            item: z
              .intersection(
                z.object({
                  _id: z
                    .string()
                    .describe('Inventory item ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  revision: z
                    .string()
                    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                    .describe(
                      'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                    )
                    .optional()
                    .nullable(),
                  _createdDate: z
                    .date()
                    .describe('Date and time the inventory item was created.')
                    .optional()
                    .nullable(),
                  _updatedDate: z
                    .date()
                    .describe(
                      'Date and time the inventory item was last updated.'
                    )
                    .optional()
                    .nullable(),
                  variantId: z
                    .string()
                    .describe('Variant ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  locationId: z
                    .string()
                    .describe(
                      "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                    )
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  productId: z
                    .string()
                    .describe('Product ID.')
                    .min(1)
                    .max(36)
                    .optional(),
                  trackQuantity: z
                    .boolean()
                    .describe('Whether the quantity is being tracked.')
                    .optional(),
                  availabilityStatus: z
                    .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                    .describe('Inventory item availability status.')
                    .optional(),
                  preorderInfo: z
                    .object({
                      enabled: z
                        .boolean()
                        .describe(
                          "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                        )
                        .optional()
                        .nullable(),
                      message: z
                        .string()
                        .describe(
                          'Message displayed to customers when the item is out of stock and preorder is enabled.'
                        )
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      limit: z
                        .number()
                        .int()
                        .describe(
                          'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                        )
                        .min(0)
                        .max(100000)
                        .optional()
                        .nullable(),
                      counter: z
                        .number()
                        .int()
                        .describe(
                          'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                        )
                        .min(0)
                        .max(99999)
                        .optional()
                        .nullable(),
                      quantity: z
                        .number()
                        .int()
                        .describe(
                          'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                    )
                    .optional(),
                  product: z
                    .object({
                      name: z
                        .string()
                        .describe('Product name.')
                        .max(80)
                        .optional()
                        .nullable(),
                      directCategoryIds: z
                        .array(z.string())
                        .max(2000)
                        .optional(),
                      variantName: z
                        .string()
                        .describe('Variant name.')
                        .min(1)
                        .max(250)
                        .optional()
                        .nullable(),
                      variantSku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      variantVisible: z
                        .boolean()
                        .describe(
                          'Whether the variant is visible in the store.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe('Associated product and variant details.')
                    .optional(),
                  extendedFields: z
                    .object({
                      namespaces: z
                        .record(z.string(), z.record(z.string(), z.any()))
                        .describe(
                          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z.never().optional(),
                  }),
                  z.object({
                    quantity: z.never().optional(),
                    inStock: z
                      .boolean()
                      .describe(
                        "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                      ),
                  }),
                  z.object({
                    inStock: z.never().optional(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                      )
                      .min(-99999)
                      .max(99999),
                  }),
                ])
              )
              .describe(
                'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
              )
              .optional(),
          })
        )
        .min(0)
        .max(1000)
        .optional(),
      bulkActionMetadata: z
        .object({
          totalSuccesses: z
            .number()
            .int()
            .describe('Number of items that were successfully processed.')
            .optional(),
          totalFailures: z
            .number()
            .int()
            .describe("Number of items that couldn't be processed.")
            .optional(),
          undetailedFailures: z
            .number()
            .int()
            .describe(
              'Number of failures without details because detailed failure threshold was exceeded.'
            )
            .optional(),
        })
        .describe('Bulk action metadata.')
        .optional(),
      error: z
        .object({
          code: z.string().describe('Error code.').optional(),
          description: z
            .string()
            .describe('Description of the error.')
            .optional(),
          data: z
            .record(z.string(), z.any())
            .describe('Data related to the error.')
            .optional()
            .nullable(),
        })
        .describe('Error details in case of failed action.')
        .optional(),
    })
    .describe('Inventories updated by bulk action.')
    .optional(),
});
export const BulkUpdateProductsByFilterRequest = z.object({
  options: z.object({
    filter: z
      .record(z.string(), z.any())
      .describe('Filter object.')
      .optional()
      .nullable(),
    product: z
      .intersection(
        z.object({
          _id: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the product was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the product was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Product name. Translatable.')
            .min(1)
            .max(80)
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe(
              'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
            )
            .optional()
            .nullable(),
          url: z
            .string()
            .describe(
              'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
            )
            .optional(),
          description: z
            .any()
            .describe(
              'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
          visible: z
            .boolean()
            .describe(
              "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
            )
            .optional()
            .nullable(),
          visibleInPos: z
            .boolean()
            .describe(
              'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
            )
            .optional()
            .nullable(),
          media: z
            .object({
              main: z
                .intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
                .describe(
                  'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                )
                .optional(),
              itemsInfo: z
                .object({
                  items: z
                    .array(
                      z.intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                    )
                    .max(50)
                    .optional(),
                })
                .describe(
                  'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
            })
            .describe(
              'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
            )
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Product SEO data.')
            .optional(),
          taxGroupId: z
            .string()
            .describe(
              '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          options: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Option name.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  optionRenderType: z
                    .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({ choicesSettings: z.never().optional() }),
                  z.object({
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Choice name. For text choices, this field also contains the choice value.'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                inStock: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                  )
                                  .optional(),
                                visible: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choices settings.'),
                  }),
                ])
              )
            )
            .max(6)
            .optional(),
          modifiers: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: MODIFIER`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Modifier title.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  modifierRenderType: z
                    .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .optional(),
                  mandatory: z
                    .boolean()
                    .describe(
                      'Whether customer input is required for this modifier.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z.never().optional(),
                  }),
                  z.object({
                    choicesSettings: z.never().optional(),
                    freeTextSettings: z
                      .object({
                        minCharCount: z
                          .number()
                          .int()
                          .describe('Minimum number of characters.')
                          .optional(),
                        maxCharCount: z
                          .number()
                          .int()
                          .describe('Maximum number of characters.')
                          .max(500)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount to be added to the product's price."
                          )
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Title of the text to be input by the customer.'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                      })
                      .describe('Free text modifier settings.'),
                  }),
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                addedPrice: z
                                  .string()
                                  .describe('Added price.')
                                  .optional()
                                  .nullable(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choice settings.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          brand: z
            .object({
              _id: z
                .string()
                .describe('Brand ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Brand name.')
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
            )
            .optional(),
          infoSections: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Info section ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                uniqueName: z
                  .string()
                  .describe(
                    'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                description: z
                  .any()
                  .describe(
                    'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                  )
                  .optional(),
                plainDescription: z
                  .string()
                  .describe(
                    'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                  )
                  .max(16000)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          ribbon: z
            .object({
              _id: z
                .string()
                .describe('Ribbon ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Ribbon name.')
                .max(30)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
            )
            .optional(),
          directCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          allCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          mainCategoryId: z
            .string()
            .describe(
              'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          costRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
            )
            .optional(),
          inventory: z
            .object({
              availabilityStatus: z
                .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
                .optional(),
              preorderStatus: z
                .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                .optional(),
              preorderAvailability: z
                .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                .optional(),
            })
            .describe(
              'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
            )
            .optional(),
          productType: z.enum(['PHYSICAL', 'DIGITAL']).optional(),
          handle: z
            .string()
            .describe(
              "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          currency: z
            .string()
            .describe(
              'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
            )
            .optional()
            .nullable(),
          breadcrumbsInfo: z
            .object({
              breadcrumbs: z
                .array(
                  z.object({
                    categoryId: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    categoryName: z
                      .string()
                      .describe('Category name.')
                      .min(1)
                      .max(80)
                      .optional(),
                    categorySlug: z
                      .string()
                      .describe('Category slug.')
                      .min(1)
                      .max(100)
                      .optional(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe(
              'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          actualPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum current selling prices across all product variants.'
            )
            .optional(),
          compareAtPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
            )
            .optional(),
          variantsInfo: z
            .object({
              variants: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      barcode: z
                        .string()
                        .describe('Variant barcode.')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      choices: z
                        .array(
                          z.object({
                            optionChoiceIds: z
                              .object({
                                optionId: z
                                  .string()
                                  .describe('Option ID.')
                                  .min(1)
                                  .max(36)
                                  .optional(),
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe(
                                "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                              )
                              .optional(),
                            optionChoiceNames: z
                              .object({
                                optionName: z
                                  .string()
                                  .describe('Option name.')
                                  .min(1)
                                  .optional(),
                                choiceName: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .optional(),
                                renderType: z
                                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                  .optional(),
                              })
                              .describe(
                                'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                          })
                        )
                        .min(0)
                        .max(6)
                        .optional(),
                      price: z
                        .object({
                          actualPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Variant's current selling price. Must be greater than or equal to 0."
                            )
                            .optional(),
                          compareAtPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                            )
                            .optional(),
                        })
                        .describe('Variant price.')
                        .optional(),
                      revenueDetails: z
                        .object({
                          cost: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Item cost.')
                            .optional(),
                          profit: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                            )
                            .optional(),
                          profitMargin: z
                            .number()
                            .describe(
                              'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                            )
                            .min(0)
                            .max(1)
                            .optional(),
                        })
                        .describe(
                          'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                        )
                        .optional(),
                      media: z
                        .intersection(
                          z.object({
                            altText: z
                              .string()
                              .describe('Image alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                            displayName: z
                              .string()
                              .describe(
                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                              )
                              .max(80)
                              .optional()
                              .nullable(),
                            mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .url()
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .optional(),
                                altText: z
                                  .string()
                                  .describe('Thumbnail alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                            uploadId: z
                              .string()
                              .describe(
                                'ID used to upload media to Wix Media Manager.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.intersection(
                            z.xor([
                              z.object({
                                _id: z.never().optional(),
                                url: z.never().optional(),
                              }),
                              z.object({
                                url: z.never().optional(),
                                _id: z
                                  .string()
                                  .describe(
                                    'Set media by ID of an existing file in Wix Media Manager.'
                                  )
                                  .min(1)
                                  .max(200),
                              }),
                              z.object({
                                _id: z.never().optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'Set media using an external media URL.'
                                  )
                                  .url(),
                              }),
                            ]),
                            z.xor([
                              z.object({
                                image: z.never().optional(),
                                video: z.never().optional(),
                              }),
                              z.object({
                                video: z.never().optional(),
                                image: z
                                  .string()
                                  .describe(
                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                  ),
                              }),
                              z.object({
                                image: z.never().optional(),
                                video: z
                                  .string()
                                  .describe(
                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                  ),
                              }),
                            ])
                          )
                        )
                        .describe('Variant media.')
                        .optional(),
                      subscriptionPricesInfo: z
                        .object({
                          subscriptionPrices: z
                            .array(
                              z.object({
                                subscriptionId: z
                                  .string()
                                  .describe('Subscription ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                price: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe(
                                        'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                      )
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                  )
                                  .optional(),
                                pricePerUnit: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Price per unit info.')
                                  .optional(),
                              })
                            )
                            .max(6)
                            .optional(),
                        })
                        .describe(
                          'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      inventoryStatus: z
                        .object({
                          inStock: z
                            .boolean()
                            .describe('Whether the variant is in stock.')
                            .optional(),
                          preorderEnabled: z
                            .boolean()
                            .describe(
                              'Whether preorder is enabled for this variant.'
                            )
                            .optional(),
                        })
                        .describe('Variant inventory status.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z.never().optional(),
                      }),
                      z.object({
                        digitalProperties: z.never().optional(),
                        physicalProperties: z
                          .object({
                            weight: z
                              .number()
                              .describe(
                                'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional()
                              .nullable(),
                            pricePerUnit: z
                              .object({
                                settings: z
                                  .object({
                                    quantity: z
                                      .number()
                                      .describe(
                                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                      )
                                      .min(0.01)
                                      .max(999999999.99)
                                      .optional(),
                                    measurementUnit: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'ML',
                                        'CL',
                                        'L',
                                        'CBM',
                                        'MG',
                                        'G',
                                        'KG',
                                        'MM',
                                        'CM',
                                        'M',
                                        'SQM',
                                        'OZ',
                                        'LB',
                                        'FLOZ',
                                        'PT',
                                        'QT',
                                        'GAL',
                                        'IN',
                                        'FT',
                                        'YD',
                                        'SQFT',
                                      ])
                                      .describe(
                                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                  )
                                  .optional(),
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                              )
                              .optional(),
                            productDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Product dimensions (length, width, height of the physical product).'
                              )
                              .optional(),
                            packageDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Package dimensions (length, width, height of the shipping package).'
                              )
                              .optional(),
                          })
                          .describe(
                            'Physical properties. Must be passed when `productType: PHYSICAL`'
                          ),
                      }),
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z
                          .object({
                            digitalFile: z
                              .object({
                                _id: z
                                  .string()
                                  .describe('Media ID in media manager.')
                                  .min(1)
                                  .max(100)
                                  .optional(),
                                fileName: z
                                  .string()
                                  .describe('Original file name.')
                                  .min(1)
                                  .max(1000)
                                  .optional(),
                                fileSize: z
                                  .string()
                                  .describe('Original file size.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                fileType: z
                                  .enum([
                                    'UNSPECIFIED',
                                    'SECURE_PICTURE',
                                    'SECURE_VIDEO',
                                    'SECURE_DOCUMENT',
                                    'SECURE_MUSIC',
                                    'SECURE_ARCHIVE',
                                    'SECURE_RAW',
                                  ])
                                  .describe('File type.')
                                  .optional(),
                              })
                              .describe(
                                'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Digital properties. Must be passed when `productType: DIGITAL`'
                          ),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(1000)
                .optional(),
            })
            .describe(
              'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
            )
            .optional(),
          purchaseEligibility: z
            .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
            .describe('Purchase eligibility settings for the product.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              publicTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe('Tags')
            .optional(),
          subscriptionDetails: z
            .object({
              subscriptions: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe('Subscription title.')
                        .min(1)
                        .max(20),
                      description: z
                        .string()
                        .describe('Subscription description.')
                        .max(60)
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                      frequency: z.enum([
                        'UNDEFINED',
                        'DAY',
                        'WEEK',
                        'MONTH',
                        'YEAR',
                      ]),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      discount: z
                        .intersection(
                          z.object({ type: z.enum(['AMOUNT', 'PERCENT']) }),
                          z.xor([
                            z.object({
                              percentOff: z.never().optional(),
                              amountOff: z
                                .string()
                                .describe(
                                  'Amount to discount from the variant discounted_price.'
                                ),
                            }),
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z
                                .number()
                                .describe(
                                  'Percentage to discount from variant discounted_price.'
                                )
                                .min(0.1)
                                .max(99.9),
                            }),
                          ])
                        )
                        .describe(
                          'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        billingCycles: z.never().optional(),
                        autoRenewal: z
                          .boolean()
                          .describe(
                            'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                          ),
                      }),
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z
                          .number()
                          .int()
                          .describe(
                            'Number of billing cycles before subscription ends.'
                          )
                          .min(2)
                          .max(999),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(6)
                .optional(),
              allowOneTimePurchases: z
                .boolean()
                .describe(
                  'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
            )
            .optional(),
          variantSummary: z
            .object({
              variantCount: z
                .number()
                .int()
                .describe('The total number of variants for the product.')
                .optional(),
            })
            .describe('The total number of variants for the product.')
            .optional(),
        }),
        z.xor([
          z.object({ physicalProperties: z.never().optional() }),
          z.object({
            physicalProperties: z
              .object({
                pricePerUnit: z
                  .object({
                    quantity: z
                      .number()
                      .describe(
                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                      )
                      .min(0.01)
                      .max(999999999.99)
                      .optional(),
                    measurementUnit: z
                      .enum([
                        'UNSPECIFIED',
                        'ML',
                        'CL',
                        'L',
                        'CBM',
                        'MG',
                        'G',
                        'KG',
                        'MM',
                        'CM',
                        'M',
                        'SQM',
                        'OZ',
                        'LB',
                        'FLOZ',
                        'PT',
                        'QT',
                        'GAL',
                        'IN',
                        'FT',
                        'YD',
                        'SQFT',
                      ])
                      .describe(
                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                      )
                      .optional(),
                  })
                  .describe(
                    'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                  )
                  .optional(),
                fulfillerId: z
                  .string()
                  .describe('Fulfiller ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shippingWeightRange: z
                  .object({
                    minValue: z
                      .number()
                      .describe(
                        'Minimum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                    maxValue: z
                      .number()
                      .describe(
                        'Maximum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                  })
                  .describe(
                    'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                  )
                  .optional(),
                pricePerUnitRange: z
                  .object({
                    minValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Minimum price per unit across all variants.')
                      .optional(),
                    maxValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum price per unit across all variants.')
                      .optional(),
                  })
                  .describe(
                    'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                  )
                  .optional(),
                weightMeasurementUnitInfo: z
                  .object({
                    weightMeasurementUnit: z
                      .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                      .describe('Weight measurement unit.')
                      .optional(),
                  })
                  .describe(
                    'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                deliveryProfileId: z
                  .string()
                  .describe('Delivery profile ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
              ),
          }),
        ])
      )
      .describe('Product to update.'),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkUpdateProductsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const DeleteProductRequest = z.object({
  productId: z.string().describe('Product ID.').min(1).max(36),
});
export const DeleteProductResponse = z.object({});
export const BulkDeleteProductsRequest = z.object({
  productIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteProductsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for products.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkDeleteProductsByFilterRequest = z.object({
  filter: z.record(z.string(), z.any()).describe('Filter object.'),
  options: z
    .object({
      search: z
        .object({
          mode: z.enum(['OR', 'AND']).optional(),
          expression: z
            .string()
            .describe('Search term or expression')
            .max(100)
            .optional()
            .nullable(),
          fields: z.array(z.string()).max(20).optional(),
          fuzzy: z
            .boolean()
            .describe(
              'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
            )
            .optional(),
        })
        .describe('Free text to match in searchable fields.')
        .optional(),
    })
    .optional(),
});
export const BulkDeleteProductsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const GetProductRequest = z.object({
  productId: z.string().describe('Product ID.').min(1).max(36),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const GetProductResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Product ID.')
      .min(1)
      .max(36)
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the product was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the product was updated.')
      .optional()
      .nullable(),
    name: z
      .string()
      .describe('Product name. Translatable.')
      .min(1)
      .max(80)
      .optional()
      .nullable(),
    slug: z
      .string()
      .describe(
        'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
      )
      .optional()
      .nullable(),
    url: z
      .string()
      .describe(
        'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
      )
      .optional(),
    description: z
      .any()
      .describe(
        'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
      )
      .optional(),
    plainDescription: z
      .string()
      .describe(
        'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
      )
      .max(16000)
      .optional()
      .nullable(),
    visible: z
      .boolean()
      .describe(
        "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
      )
      .optional()
      .nullable(),
    visibleInPos: z
      .boolean()
      .describe(
        'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
      )
      .optional()
      .nullable(),
    media: z
      .object({
        main: z
          .intersection(
            z.object({
              altText: z
                .string()
                .describe('Image alt text.')
                .min(1)
                .max(1000)
                .optional()
                .nullable(),
              displayName: z
                .string()
                .describe(
                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                )
                .max(80)
                .optional()
                .nullable(),
              mediaType: z
                .enum(['IMAGE', 'VIDEO'])
                .describe('Media type.')
                .optional(),
              thumbnail: z
                .object({
                  url: z.string().describe('Thumbnail url.').url().optional(),
                  height: z
                    .number()
                    .int()
                    .describe('Thumbnail height.')
                    .optional(),
                  width: z
                    .number()
                    .int()
                    .describe('Thumbnail width.')
                    .optional(),
                  altText: z
                    .string()
                    .describe('Thumbnail alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              uploadId: z
                .string()
                .describe('ID used to upload media to Wix Media Manager.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            }),
            z.intersection(
              z.xor([
                z.object({
                  _id: z.never().optional(),
                  url: z.never().optional(),
                }),
                z.object({
                  url: z.never().optional(),
                  _id: z
                    .string()
                    .describe(
                      'Set media by ID of an existing file in Wix Media Manager.'
                    )
                    .min(1)
                    .max(200),
                }),
                z.object({
                  _id: z.never().optional(),
                  url: z
                    .string()
                    .describe('Set media using an external media URL.')
                    .url(),
                }),
              ]),
              z.xor([
                z.object({
                  image: z.never().optional(),
                  video: z.never().optional(),
                }),
                z.object({
                  video: z.never().optional(),
                  image: z
                    .string()
                    .describe(
                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                    ),
                }),
                z.object({
                  image: z.never().optional(),
                  video: z
                    .string()
                    .describe(
                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                    ),
                }),
              ])
            )
          )
          .describe(
            'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
          )
          .optional(),
        itemsInfo: z
          .object({
            items: z
              .array(
                z.intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z
                      .enum(['IMAGE', 'VIDEO'])
                      .describe('Media type.')
                      .optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
              )
              .max(50)
              .optional(),
          })
          .describe(
            'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
      })
      .describe(
        'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
      )
      .optional(),
    seoData: z
      .object({
        tags: z
          .array(
            z.object({
              type: z
                .string()
                .describe(
                  'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                )
                .optional(),
              props: z
                .record(z.string(), z.any())
                .describe(
                  'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                )
                .optional()
                .nullable(),
              meta: z
                .record(z.string(), z.any())
                .describe(
                  'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                )
                .optional()
                .nullable(),
              children: z
                .string()
                .describe(
                  'SEO tag inner content. For example, `<title> inner content </title>`.'
                )
                .optional(),
              custom: z
                .boolean()
                .describe(
                  'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                )
                .optional(),
              disabled: z
                .boolean()
                .describe(
                  "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                )
                .optional(),
            })
          )
          .optional(),
        settings: z
          .object({
            preventAutoRedirect: z
              .boolean()
              .describe(
                'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
              )
              .optional(),
            keywords: z
              .array(
                z.object({
                  term: z.string().describe('Keyword value.').optional(),
                  isMain: z
                    .boolean()
                    .describe('Whether the keyword is the main focus keyword.')
                    .optional(),
                  origin: z
                    .string()
                    .describe(
                      'The source that added the keyword terms to the SEO settings.'
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe('SEO general settings.')
          .optional(),
      })
      .describe('Product SEO data.')
      .optional(),
    taxGroupId: z
      .string()
      .describe(
        '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    options: z
      .array(
        z.intersection(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of a customization with `customizationType: PRODUCT_OPTION`.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Option name.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            optionRenderType: z
              .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
              .describe('Option render type.')
              .optional(),
            key: z
              .string()
              .describe(
                'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
              )
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({ choicesSettings: z.never().optional() }),
            z.object({
              choicesSettings: z
                .object({
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          linkedMedia: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(15)
                            .optional(),
                          choiceType: z
                            .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                            .describe('Choice type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          name: z
                            .string()
                            .describe(
                              'Choice name. For text choices, this field also contains the choice value.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          inStock: z
                            .boolean()
                            .describe(
                              'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                            )
                            .optional(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({ colorCode: z.never().optional() }),
                          z.object({
                            colorCode: z
                              .string()
                              .describe(
                                'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                              )
                              .min(3)
                              .max(20),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                })
                .describe('Choices settings.'),
            }),
          ])
        )
      )
      .max(6)
      .optional(),
    modifiers: z
      .array(
        z.intersection(
          z.object({
            _id: z
              .string()
              .describe(
                'ID of a customization with `customizationType: MODIFIER`.'
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Modifier title.')
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            modifierRenderType: z
              .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
              .describe('Modifier render type.')
              .optional(),
            mandatory: z
              .boolean()
              .describe('Whether customer input is required for this modifier.')
              .optional(),
            key: z
              .string()
              .describe(
                'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
              )
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({
              freeTextSettings: z.never().optional(),
              choicesSettings: z.never().optional(),
            }),
            z.object({
              choicesSettings: z.never().optional(),
              freeTextSettings: z
                .object({
                  minCharCount: z
                    .number()
                    .int()
                    .describe('Minimum number of characters.')
                    .optional(),
                  maxCharCount: z
                    .number()
                    .int()
                    .describe('Maximum number of characters.')
                    .max(500)
                    .optional(),
                  defaultAddedPrice: z
                    .string()
                    .describe(
                      "Default amount to be added to the product's price."
                    )
                    .optional()
                    .nullable(),
                  title: z
                    .string()
                    .describe('Title of the text to be input by the customer.')
                    .min(1)
                    .max(150)
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(150)
                    .optional(),
                })
                .describe('Free text modifier settings.'),
            }),
            z.object({
              freeTextSettings: z.never().optional(),
              choicesSettings: z
                .object({
                  choices: z
                    .array(
                      z.intersection(
                        z.object({
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          linkedMedia: z
                            .array(
                              z.intersection(
                                z.object({
                                  altText: z
                                    .string()
                                    .describe('Image alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                  displayName: z
                                    .string()
                                    .describe(
                                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                    )
                                    .max(80)
                                    .optional()
                                    .nullable(),
                                  mediaType: z
                                    .enum(['IMAGE', 'VIDEO'])
                                    .describe('Media type.')
                                    .optional(),
                                  thumbnail: z
                                    .object({
                                      url: z
                                        .string()
                                        .describe('Thumbnail url.')
                                        .url()
                                        .optional(),
                                      height: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail height.')
                                        .optional(),
                                      width: z
                                        .number()
                                        .int()
                                        .describe('Thumbnail width.')
                                        .optional(),
                                      altText: z
                                        .string()
                                        .describe('Thumbnail alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                    })
                                    .describe(
                                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                    )
                                    .optional(),
                                  uploadId: z
                                    .string()
                                    .describe(
                                      'ID used to upload media to Wix Media Manager.'
                                    )
                                    .regex(
                                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                      'Must be a valid GUID'
                                    )
                                    .optional(),
                                }),
                                z.intersection(
                                  z.xor([
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z.never().optional(),
                                    }),
                                    z.object({
                                      url: z.never().optional(),
                                      _id: z
                                        .string()
                                        .describe(
                                          'Set media by ID of an existing file in Wix Media Manager.'
                                        )
                                        .min(1)
                                        .max(200),
                                    }),
                                    z.object({
                                      _id: z.never().optional(),
                                      url: z
                                        .string()
                                        .describe(
                                          'Set media using an external media URL.'
                                        )
                                        .url(),
                                    }),
                                  ]),
                                  z.xor([
                                    z.object({
                                      image: z.never().optional(),
                                      video: z.never().optional(),
                                    }),
                                    z.object({
                                      video: z.never().optional(),
                                      image: z
                                        .string()
                                        .describe(
                                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                        ),
                                    }),
                                    z.object({
                                      image: z.never().optional(),
                                      video: z
                                        .string()
                                        .describe(
                                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                        ),
                                    }),
                                  ])
                                )
                              )
                            )
                            .max(15)
                            .optional(),
                          choiceType: z
                            .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                            .describe('Choice type.')
                            .optional(),
                          key: z
                            .string()
                            .describe(
                              'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                            )
                            .min(1)
                            .max(50)
                            .optional(),
                          name: z
                            .string()
                            .describe('Choice name.')
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          addedPrice: z
                            .string()
                            .describe('Added price.')
                            .optional()
                            .nullable(),
                        }),
                        z.xor([
                          z.object({ colorCode: z.never().optional() }),
                          z.object({
                            colorCode: z
                              .string()
                              .describe(
                                'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                              )
                              .min(3)
                              .max(20),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(100)
                    .optional(),
                })
                .describe('Choice settings.'),
            }),
          ])
        )
      )
      .max(10)
      .optional(),
    brand: z
      .object({
        _id: z
          .string()
          .describe('Brand ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Brand name.').max(50).optional().nullable(),
      })
      .describe(
        "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
      )
      .optional(),
    infoSections: z
      .array(
        z.object({
          _id: z
            .string()
            .describe('Info section ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          uniqueName: z
            .string()
            .describe(
              'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
            )
            .max(100)
            .optional()
            .nullable(),
          title: z
            .string()
            .describe(
              'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
            )
            .min(1)
            .max(50)
            .optional()
            .nullable(),
          description: z
            .any()
            .describe(
              'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
      )
      .max(10)
      .optional(),
    ribbon: z
      .object({
        _id: z
          .string()
          .describe('Ribbon ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        name: z.string().describe('Ribbon name.').max(30).optional().nullable(),
      })
      .describe(
        "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
      )
      .optional(),
    directCategoriesInfo: z
      .object({
        categories: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                )
                .min(0)
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(2000)
          .optional(),
      })
      .describe(
        'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    allCategoriesInfo: z
      .object({
        categories: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              index: z
                .number()
                .int()
                .describe(
                  'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                )
                .min(0)
                .max(200)
                .optional()
                .nullable(),
            })
          )
          .max(2000)
          .optional(),
      })
      .describe(
        'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    mainCategoryId: z
      .string()
      .describe(
        'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    costRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
      )
      .optional(),
    inventory: z
      .object({
        availabilityStatus: z
          .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
          .describe('Current availability status.')
          .optional(),
        preorderStatus: z
          .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
          .describe('Current preorder status.')
          .optional(),
        preorderAvailability: z
          .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
          .describe('Preorder availability status.')
          .optional(),
      })
      .describe(
        'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
      )
      .optional(),
    productType: z
      .enum(['PHYSICAL', 'DIGITAL'])
      .describe(
        'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
      )
      .optional(),
    handle: z
      .string()
      .describe(
        "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
      )
      .min(1)
      .max(100)
      .optional()
      .nullable(),
    currency: z
      .string()
      .describe(
        'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
      )
      .optional()
      .nullable(),
    breadcrumbsInfo: z
      .object({
        breadcrumbs: z
          .array(
            z.object({
              categoryId: z
                .string()
                .describe('Category ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              categoryName: z
                .string()
                .describe('Category name.')
                .min(1)
                .max(80)
                .optional(),
              categorySlug: z
                .string()
                .describe('Category slug.')
                .min(1)
                .max(100)
                .optional(),
            })
          )
          .max(5)
          .optional(),
      })
      .describe(
        'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
      )
      .optional(),
    actualPriceRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Minimum and maximum current selling prices across all product variants.'
      )
      .optional(),
    compareAtPriceRange: z
      .object({
        minValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Minimum value.')
          .optional(),
        maxValue: z
          .object({
            amount: z
              .string()
              .describe(
                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
              )
              .optional(),
            formattedAmount: z
              .string()
              .describe(
                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
              )
              .max(20)
              .optional()
              .nullable(),
          })
          .describe('Maximum value.')
          .optional(),
      })
      .describe(
        'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
      )
      .optional(),
    variantsInfo: z
      .object({
        variants: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                visible: z
                  .boolean()
                  .describe(
                    "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                  )
                  .optional()
                  .nullable(),
                sku: z
                  .string()
                  .describe('Variant SKU (stock keeping unit).')
                  .min(1)
                  .max(40)
                  .optional()
                  .nullable(),
                barcode: z
                  .string()
                  .describe('Variant barcode.')
                  .min(1)
                  .max(40)
                  .optional()
                  .nullable(),
                choices: z
                  .array(
                    z.object({
                      optionChoiceIds: z
                        .object({
                          optionId: z
                            .string()
                            .describe('Option ID.')
                            .min(1)
                            .max(36)
                            .optional(),
                          choiceId: z
                            .string()
                            .describe('Choice ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        })
                        .describe(
                          "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                        )
                        .optional(),
                      optionChoiceNames: z
                        .object({
                          optionName: z
                            .string()
                            .describe('Option name.')
                            .min(1)
                            .optional(),
                          choiceName: z
                            .string()
                            .describe('Choice name.')
                            .min(1)
                            .optional(),
                          renderType: z
                            .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                            .describe(
                              'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                    })
                  )
                  .min(0)
                  .max(6)
                  .optional(),
                price: z
                  .object({
                    actualPrice: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        "Variant's current selling price. Must be greater than or equal to 0."
                      )
                      .optional(),
                    compareAtPrice: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                      )
                      .optional(),
                  })
                  .describe('Variant price.')
                  .optional(),
                revenueDetails: z
                  .object({
                    cost: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe('Item cost.')
                      .optional(),
                    profit: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                      )
                      .optional(),
                    profitMargin: z
                      .number()
                      .describe(
                        'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                      )
                      .min(0)
                      .max(1)
                      .optional(),
                  })
                  .describe(
                    'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                  )
                  .optional(),
                media: z
                  .intersection(
                    z.object({
                      altText: z
                        .string()
                        .describe('Image alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                      displayName: z
                        .string()
                        .describe(
                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                        )
                        .max(80)
                        .optional()
                        .nullable(),
                      mediaType: z
                        .enum(['IMAGE', 'VIDEO'])
                        .describe('Media type.')
                        .optional(),
                      thumbnail: z
                        .object({
                          url: z
                            .string()
                            .describe('Thumbnail url.')
                            .url()
                            .optional(),
                          height: z
                            .number()
                            .int()
                            .describe('Thumbnail height.')
                            .optional(),
                          width: z
                            .number()
                            .int()
                            .describe('Thumbnail width.')
                            .optional(),
                          altText: z
                            .string()
                            .describe('Thumbnail alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      uploadId: z
                        .string()
                        .describe(
                          'ID used to upload media to Wix Media Manager.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional(),
                    }),
                    z.intersection(
                      z.xor([
                        z.object({
                          _id: z.never().optional(),
                          url: z.never().optional(),
                        }),
                        z.object({
                          url: z.never().optional(),
                          _id: z
                            .string()
                            .describe(
                              'Set media by ID of an existing file in Wix Media Manager.'
                            )
                            .min(1)
                            .max(200),
                        }),
                        z.object({
                          _id: z.never().optional(),
                          url: z
                            .string()
                            .describe('Set media using an external media URL.')
                            .url(),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          image: z.never().optional(),
                          video: z.never().optional(),
                        }),
                        z.object({
                          video: z.never().optional(),
                          image: z
                            .string()
                            .describe(
                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                            ),
                        }),
                        z.object({
                          image: z.never().optional(),
                          video: z
                            .string()
                            .describe(
                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                            ),
                        }),
                      ])
                    )
                  )
                  .describe('Variant media.')
                  .optional(),
                subscriptionPricesInfo: z
                  .object({
                    subscriptionPrices: z
                      .array(
                        z.object({
                          subscriptionId: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                          price: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                            )
                            .optional(),
                          pricePerUnit: z
                            .object({
                              value: z
                                .string()
                                .describe(
                                  'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Price per unit info.')
                            .optional(),
                        })
                      )
                      .max(6)
                      .optional(),
                  })
                  .describe(
                    'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                inventoryStatus: z
                  .object({
                    inStock: z
                      .boolean()
                      .describe('Whether the variant is in stock.')
                      .optional(),
                    preorderEnabled: z
                      .boolean()
                      .describe('Whether preorder is enabled for this variant.')
                      .optional(),
                  })
                  .describe('Variant inventory status.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  physicalProperties: z.never().optional(),
                  digitalProperties: z.never().optional(),
                }),
                z.object({
                  digitalProperties: z.never().optional(),
                  physicalProperties: z
                    .object({
                      weight: z
                        .number()
                        .describe(
                          'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                        )
                        .min(0)
                        .max(999999999.99)
                        .optional()
                        .nullable(),
                      pricePerUnit: z
                        .object({
                          settings: z
                            .object({
                              quantity: z
                                .number()
                                .describe(
                                  'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                )
                                .min(0.01)
                                .max(999999999.99)
                                .optional(),
                              measurementUnit: z
                                .enum([
                                  'UNSPECIFIED',
                                  'ML',
                                  'CL',
                                  'L',
                                  'CBM',
                                  'MG',
                                  'G',
                                  'KG',
                                  'MM',
                                  'CM',
                                  'M',
                                  'SQM',
                                  'OZ',
                                  'LB',
                                  'FLOZ',
                                  'PT',
                                  'QT',
                                  'GAL',
                                  'IN',
                                  'FT',
                                  'YD',
                                  'SQFT',
                                ])
                                .describe(
                                  'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                )
                                .optional(),
                            })
                            .describe(
                              'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                            )
                            .optional(),
                          value: z
                            .string()
                            .describe(
                              "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                            )
                            .optional(),
                          description: z
                            .string()
                            .describe(
                              'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                            )
                            .max(100)
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                        )
                        .optional(),
                      productDimensions: z
                        .object({
                          length: z
                            .string()
                            .describe(
                              'Length. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          width: z
                            .string()
                            .describe(
                              'Width. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          height: z
                            .string()
                            .describe(
                              'Height. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          unit: z
                            .enum([
                              'UNKNOWN_DIMENSIONS_UNIT',
                              'MM',
                              'CM',
                              'M',
                              'IN',
                              'FT',
                              'YD',
                            ])
                            .describe(
                              'Unit of measurement for dimensions (length, width, height).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Product dimensions (length, width, height of the physical product).'
                        )
                        .optional(),
                      packageDimensions: z
                        .object({
                          length: z
                            .string()
                            .describe(
                              'Length. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          width: z
                            .string()
                            .describe(
                              'Width. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          height: z
                            .string()
                            .describe(
                              'Height. Measurement unit is handled at the system level (metric/imperial).'
                            )
                            .optional()
                            .nullable(),
                          unit: z
                            .enum([
                              'UNKNOWN_DIMENSIONS_UNIT',
                              'MM',
                              'CM',
                              'M',
                              'IN',
                              'FT',
                              'YD',
                            ])
                            .describe(
                              'Unit of measurement for dimensions (length, width, height).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Package dimensions (length, width, height of the shipping package).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Physical properties. Must be passed when `productType: PHYSICAL`'
                    ),
                }),
                z.object({
                  physicalProperties: z.never().optional(),
                  digitalProperties: z
                    .object({
                      digitalFile: z
                        .object({
                          _id: z
                            .string()
                            .describe('Media ID in media manager.')
                            .min(1)
                            .max(100)
                            .optional(),
                          fileName: z
                            .string()
                            .describe('Original file name.')
                            .min(1)
                            .max(1000)
                            .optional(),
                          fileSize: z
                            .string()
                            .describe('Original file size.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          fileType: z
                            .enum([
                              'UNSPECIFIED',
                              'SECURE_PICTURE',
                              'SECURE_VIDEO',
                              'SECURE_DOCUMENT',
                              'SECURE_MUSIC',
                              'SECURE_ARCHIVE',
                              'SECURE_RAW',
                            ])
                            .describe('File type.')
                            .optional(),
                        })
                        .describe(
                          'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Digital properties. Must be passed when `productType: DIGITAL`'
                    ),
                }),
              ])
            )
          )
          .min(1)
          .max(1000)
          .optional(),
      })
      .describe(
        'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
      )
      .optional(),
    purchaseEligibility: z
      .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
      .describe('Purchase eligibility settings for the product.')
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        publicTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags')
      .optional(),
    subscriptionDetails: z
      .object({
        subscriptions: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Subscription ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe('Subscription title.')
                  .min(1)
                  .max(20)
                  .optional(),
                description: z
                  .string()
                  .describe('Subscription description.')
                  .max(60)
                  .optional()
                  .nullable(),
                visible: z
                  .boolean()
                  .describe(
                    'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                  )
                  .optional()
                  .nullable(),
                frequency: z
                  .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                  .describe(
                    'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                  )
                  .optional(),
                interval: z
                  .number()
                  .int()
                  .describe(
                    'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                discount: z
                  .intersection(
                    z.object({
                      type: z
                        .enum(['AMOUNT', 'PERCENT'])
                        .describe('Discount type.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        amountOff: z.never().optional(),
                        percentOff: z.never().optional(),
                      }),
                      z.object({
                        percentOff: z.never().optional(),
                        amountOff: z
                          .string()
                          .describe(
                            'Amount to discount from the variant discounted_price.'
                          ),
                      }),
                      z.object({
                        amountOff: z.never().optional(),
                        percentOff: z
                          .number()
                          .describe(
                            'Percentage to discount from variant discounted_price.'
                          )
                          .min(0.1)
                          .max(99.9),
                      }),
                    ])
                  )
                  .describe(
                    'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  autoRenewal: z.never().optional(),
                  billingCycles: z.never().optional(),
                }),
                z.object({
                  billingCycles: z.never().optional(),
                  autoRenewal: z
                    .boolean()
                    .describe(
                      'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                    ),
                }),
                z.object({
                  autoRenewal: z.never().optional(),
                  billingCycles: z
                    .number()
                    .int()
                    .describe(
                      'Number of billing cycles before subscription ends.'
                    )
                    .min(2)
                    .max(999),
                }),
              ])
            )
          )
          .min(1)
          .max(6)
          .optional(),
        allowOneTimePurchases: z
          .boolean()
          .describe(
            'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
          )
          .optional()
          .nullable(),
      })
      .describe(
        "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
      )
      .optional(),
    variantSummary: z
      .object({
        variantCount: z
          .number()
          .int()
          .describe('The total number of variants for the product.')
          .optional(),
      })
      .describe('The total number of variants for the product.')
      .optional(),
  }),
  z.xor([
    z.object({ physicalProperties: z.never().optional() }),
    z.object({
      physicalProperties: z
        .object({
          pricePerUnit: z
            .object({
              quantity: z
                .number()
                .describe(
                  'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                )
                .min(0.01)
                .max(999999999.99)
                .optional(),
              measurementUnit: z
                .enum([
                  'UNSPECIFIED',
                  'ML',
                  'CL',
                  'L',
                  'CBM',
                  'MG',
                  'G',
                  'KG',
                  'MM',
                  'CM',
                  'M',
                  'SQM',
                  'OZ',
                  'LB',
                  'FLOZ',
                  'PT',
                  'QT',
                  'GAL',
                  'IN',
                  'FT',
                  'YD',
                  'SQFT',
                ])
                .describe(
                  'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                )
                .optional(),
            })
            .describe(
              'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
            )
            .optional(),
          fulfillerId: z
            .string()
            .describe('Fulfiller ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          shippingWeightRange: z
            .object({
              minValue: z
                .number()
                .describe(
                  'Minimum weight across all variants associated with this product.'
                )
                .min(0)
                .max(999999999.99)
                .optional(),
              maxValue: z
                .number()
                .describe(
                  'Maximum weight across all variants associated with this product.'
                )
                .min(0)
                .max(999999999.99)
                .optional(),
            })
            .describe(
              'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
            )
            .optional(),
          pricePerUnitRange: z
            .object({
              minValue: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                    )
                    .optional(),
                  description: z
                    .string()
                    .describe(
                      'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum price per unit across all variants.')
                .optional(),
              maxValue: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                    )
                    .optional(),
                  description: z
                    .string()
                    .describe(
                      'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                    )
                    .max(100)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum price per unit across all variants.')
                .optional(),
            })
            .describe(
              'Product price per unit range. The minimum and maximum price per unit of all the variants.'
            )
            .optional(),
          weightMeasurementUnitInfo: z
            .object({
              weightMeasurementUnit: z
                .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                .describe('Weight measurement unit.')
                .optional(),
            })
            .describe(
              'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          deliveryProfileId: z
            .string()
            .describe('Delivery profile ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
        ),
    }),
  ])
);
export const GetProductBySlugRequest = z.object({
  slug: z.string().describe('Product slug.').min(1).max(100),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const GetProductBySlugResponse = z.object({
  product: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Product ID.')
          .min(1)
          .max(36)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the product was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the product was updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Product name. Translatable.')
          .min(1)
          .max(80)
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
          )
          .optional()
          .nullable(),
        url: z
          .string()
          .describe(
            'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
          )
          .optional(),
        description: z
          .any()
          .describe(
            'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
          )
          .optional(),
        plainDescription: z
          .string()
          .describe(
            'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
          )
          .max(16000)
          .optional()
          .nullable(),
        visible: z
          .boolean()
          .describe(
            "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
          )
          .optional()
          .nullable(),
        visibleInPos: z
          .boolean()
          .describe(
            'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
          )
          .optional()
          .nullable(),
        media: z
          .object({
            main: z
              .intersection(
                z.object({
                  altText: z
                    .string()
                    .describe('Image alt text.')
                    .min(1)
                    .max(1000)
                    .optional()
                    .nullable(),
                  displayName: z
                    .string()
                    .describe(
                      "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                    )
                    .max(80)
                    .optional()
                    .nullable(),
                  mediaType: z
                    .enum(['IMAGE', 'VIDEO'])
                    .describe('Media type.')
                    .optional(),
                  thumbnail: z
                    .object({
                      url: z
                        .string()
                        .describe('Thumbnail url.')
                        .url()
                        .optional(),
                      height: z
                        .number()
                        .int()
                        .describe('Thumbnail height.')
                        .optional(),
                      width: z
                        .number()
                        .int()
                        .describe('Thumbnail width.')
                        .optional(),
                      altText: z
                        .string()
                        .describe('Thumbnail alt text.')
                        .min(1)
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                  uploadId: z
                    .string()
                    .describe('ID used to upload media to Wix Media Manager.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                }),
                z.intersection(
                  z.xor([
                    z.object({
                      _id: z.never().optional(),
                      url: z.never().optional(),
                    }),
                    z.object({
                      url: z.never().optional(),
                      _id: z
                        .string()
                        .describe(
                          'Set media by ID of an existing file in Wix Media Manager.'
                        )
                        .min(1)
                        .max(200),
                    }),
                    z.object({
                      _id: z.never().optional(),
                      url: z
                        .string()
                        .describe('Set media using an external media URL.')
                        .url(),
                    }),
                  ]),
                  z.xor([
                    z.object({
                      image: z.never().optional(),
                      video: z.never().optional(),
                    }),
                    z.object({
                      video: z.never().optional(),
                      image: z
                        .string()
                        .describe(
                          "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                        ),
                    }),
                    z.object({
                      image: z.never().optional(),
                      video: z
                        .string()
                        .describe(
                          "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                        ),
                    }),
                  ])
                )
              )
              .describe(
                'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
              )
              .optional(),
            itemsInfo: z
              .object({
                items: z
                  .array(
                    z.intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                  )
                  .max(50)
                  .optional(),
              })
              .describe(
                'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
              )
              .optional(),
          })
          .describe(
            'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
          )
          .optional(),
        seoData: z
          .object({
            tags: z
              .array(
                z.object({
                  type: z
                    .string()
                    .describe(
                      'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                    )
                    .optional(),
                  props: z
                    .record(z.string(), z.any())
                    .describe(
                      'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                    )
                    .optional()
                    .nullable(),
                  meta: z
                    .record(z.string(), z.any())
                    .describe(
                      'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                    )
                    .optional()
                    .nullable(),
                  children: z
                    .string()
                    .describe(
                      'SEO tag inner content. For example, `<title> inner content </title>`.'
                    )
                    .optional(),
                  custom: z
                    .boolean()
                    .describe(
                      'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                    )
                    .optional(),
                  disabled: z
                    .boolean()
                    .describe(
                      "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                    )
                    .optional(),
                })
              )
              .optional(),
            settings: z
              .object({
                preventAutoRedirect: z
                  .boolean()
                  .describe(
                    'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                  )
                  .optional(),
                keywords: z
                  .array(
                    z.object({
                      term: z.string().describe('Keyword value.').optional(),
                      isMain: z
                        .boolean()
                        .describe(
                          'Whether the keyword is the main focus keyword.'
                        )
                        .optional(),
                      origin: z
                        .string()
                        .describe(
                          'The source that added the keyword terms to the SEO settings.'
                        )
                        .max(1000)
                        .optional()
                        .nullable(),
                    })
                  )
                  .max(5)
                  .optional(),
              })
              .describe('SEO general settings.')
              .optional(),
          })
          .describe('Product SEO data.')
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        options: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Option name.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                optionRenderType: z
                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .describe('Option render type.')
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({ choicesSettings: z.never().optional() }),
                z.object({
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .describe('Media type.')
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .describe('Choice type.')
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe(
                                  'Choice name. For text choices, this field also contains the choice value.'
                                )
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              inStock: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                )
                                .optional(),
                              visible: z
                                .boolean()
                                .describe(
                                  'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choices settings.'),
                }),
              ])
            )
          )
          .max(6)
          .optional(),
        modifiers: z
          .array(
            z.intersection(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'ID of a customization with `customizationType: MODIFIER`.'
                  )
                  .min(1)
                  .max(36)
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Modifier title.')
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                modifierRenderType: z
                  .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                  .describe('Modifier render type.')
                  .optional(),
                mandatory: z
                  .boolean()
                  .describe(
                    'Whether customer input is required for this modifier.'
                  )
                  .optional(),
                key: z
                  .string()
                  .describe(
                    'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                  )
                  .min(1)
                  .max(50)
                  .optional(),
              }),
              z.xor([
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z.never().optional(),
                }),
                z.object({
                  choicesSettings: z.never().optional(),
                  freeTextSettings: z
                    .object({
                      minCharCount: z
                        .number()
                        .int()
                        .describe('Minimum number of characters.')
                        .optional(),
                      maxCharCount: z
                        .number()
                        .int()
                        .describe('Maximum number of characters.')
                        .max(500)
                        .optional(),
                      defaultAddedPrice: z
                        .string()
                        .describe(
                          "Default amount to be added to the product's price."
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe(
                          'Title of the text to be input by the customer.'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(150)
                        .optional(),
                    })
                    .describe('Free text modifier settings.'),
                }),
                z.object({
                  freeTextSettings: z.never().optional(),
                  choicesSettings: z
                    .object({
                      choices: z
                        .array(
                          z.intersection(
                            z.object({
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional()
                                .nullable(),
                              linkedMedia: z
                                .array(
                                  z.intersection(
                                    z.object({
                                      altText: z
                                        .string()
                                        .describe('Image alt text.')
                                        .min(1)
                                        .max(1000)
                                        .optional()
                                        .nullable(),
                                      displayName: z
                                        .string()
                                        .describe(
                                          "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                        )
                                        .max(80)
                                        .optional()
                                        .nullable(),
                                      mediaType: z
                                        .enum(['IMAGE', 'VIDEO'])
                                        .describe('Media type.')
                                        .optional(),
                                      thumbnail: z
                                        .object({
                                          url: z
                                            .string()
                                            .describe('Thumbnail url.')
                                            .url()
                                            .optional(),
                                          height: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail height.')
                                            .optional(),
                                          width: z
                                            .number()
                                            .int()
                                            .describe('Thumbnail width.')
                                            .optional(),
                                          altText: z
                                            .string()
                                            .describe('Thumbnail alt text.')
                                            .min(1)
                                            .max(1000)
                                            .optional()
                                            .nullable(),
                                        })
                                        .describe(
                                          'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                        )
                                        .optional(),
                                      uploadId: z
                                        .string()
                                        .describe(
                                          'ID used to upload media to Wix Media Manager.'
                                        )
                                        .regex(
                                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                          'Must be a valid GUID'
                                        )
                                        .optional(),
                                    }),
                                    z.intersection(
                                      z.xor([
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z.never().optional(),
                                        }),
                                        z.object({
                                          url: z.never().optional(),
                                          _id: z
                                            .string()
                                            .describe(
                                              'Set media by ID of an existing file in Wix Media Manager.'
                                            )
                                            .min(1)
                                            .max(200),
                                        }),
                                        z.object({
                                          _id: z.never().optional(),
                                          url: z
                                            .string()
                                            .describe(
                                              'Set media using an external media URL.'
                                            )
                                            .url(),
                                        }),
                                      ]),
                                      z.xor([
                                        z.object({
                                          image: z.never().optional(),
                                          video: z.never().optional(),
                                        }),
                                        z.object({
                                          video: z.never().optional(),
                                          image: z
                                            .string()
                                            .describe(
                                              "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                            ),
                                        }),
                                        z.object({
                                          image: z.never().optional(),
                                          video: z
                                            .string()
                                            .describe(
                                              "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                            ),
                                        }),
                                      ])
                                    )
                                  )
                                )
                                .max(15)
                                .optional(),
                              choiceType: z
                                .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                .describe('Choice type.')
                                .optional(),
                              key: z
                                .string()
                                .describe(
                                  'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                )
                                .min(1)
                                .max(50)
                                .optional(),
                              name: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .max(50)
                                .optional()
                                .nullable(),
                              addedPrice: z
                                .string()
                                .describe('Added price.')
                                .optional()
                                .nullable(),
                            }),
                            z.xor([
                              z.object({ colorCode: z.never().optional() }),
                              z.object({
                                colorCode: z
                                  .string()
                                  .describe(
                                    'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                  )
                                  .min(3)
                                  .max(20),
                              }),
                            ])
                          )
                        )
                        .min(1)
                        .max(100)
                        .optional(),
                    })
                    .describe('Choice settings.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        brand: z
          .object({
            _id: z
              .string()
              .describe('Brand ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Brand name.')
              .max(50)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
          )
          .optional(),
        infoSections: z
          .array(
            z.object({
              _id: z
                .string()
                .describe('Info section ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              uniqueName: z
                .string()
                .describe(
                  'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .max(100)
                .optional()
                .nullable(),
              title: z
                .string()
                .describe(
                  'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              description: z
                .any()
                .describe(
                  'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
            })
          )
          .max(10)
          .optional(),
        ribbon: z
          .object({
            _id: z
              .string()
              .describe('Ribbon ID.')
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Ribbon name.')
              .max(30)
              .optional()
              .nullable(),
          })
          .describe(
            "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
          )
          .optional(),
        directCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        allCategoriesInfo: z
          .object({
            categories: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  index: z
                    .number()
                    .int()
                    .describe(
                      'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                    )
                    .min(0)
                    .max(200)
                    .optional()
                    .nullable(),
                })
              )
              .max(2000)
              .optional(),
          })
          .describe(
            'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        mainCategoryId: z
          .string()
          .describe(
            'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        costRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
          )
          .optional(),
        inventory: z
          .object({
            availabilityStatus: z
              .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
              .describe('Current availability status.')
              .optional(),
            preorderStatus: z
              .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
              .describe('Current preorder status.')
              .optional(),
            preorderAvailability: z
              .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
              .describe('Preorder availability status.')
              .optional(),
          })
          .describe(
            'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
          )
          .optional(),
        productType: z
          .enum(['PHYSICAL', 'DIGITAL'])
          .describe(
            'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
          )
          .optional(),
        handle: z
          .string()
          .describe(
            "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        currency: z
          .string()
          .describe(
            'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
          )
          .optional()
          .nullable(),
        breadcrumbsInfo: z
          .object({
            breadcrumbs: z
              .array(
                z.object({
                  categoryId: z
                    .string()
                    .describe('Category ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  categoryName: z
                    .string()
                    .describe('Category name.')
                    .min(1)
                    .max(80)
                    .optional(),
                  categorySlug: z
                    .string()
                    .describe('Category slug.')
                    .min(1)
                    .max(100)
                    .optional(),
                })
              )
              .max(5)
              .optional(),
          })
          .describe(
            'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
          )
          .optional(),
        actualPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum current selling prices across all product variants.'
          )
          .optional(),
        compareAtPriceRange: z
          .object({
            minValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Minimum value.')
              .optional(),
            maxValue: z
              .object({
                amount: z
                  .string()
                  .describe(
                    'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                  )
                  .optional(),
                formattedAmount: z
                  .string()
                  .describe(
                    'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
              })
              .describe('Maximum value.')
              .optional(),
          })
          .describe(
            'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
          )
          .optional(),
        variantsInfo: z
          .object({
            variants: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Variant ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                      )
                      .optional()
                      .nullable(),
                    sku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    barcode: z
                      .string()
                      .describe('Variant barcode.')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    choices: z
                      .array(
                        z.object({
                          optionChoiceIds: z
                            .object({
                              optionId: z
                                .string()
                                .describe('Option ID.')
                                .min(1)
                                .max(36)
                                .optional(),
                              choiceId: z
                                .string()
                                .describe('Choice ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            })
                            .describe(
                              "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                            )
                            .optional(),
                          optionChoiceNames: z
                            .object({
                              optionName: z
                                .string()
                                .describe('Option name.')
                                .min(1)
                                .optional(),
                              choiceName: z
                                .string()
                                .describe('Choice name.')
                                .min(1)
                                .optional(),
                              renderType: z
                                .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                .describe(
                                  'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                )
                                .optional(),
                            })
                            .describe(
                              'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                        })
                      )
                      .min(0)
                      .max(6)
                      .optional(),
                    price: z
                      .object({
                        actualPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            "Variant's current selling price. Must be greater than or equal to 0."
                          )
                          .optional(),
                        compareAtPrice: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                          )
                          .optional(),
                      })
                      .describe('Variant price.')
                      .optional(),
                    revenueDetails: z
                      .object({
                        cost: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe('Item cost.')
                          .optional(),
                        profit: z
                          .object({
                            amount: z
                              .string()
                              .describe(
                                'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                              )
                              .optional(),
                            formattedAmount: z
                              .string()
                              .describe(
                                'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                              )
                              .max(20)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                          )
                          .optional(),
                        profitMargin: z
                          .number()
                          .describe(
                            'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                          )
                          .min(0)
                          .max(1)
                          .optional(),
                      })
                      .describe(
                        'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                      )
                      .optional(),
                    media: z
                      .intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z
                            .enum(['IMAGE', 'VIDEO'])
                            .describe('Media type.')
                            .optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                      .describe('Variant media.')
                      .optional(),
                    subscriptionPricesInfo: z
                      .object({
                        subscriptionPrices: z
                          .array(
                            z.object({
                              subscriptionId: z
                                .string()
                                .describe('Subscription ID.')
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                              price: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                )
                                .optional(),
                              pricePerUnit: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                    )
                                    .optional(),
                                  description: z
                                    .string()
                                    .describe(
                                      'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Price per unit info.')
                                .optional(),
                            })
                          )
                          .max(6)
                          .optional(),
                      })
                      .describe(
                        'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    inventoryStatus: z
                      .object({
                        inStock: z
                          .boolean()
                          .describe('Whether the variant is in stock.')
                          .optional(),
                        preorderEnabled: z
                          .boolean()
                          .describe(
                            'Whether preorder is enabled for this variant.'
                          )
                          .optional(),
                      })
                      .describe('Variant inventory status.')
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z.never().optional(),
                    }),
                    z.object({
                      digitalProperties: z.never().optional(),
                      physicalProperties: z
                        .object({
                          weight: z
                            .number()
                            .describe(
                              'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                            )
                            .min(0)
                            .max(999999999.99)
                            .optional()
                            .nullable(),
                          pricePerUnit: z
                            .object({
                              settings: z
                                .object({
                                  quantity: z
                                    .number()
                                    .describe(
                                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                    )
                                    .min(0.01)
                                    .max(999999999.99)
                                    .optional(),
                                  measurementUnit: z
                                    .enum([
                                      'UNSPECIFIED',
                                      'ML',
                                      'CL',
                                      'L',
                                      'CBM',
                                      'MG',
                                      'G',
                                      'KG',
                                      'MM',
                                      'CM',
                                      'M',
                                      'SQM',
                                      'OZ',
                                      'LB',
                                      'FLOZ',
                                      'PT',
                                      'QT',
                                      'GAL',
                                      'IN',
                                      'FT',
                                      'YD',
                                      'SQFT',
                                    ])
                                    .describe(
                                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                )
                                .optional(),
                              value: z
                                .string()
                                .describe(
                                  "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                )
                                .optional(),
                              description: z
                                .string()
                                .describe(
                                  'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                            )
                            .optional(),
                          productDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Product dimensions (length, width, height of the physical product).'
                            )
                            .optional(),
                          packageDimensions: z
                            .object({
                              length: z
                                .string()
                                .describe(
                                  'Length. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              width: z
                                .string()
                                .describe(
                                  'Width. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              height: z
                                .string()
                                .describe(
                                  'Height. Measurement unit is handled at the system level (metric/imperial).'
                                )
                                .optional()
                                .nullable(),
                              unit: z
                                .enum([
                                  'UNKNOWN_DIMENSIONS_UNIT',
                                  'MM',
                                  'CM',
                                  'M',
                                  'IN',
                                  'FT',
                                  'YD',
                                ])
                                .describe(
                                  'Unit of measurement for dimensions (length, width, height).'
                                )
                                .optional(),
                            })
                            .describe(
                              'Package dimensions (length, width, height of the shipping package).'
                            )
                            .optional(),
                        })
                        .describe(
                          'Physical properties. Must be passed when `productType: PHYSICAL`'
                        ),
                    }),
                    z.object({
                      physicalProperties: z.never().optional(),
                      digitalProperties: z
                        .object({
                          digitalFile: z
                            .object({
                              _id: z
                                .string()
                                .describe('Media ID in media manager.')
                                .min(1)
                                .max(100)
                                .optional(),
                              fileName: z
                                .string()
                                .describe('Original file name.')
                                .min(1)
                                .max(1000)
                                .optional(),
                              fileSize: z
                                .string()
                                .describe('Original file size.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              fileType: z
                                .enum([
                                  'UNSPECIFIED',
                                  'SECURE_PICTURE',
                                  'SECURE_VIDEO',
                                  'SECURE_DOCUMENT',
                                  'SECURE_MUSIC',
                                  'SECURE_ARCHIVE',
                                  'SECURE_RAW',
                                ])
                                .describe('File type.')
                                .optional(),
                            })
                            .describe(
                              'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                            )
                            .optional(),
                        })
                        .describe(
                          'Digital properties. Must be passed when `productType: DIGITAL`'
                        ),
                    }),
                  ])
                )
              )
              .min(1)
              .max(1000)
              .optional(),
          })
          .describe(
            'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
          )
          .optional(),
        purchaseEligibility: z
          .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
          .describe('Purchase eligibility settings for the product.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags')
          .optional(),
        subscriptionDetails: z
          .object({
            subscriptions: z
              .array(
                z.intersection(
                  z.object({
                    _id: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe('Subscription title.')
                      .min(1)
                      .max(20)
                      .optional(),
                    description: z
                      .string()
                      .describe('Subscription description.')
                      .max(60)
                      .optional()
                      .nullable(),
                    visible: z
                      .boolean()
                      .describe(
                        'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                      )
                      .optional()
                      .nullable(),
                    frequency: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe(
                        'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                      )
                      .optional(),
                    interval: z
                      .number()
                      .int()
                      .describe(
                        'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    discount: z
                      .intersection(
                        z.object({
                          type: z
                            .enum(['AMOUNT', 'PERCENT'])
                            .describe('Discount type.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z.never().optional(),
                          }),
                          z.object({
                            percentOff: z.never().optional(),
                            amountOff: z
                              .string()
                              .describe(
                                'Amount to discount from the variant discounted_price.'
                              ),
                          }),
                          z.object({
                            amountOff: z.never().optional(),
                            percentOff: z
                              .number()
                              .describe(
                                'Percentage to discount from variant discounted_price.'
                              )
                              .min(0.1)
                              .max(99.9),
                          }),
                        ])
                      )
                      .describe(
                        'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                      )
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z.never().optional(),
                    }),
                    z.object({
                      billingCycles: z.never().optional(),
                      autoRenewal: z
                        .boolean()
                        .describe(
                          'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                        ),
                    }),
                    z.object({
                      autoRenewal: z.never().optional(),
                      billingCycles: z
                        .number()
                        .int()
                        .describe(
                          'Number of billing cycles before subscription ends.'
                        )
                        .min(2)
                        .max(999),
                    }),
                  ])
                )
              )
              .min(1)
              .max(6)
              .optional(),
            allowOneTimePurchases: z
              .boolean()
              .describe(
                'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
          )
          .optional(),
        variantSummary: z
          .object({
            variantCount: z
              .number()
              .int()
              .describe('The total number of variants for the product.')
              .optional(),
          })
          .describe('The total number of variants for the product.')
          .optional(),
      }),
      z.xor([
        z.object({ physicalProperties: z.never().optional() }),
        z.object({
          physicalProperties: z
            .object({
              pricePerUnit: z
                .object({
                  quantity: z
                    .number()
                    .describe(
                      'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                    )
                    .min(0.01)
                    .max(999999999.99)
                    .optional(),
                  measurementUnit: z
                    .enum([
                      'UNSPECIFIED',
                      'ML',
                      'CL',
                      'L',
                      'CBM',
                      'MG',
                      'G',
                      'KG',
                      'MM',
                      'CM',
                      'M',
                      'SQM',
                      'OZ',
                      'LB',
                      'FLOZ',
                      'PT',
                      'QT',
                      'GAL',
                      'IN',
                      'FT',
                      'YD',
                      'SQFT',
                    ])
                    .describe(
                      'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                    )
                    .optional(),
                })
                .describe(
                  'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                )
                .optional(),
              fulfillerId: z
                .string()
                .describe('Fulfiller ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              shippingWeightRange: z
                .object({
                  minValue: z
                    .number()
                    .describe(
                      'Minimum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                  maxValue: z
                    .number()
                    .describe(
                      'Maximum weight across all variants associated with this product.'
                    )
                    .min(0)
                    .max(999999999.99)
                    .optional(),
                })
                .describe(
                  'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                )
                .optional(),
              pricePerUnitRange: z
                .object({
                  minValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum price per unit across all variants.')
                    .optional(),
                  maxValue: z
                    .object({
                      value: z
                        .string()
                        .describe(
                          "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                        )
                        .optional(),
                      description: z
                        .string()
                        .describe(
                          'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum price per unit across all variants.')
                    .optional(),
                })
                .describe(
                  'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                )
                .optional(),
              weightMeasurementUnitInfo: z
                .object({
                  weightMeasurementUnit: z
                    .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                    .describe('Weight measurement unit.')
                    .optional(),
                })
                .describe(
                  'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              deliveryProfileId: z
                .string()
                .describe('Delivery profile ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
            ),
        }),
      ])
    )
    .describe('Product.')
    .optional(),
});
export const SearchProductsRequest = z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
              selectItemsBy: z
                .array(z.record(z.string(), z.any()))
                .max(10)
                .optional(),
            })
          )
          .max(10)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Aggregation name, returned in `aggregations.results.name`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                  )
                  .max(200)
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe('Sort type.')
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe('Sort direction.')
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                )
                                .max(20)
                                .optional(),
                            })
                            .describe(
                              'Options for including missing values in results.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not provided.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not provided.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                        .describe('Operator type for the scalar aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe('Interval for date histogram aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'Unique, caller-defined aggregation name, returned in `aggregations.results`.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe(
                                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                                )
                                .max(200)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe('Sort type.')
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe('Sort direction.')
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                              )
                                              .max(20)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values in results.'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe(
                                    "A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status."
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if `to` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if `from` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(50)
                                      .optional(),
                                  })
                                  .describe(
                                    'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                      .describe(
                                        'Operator type for the scalar aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Interval for date histogram aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(3)
                        .optional(),
                    })
                    .describe(
                      'A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`.'
                    ),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(20).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.\nApplies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Search options.'),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const SearchProductsResponse = z.object({
  products: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the product was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the product was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Product name. Translatable.')
            .min(1)
            .max(80)
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe(
              'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
            )
            .optional()
            .nullable(),
          url: z
            .string()
            .describe(
              'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
            )
            .optional(),
          description: z
            .any()
            .describe(
              'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
          visible: z
            .boolean()
            .describe(
              "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
            )
            .optional()
            .nullable(),
          visibleInPos: z
            .boolean()
            .describe(
              'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
            )
            .optional()
            .nullable(),
          media: z
            .object({
              main: z
                .intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z
                      .enum(['IMAGE', 'VIDEO'])
                      .describe('Media type.')
                      .optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
                .describe(
                  'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                )
                .optional(),
              itemsInfo: z
                .object({
                  items: z
                    .array(
                      z.intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z
                            .enum(['IMAGE', 'VIDEO'])
                            .describe('Media type.')
                            .optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                    )
                    .max(50)
                    .optional(),
                })
                .describe(
                  'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
            })
            .describe(
              'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
            )
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Product SEO data.')
            .optional(),
          taxGroupId: z
            .string()
            .describe(
              '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          options: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Option name.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  optionRenderType: z
                    .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .describe('Option render type.')
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({ choicesSettings: z.never().optional() }),
                  z.object({
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .describe('Media type.')
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .describe('Choice type.')
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Choice name. For text choices, this field also contains the choice value.'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                inStock: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                  )
                                  .optional(),
                                visible: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choices settings.'),
                  }),
                ])
              )
            )
            .max(6)
            .optional(),
          modifiers: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: MODIFIER`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Modifier title.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  modifierRenderType: z
                    .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .describe('Modifier render type.')
                    .optional(),
                  mandatory: z
                    .boolean()
                    .describe(
                      'Whether customer input is required for this modifier.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z.never().optional(),
                  }),
                  z.object({
                    choicesSettings: z.never().optional(),
                    freeTextSettings: z
                      .object({
                        minCharCount: z
                          .number()
                          .int()
                          .describe('Minimum number of characters.')
                          .optional(),
                        maxCharCount: z
                          .number()
                          .int()
                          .describe('Maximum number of characters.')
                          .max(500)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount to be added to the product's price."
                          )
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Title of the text to be input by the customer.'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                      })
                      .describe('Free text modifier settings.'),
                  }),
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .describe('Media type.')
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .describe('Choice type.')
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                addedPrice: z
                                  .string()
                                  .describe('Added price.')
                                  .optional()
                                  .nullable(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choice settings.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          brand: z
            .object({
              _id: z
                .string()
                .describe('Brand ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Brand name.')
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
            )
            .optional(),
          infoSections: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Info section ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                uniqueName: z
                  .string()
                  .describe(
                    'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                description: z
                  .any()
                  .describe(
                    'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                  )
                  .optional(),
                plainDescription: z
                  .string()
                  .describe(
                    'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                  )
                  .max(16000)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          ribbon: z
            .object({
              _id: z
                .string()
                .describe('Ribbon ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Ribbon name.')
                .max(30)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
            )
            .optional(),
          directCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          allCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          mainCategoryId: z
            .string()
            .describe(
              'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          costRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
            )
            .optional(),
          inventory: z
            .object({
              availabilityStatus: z
                .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
                .describe('Current availability status.')
                .optional(),
              preorderStatus: z
                .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                .describe('Current preorder status.')
                .optional(),
              preorderAvailability: z
                .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                .describe('Preorder availability status.')
                .optional(),
            })
            .describe(
              'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
            )
            .optional(),
          productType: z
            .enum(['PHYSICAL', 'DIGITAL'])
            .describe(
              'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
            )
            .optional(),
          handle: z
            .string()
            .describe(
              "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          currency: z
            .string()
            .describe(
              'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
            )
            .optional()
            .nullable(),
          breadcrumbsInfo: z
            .object({
              breadcrumbs: z
                .array(
                  z.object({
                    categoryId: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    categoryName: z
                      .string()
                      .describe('Category name.')
                      .min(1)
                      .max(80)
                      .optional(),
                    categorySlug: z
                      .string()
                      .describe('Category slug.')
                      .min(1)
                      .max(100)
                      .optional(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe(
              'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          actualPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum current selling prices across all product variants.'
            )
            .optional(),
          compareAtPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
            )
            .optional(),
          variantsInfo: z
            .object({
              variants: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      barcode: z
                        .string()
                        .describe('Variant barcode.')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      choices: z
                        .array(
                          z.object({
                            optionChoiceIds: z
                              .object({
                                optionId: z
                                  .string()
                                  .describe('Option ID.')
                                  .min(1)
                                  .max(36)
                                  .optional(),
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe(
                                "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                              )
                              .optional(),
                            optionChoiceNames: z
                              .object({
                                optionName: z
                                  .string()
                                  .describe('Option name.')
                                  .min(1)
                                  .optional(),
                                choiceName: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .optional(),
                                renderType: z
                                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                  .describe(
                                    'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                          })
                        )
                        .min(0)
                        .max(6)
                        .optional(),
                      price: z
                        .object({
                          actualPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Variant's current selling price. Must be greater than or equal to 0."
                            )
                            .optional(),
                          compareAtPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                            )
                            .optional(),
                        })
                        .describe('Variant price.')
                        .optional(),
                      revenueDetails: z
                        .object({
                          cost: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Item cost.')
                            .optional(),
                          profit: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                            )
                            .optional(),
                          profitMargin: z
                            .number()
                            .describe(
                              'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                            )
                            .min(0)
                            .max(1)
                            .optional(),
                        })
                        .describe(
                          'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                        )
                        .optional(),
                      media: z
                        .intersection(
                          z.object({
                            altText: z
                              .string()
                              .describe('Image alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                            displayName: z
                              .string()
                              .describe(
                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                              )
                              .max(80)
                              .optional()
                              .nullable(),
                            mediaType: z
                              .enum(['IMAGE', 'VIDEO'])
                              .describe('Media type.')
                              .optional(),
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .url()
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .optional(),
                                altText: z
                                  .string()
                                  .describe('Thumbnail alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                            uploadId: z
                              .string()
                              .describe(
                                'ID used to upload media to Wix Media Manager.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.intersection(
                            z.xor([
                              z.object({
                                _id: z.never().optional(),
                                url: z.never().optional(),
                              }),
                              z.object({
                                url: z.never().optional(),
                                _id: z
                                  .string()
                                  .describe(
                                    'Set media by ID of an existing file in Wix Media Manager.'
                                  )
                                  .min(1)
                                  .max(200),
                              }),
                              z.object({
                                _id: z.never().optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'Set media using an external media URL.'
                                  )
                                  .url(),
                              }),
                            ]),
                            z.xor([
                              z.object({
                                image: z.never().optional(),
                                video: z.never().optional(),
                              }),
                              z.object({
                                video: z.never().optional(),
                                image: z
                                  .string()
                                  .describe(
                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                  ),
                              }),
                              z.object({
                                image: z.never().optional(),
                                video: z
                                  .string()
                                  .describe(
                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                  ),
                              }),
                            ])
                          )
                        )
                        .describe('Variant media.')
                        .optional(),
                      subscriptionPricesInfo: z
                        .object({
                          subscriptionPrices: z
                            .array(
                              z.object({
                                subscriptionId: z
                                  .string()
                                  .describe('Subscription ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                price: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe(
                                        'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                      )
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                  )
                                  .optional(),
                                pricePerUnit: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Price per unit info.')
                                  .optional(),
                              })
                            )
                            .max(6)
                            .optional(),
                        })
                        .describe(
                          'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      inventoryStatus: z
                        .object({
                          inStock: z
                            .boolean()
                            .describe('Whether the variant is in stock.')
                            .optional(),
                          preorderEnabled: z
                            .boolean()
                            .describe(
                              'Whether preorder is enabled for this variant.'
                            )
                            .optional(),
                        })
                        .describe('Variant inventory status.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z.never().optional(),
                      }),
                      z.object({
                        digitalProperties: z.never().optional(),
                        physicalProperties: z
                          .object({
                            weight: z
                              .number()
                              .describe(
                                'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional()
                              .nullable(),
                            pricePerUnit: z
                              .object({
                                settings: z
                                  .object({
                                    quantity: z
                                      .number()
                                      .describe(
                                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                      )
                                      .min(0.01)
                                      .max(999999999.99)
                                      .optional(),
                                    measurementUnit: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'ML',
                                        'CL',
                                        'L',
                                        'CBM',
                                        'MG',
                                        'G',
                                        'KG',
                                        'MM',
                                        'CM',
                                        'M',
                                        'SQM',
                                        'OZ',
                                        'LB',
                                        'FLOZ',
                                        'PT',
                                        'QT',
                                        'GAL',
                                        'IN',
                                        'FT',
                                        'YD',
                                        'SQFT',
                                      ])
                                      .describe(
                                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                  )
                                  .optional(),
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                              )
                              .optional(),
                            productDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Product dimensions (length, width, height of the physical product).'
                              )
                              .optional(),
                            packageDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Package dimensions (length, width, height of the shipping package).'
                              )
                              .optional(),
                          })
                          .describe(
                            'Physical properties. Must be passed when `productType: PHYSICAL`'
                          ),
                      }),
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z
                          .object({
                            digitalFile: z
                              .object({
                                _id: z
                                  .string()
                                  .describe('Media ID in media manager.')
                                  .min(1)
                                  .max(100)
                                  .optional(),
                                fileName: z
                                  .string()
                                  .describe('Original file name.')
                                  .min(1)
                                  .max(1000)
                                  .optional(),
                                fileSize: z
                                  .string()
                                  .describe('Original file size.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                fileType: z
                                  .enum([
                                    'UNSPECIFIED',
                                    'SECURE_PICTURE',
                                    'SECURE_VIDEO',
                                    'SECURE_DOCUMENT',
                                    'SECURE_MUSIC',
                                    'SECURE_ARCHIVE',
                                    'SECURE_RAW',
                                  ])
                                  .describe('File type.')
                                  .optional(),
                              })
                              .describe(
                                'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Digital properties. Must be passed when `productType: DIGITAL`'
                          ),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(1000)
                .optional(),
            })
            .describe(
              'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
            )
            .optional(),
          purchaseEligibility: z
            .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
            .describe('Purchase eligibility settings for the product.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              publicTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe('Tags')
            .optional(),
          subscriptionDetails: z
            .object({
              subscriptions: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe('Subscription title.')
                        .min(1)
                        .max(20)
                        .optional(),
                      description: z
                        .string()
                        .describe('Subscription description.')
                        .max(60)
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                      frequency: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe(
                          'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                        )
                        .optional(),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      discount: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['AMOUNT', 'PERCENT'])
                              .describe('Discount type.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z.never().optional(),
                            }),
                            z.object({
                              percentOff: z.never().optional(),
                              amountOff: z
                                .string()
                                .describe(
                                  'Amount to discount from the variant discounted_price.'
                                ),
                            }),
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z
                                .number()
                                .describe(
                                  'Percentage to discount from variant discounted_price.'
                                )
                                .min(0.1)
                                .max(99.9),
                            }),
                          ])
                        )
                        .describe(
                          'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z.never().optional(),
                      }),
                      z.object({
                        billingCycles: z.never().optional(),
                        autoRenewal: z
                          .boolean()
                          .describe(
                            'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                          ),
                      }),
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z
                          .number()
                          .int()
                          .describe(
                            'Number of billing cycles before subscription ends.'
                          )
                          .min(2)
                          .max(999),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(6)
                .optional(),
              allowOneTimePurchases: z
                .boolean()
                .describe(
                  'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
            )
            .optional(),
          variantSummary: z
            .object({
              variantCount: z
                .number()
                .int()
                .describe('The total number of variants for the product.')
                .optional(),
            })
            .describe('The total number of variants for the product.')
            .optional(),
        }),
        z.xor([
          z.object({ physicalProperties: z.never().optional() }),
          z.object({
            physicalProperties: z
              .object({
                pricePerUnit: z
                  .object({
                    quantity: z
                      .number()
                      .describe(
                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                      )
                      .min(0.01)
                      .max(999999999.99)
                      .optional(),
                    measurementUnit: z
                      .enum([
                        'UNSPECIFIED',
                        'ML',
                        'CL',
                        'L',
                        'CBM',
                        'MG',
                        'G',
                        'KG',
                        'MM',
                        'CM',
                        'M',
                        'SQM',
                        'OZ',
                        'LB',
                        'FLOZ',
                        'PT',
                        'QT',
                        'GAL',
                        'IN',
                        'FT',
                        'YD',
                        'SQFT',
                      ])
                      .describe(
                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                      )
                      .optional(),
                  })
                  .describe(
                    'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                  )
                  .optional(),
                fulfillerId: z
                  .string()
                  .describe('Fulfiller ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shippingWeightRange: z
                  .object({
                    minValue: z
                      .number()
                      .describe(
                        'Minimum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                    maxValue: z
                      .number()
                      .describe(
                        'Maximum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                  })
                  .describe(
                    'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                  )
                  .optional(),
                pricePerUnitRange: z
                  .object({
                    minValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Minimum price per unit across all variants.')
                      .optional(),
                    maxValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum price per unit across all variants.')
                      .optional(),
                  })
                  .describe(
                    'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                  )
                  .optional(),
                weightMeasurementUnitInfo: z
                  .object({
                    weightMeasurementUnit: z
                      .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                      .describe('Weight measurement unit.')
                      .optional(),
                  })
                  .describe(
                    'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                deliveryProfileId: z
                  .string()
                  .describe('Delivery profile ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'Aggregation name, returned in `aggregations.results.name`.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Aggregation type. Must align with the corresponding aggregation field.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Total number of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe('Aggregation type.')
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field which the data was aggregated by.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Number of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Total number of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe(
                              'Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.'
                            )
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Aggregation data.')
    .optional(),
});
export const QueryProductsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
              selectItemsBy: z
                .array(z.record(z.string(), z.any()))
                .max(10)
                .optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Query options.'),
  options: z
    .object({
      fields: z
        .array(
          z.enum([
            'URL',
            'CURRENCY',
            'INFO_SECTION',
            'MERCHANT_DATA',
            'PLAIN_DESCRIPTION',
            'INFO_SECTION_PLAIN_DESCRIPTION',
            'SUBSCRIPTION_PRICES_INFO',
            'BREADCRUMBS_INFO',
            'WEIGHT_MEASUREMENT_UNIT_INFO',
            'VARIANT_OPTION_CHOICE_NAMES',
            'MEDIA_ITEMS_INFO',
            'DESCRIPTION',
            'DIRECT_CATEGORIES_INFO',
            'ALL_CATEGORIES_INFO',
            'INFO_SECTION_DESCRIPTION',
            'THUMBNAIL',
            'PRODUCT_CHOICES_MEDIA_REFERENCES',
          ])
        )
        .max(100)
        .optional(),
    })
    .optional(),
});
export const QueryProductsResponse = z.object({
  products: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the product was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the product was updated.')
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Product name. Translatable.')
            .min(1)
            .max(80)
            .optional()
            .nullable(),
          slug: z
            .string()
            .describe(
              'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
            )
            .optional()
            .nullable(),
          url: z
            .string()
            .describe(
              'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
            )
            .optional(),
          description: z
            .any()
            .describe(
              'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
            )
            .optional(),
          plainDescription: z
            .string()
            .describe(
              'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
            )
            .max(16000)
            .optional()
            .nullable(),
          visible: z
            .boolean()
            .describe(
              "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
            )
            .optional()
            .nullable(),
          visibleInPos: z
            .boolean()
            .describe(
              'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
            )
            .optional()
            .nullable(),
          media: z
            .object({
              main: z
                .intersection(
                  z.object({
                    altText: z
                      .string()
                      .describe('Image alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    displayName: z
                      .string()
                      .describe(
                        "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                      )
                      .max(80)
                      .optional()
                      .nullable(),
                    mediaType: z
                      .enum(['IMAGE', 'VIDEO'])
                      .describe('Media type.')
                      .optional(),
                    thumbnail: z
                      .object({
                        url: z
                          .string()
                          .describe('Thumbnail url.')
                          .url()
                          .optional(),
                        height: z
                          .number()
                          .int()
                          .describe('Thumbnail height.')
                          .optional(),
                        width: z
                          .number()
                          .int()
                          .describe('Thumbnail width.')
                          .optional(),
                        altText: z
                          .string()
                          .describe('Thumbnail alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    uploadId: z
                      .string()
                      .describe('ID used to upload media to Wix Media Manager.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        _id: z.never().optional(),
                        url: z.never().optional(),
                      }),
                      z.object({
                        url: z.never().optional(),
                        _id: z
                          .string()
                          .describe(
                            'Set media by ID of an existing file in Wix Media Manager.'
                          )
                          .min(1)
                          .max(200),
                      }),
                      z.object({
                        _id: z.never().optional(),
                        url: z
                          .string()
                          .describe('Set media using an external media URL.')
                          .url(),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        image: z.never().optional(),
                        video: z.never().optional(),
                      }),
                      z.object({
                        video: z.never().optional(),
                        image: z
                          .string()
                          .describe(
                            "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                          ),
                      }),
                      z.object({
                        image: z.never().optional(),
                        video: z
                          .string()
                          .describe(
                            "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                          ),
                      }),
                    ])
                  )
                )
                .describe(
                  'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                )
                .optional(),
              itemsInfo: z
                .object({
                  items: z
                    .array(
                      z.intersection(
                        z.object({
                          altText: z
                            .string()
                            .describe('Image alt text.')
                            .min(1)
                            .max(1000)
                            .optional()
                            .nullable(),
                          displayName: z
                            .string()
                            .describe(
                              "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                            )
                            .max(80)
                            .optional()
                            .nullable(),
                          mediaType: z
                            .enum(['IMAGE', 'VIDEO'])
                            .describe('Media type.')
                            .optional(),
                          thumbnail: z
                            .object({
                              url: z
                                .string()
                                .describe('Thumbnail url.')
                                .url()
                                .optional(),
                              height: z
                                .number()
                                .int()
                                .describe('Thumbnail height.')
                                .optional(),
                              width: z
                                .number()
                                .int()
                                .describe('Thumbnail width.')
                                .optional(),
                              altText: z
                                .string()
                                .describe('Thumbnail alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          uploadId: z
                            .string()
                            .describe(
                              'ID used to upload media to Wix Media Manager.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional(),
                        }),
                        z.intersection(
                          z.xor([
                            z.object({
                              _id: z.never().optional(),
                              url: z.never().optional(),
                            }),
                            z.object({
                              url: z.never().optional(),
                              _id: z
                                .string()
                                .describe(
                                  'Set media by ID of an existing file in Wix Media Manager.'
                                )
                                .min(1)
                                .max(200),
                            }),
                            z.object({
                              _id: z.never().optional(),
                              url: z
                                .string()
                                .describe(
                                  'Set media using an external media URL.'
                                )
                                .url(),
                            }),
                          ]),
                          z.xor([
                            z.object({
                              image: z.never().optional(),
                              video: z.never().optional(),
                            }),
                            z.object({
                              video: z.never().optional(),
                              image: z
                                .string()
                                .describe(
                                  "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                ),
                            }),
                            z.object({
                              image: z.never().optional(),
                              video: z
                                .string()
                                .describe(
                                  "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                ),
                            }),
                          ])
                        )
                      )
                    )
                    .max(50)
                    .optional(),
                })
                .describe(
                  'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
            })
            .describe(
              'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
            )
            .optional(),
          seoData: z
            .object({
              tags: z
                .array(
                  z.object({
                    type: z
                      .string()
                      .describe(
                        'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                      )
                      .optional(),
                    props: z
                      .record(z.string(), z.any())
                      .describe(
                        'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                      )
                      .optional()
                      .nullable(),
                    meta: z
                      .record(z.string(), z.any())
                      .describe(
                        'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                      )
                      .optional()
                      .nullable(),
                    children: z
                      .string()
                      .describe(
                        'SEO tag inner content. For example, `<title> inner content </title>`.'
                      )
                      .optional(),
                    custom: z
                      .boolean()
                      .describe(
                        'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                      )
                      .optional(),
                    disabled: z
                      .boolean()
                      .describe(
                        "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                      )
                      .optional(),
                  })
                )
                .optional(),
              settings: z
                .object({
                  preventAutoRedirect: z
                    .boolean()
                    .describe(
                      'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                    )
                    .optional(),
                  keywords: z
                    .array(
                      z.object({
                        term: z.string().describe('Keyword value.').optional(),
                        isMain: z
                          .boolean()
                          .describe(
                            'Whether the keyword is the main focus keyword.'
                          )
                          .optional(),
                        origin: z
                          .string()
                          .describe(
                            'The source that added the keyword terms to the SEO settings.'
                          )
                          .max(1000)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe('SEO general settings.')
                .optional(),
            })
            .describe('Product SEO data.')
            .optional(),
          taxGroupId: z
            .string()
            .describe(
              '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          options: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Option name.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  optionRenderType: z
                    .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .describe('Option render type.')
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({ choicesSettings: z.never().optional() }),
                  z.object({
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .describe('Media type.')
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .describe('Choice type.')
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe(
                                    'Choice name. For text choices, this field also contains the choice value.'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                inStock: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                  )
                                  .optional(),
                                visible: z
                                  .boolean()
                                  .describe(
                                    'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                  )
                                  .optional(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choices settings.'),
                  }),
                ])
              )
            )
            .max(6)
            .optional(),
          modifiers: z
            .array(
              z.intersection(
                z.object({
                  _id: z
                    .string()
                    .describe(
                      'ID of a customization with `customizationType: MODIFIER`.'
                    )
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Modifier title.')
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  modifierRenderType: z
                    .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                    .describe('Modifier render type.')
                    .optional(),
                  mandatory: z
                    .boolean()
                    .describe(
                      'Whether customer input is required for this modifier.'
                    )
                    .optional(),
                  key: z
                    .string()
                    .describe(
                      'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                    )
                    .min(1)
                    .max(50)
                    .optional(),
                }),
                z.xor([
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z.never().optional(),
                  }),
                  z.object({
                    choicesSettings: z.never().optional(),
                    freeTextSettings: z
                      .object({
                        minCharCount: z
                          .number()
                          .int()
                          .describe('Minimum number of characters.')
                          .optional(),
                        maxCharCount: z
                          .number()
                          .int()
                          .describe('Maximum number of characters.')
                          .max(500)
                          .optional(),
                        defaultAddedPrice: z
                          .string()
                          .describe(
                            "Default amount to be added to the product's price."
                          )
                          .optional()
                          .nullable(),
                        title: z
                          .string()
                          .describe(
                            'Title of the text to be input by the customer.'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                        key: z
                          .string()
                          .describe(
                            'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                          )
                          .min(1)
                          .max(150)
                          .optional(),
                      })
                      .describe('Free text modifier settings.'),
                  }),
                  z.object({
                    freeTextSettings: z.never().optional(),
                    choicesSettings: z
                      .object({
                        choices: z
                          .array(
                            z.intersection(
                              z.object({
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional()
                                  .nullable(),
                                linkedMedia: z
                                  .array(
                                    z.intersection(
                                      z.object({
                                        altText: z
                                          .string()
                                          .describe('Image alt text.')
                                          .min(1)
                                          .max(1000)
                                          .optional()
                                          .nullable(),
                                        displayName: z
                                          .string()
                                          .describe(
                                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                          )
                                          .max(80)
                                          .optional()
                                          .nullable(),
                                        mediaType: z
                                          .enum(['IMAGE', 'VIDEO'])
                                          .describe('Media type.')
                                          .optional(),
                                        thumbnail: z
                                          .object({
                                            url: z
                                              .string()
                                              .describe('Thumbnail url.')
                                              .url()
                                              .optional(),
                                            height: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail height.')
                                              .optional(),
                                            width: z
                                              .number()
                                              .int()
                                              .describe('Thumbnail width.')
                                              .optional(),
                                            altText: z
                                              .string()
                                              .describe('Thumbnail alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                          })
                                          .describe(
                                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                          )
                                          .optional(),
                                        uploadId: z
                                          .string()
                                          .describe(
                                            'ID used to upload media to Wix Media Manager.'
                                          )
                                          .regex(
                                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                            'Must be a valid GUID'
                                          )
                                          .optional(),
                                      }),
                                      z.intersection(
                                        z.xor([
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z.never().optional(),
                                          }),
                                          z.object({
                                            url: z.never().optional(),
                                            _id: z
                                              .string()
                                              .describe(
                                                'Set media by ID of an existing file in Wix Media Manager.'
                                              )
                                              .min(1)
                                              .max(200),
                                          }),
                                          z.object({
                                            _id: z.never().optional(),
                                            url: z
                                              .string()
                                              .describe(
                                                'Set media using an external media URL.'
                                              )
                                              .url(),
                                          }),
                                        ]),
                                        z.xor([
                                          z.object({
                                            image: z.never().optional(),
                                            video: z.never().optional(),
                                          }),
                                          z.object({
                                            video: z.never().optional(),
                                            image: z
                                              .string()
                                              .describe(
                                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                              ),
                                          }),
                                          z.object({
                                            image: z.never().optional(),
                                            video: z
                                              .string()
                                              .describe(
                                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                              ),
                                          }),
                                        ])
                                      )
                                    )
                                  )
                                  .max(15)
                                  .optional(),
                                choiceType: z
                                  .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                  .describe('Choice type.')
                                  .optional(),
                                key: z
                                  .string()
                                  .describe(
                                    'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                  )
                                  .min(1)
                                  .max(50)
                                  .optional(),
                                name: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                addedPrice: z
                                  .string()
                                  .describe('Added price.')
                                  .optional()
                                  .nullable(),
                              }),
                              z.xor([
                                z.object({ colorCode: z.never().optional() }),
                                z.object({
                                  colorCode: z
                                    .string()
                                    .describe(
                                      'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                    )
                                    .min(3)
                                    .max(20),
                                }),
                              ])
                            )
                          )
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                      .describe('Choice settings.'),
                  }),
                ])
              )
            )
            .max(10)
            .optional(),
          brand: z
            .object({
              _id: z
                .string()
                .describe('Brand ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Brand name.')
                .max(50)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
            )
            .optional(),
          infoSections: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe('Info section ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                uniqueName: z
                  .string()
                  .describe(
                    'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                title: z
                  .string()
                  .describe(
                    'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                description: z
                  .any()
                  .describe(
                    'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                  )
                  .optional(),
                plainDescription: z
                  .string()
                  .describe(
                    'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                  )
                  .max(16000)
                  .optional()
                  .nullable(),
              })
            )
            .max(10)
            .optional(),
          ribbon: z
            .object({
              _id: z
                .string()
                .describe('Ribbon ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Ribbon name.')
                .max(30)
                .optional()
                .nullable(),
            })
            .describe(
              "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
            )
            .optional(),
          directCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          allCategoriesInfo: z
            .object({
              categories: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    index: z
                      .number()
                      .int()
                      .describe(
                        'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                      )
                      .min(0)
                      .max(200)
                      .optional()
                      .nullable(),
                  })
                )
                .max(2000)
                .optional(),
            })
            .describe(
              'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          mainCategoryId: z
            .string()
            .describe(
              'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          costRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
            )
            .optional(),
          inventory: z
            .object({
              availabilityStatus: z
                .enum(['IN_STOCK', 'OUT_OF_STOCK', 'PARTIALLY_OUT_OF_STOCK'])
                .describe('Current availability status.')
                .optional(),
              preorderStatus: z
                .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                .describe('Current preorder status.')
                .optional(),
              preorderAvailability: z
                .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                .describe('Preorder availability status.')
                .optional(),
            })
            .describe(
              'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
            )
            .optional(),
          productType: z
            .enum(['PHYSICAL', 'DIGITAL'])
            .describe(
              'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
            )
            .optional(),
          handle: z
            .string()
            .describe(
              "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          currency: z
            .string()
            .describe(
              'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
            )
            .optional()
            .nullable(),
          breadcrumbsInfo: z
            .object({
              breadcrumbs: z
                .array(
                  z.object({
                    categoryId: z
                      .string()
                      .describe('Category ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    categoryName: z
                      .string()
                      .describe('Category name.')
                      .min(1)
                      .max(80)
                      .optional(),
                    categorySlug: z
                      .string()
                      .describe('Category slug.')
                      .min(1)
                      .max(100)
                      .optional(),
                  })
                )
                .max(5)
                .optional(),
            })
            .describe(
              'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          actualPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum current selling prices across all product variants.'
            )
            .optional(),
          compareAtPriceRange: z
            .object({
              minValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Minimum value.')
                .optional(),
              maxValue: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Maximum value.')
                .optional(),
            })
            .describe(
              'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
            )
            .optional(),
          variantsInfo: z
            .object({
              variants: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Variant ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                        )
                        .optional()
                        .nullable(),
                      sku: z
                        .string()
                        .describe('Variant SKU (stock keeping unit).')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      barcode: z
                        .string()
                        .describe('Variant barcode.')
                        .min(1)
                        .max(40)
                        .optional()
                        .nullable(),
                      choices: z
                        .array(
                          z.object({
                            optionChoiceIds: z
                              .object({
                                optionId: z
                                  .string()
                                  .describe('Option ID.')
                                  .min(1)
                                  .max(36)
                                  .optional(),
                                choiceId: z
                                  .string()
                                  .describe('Choice ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              })
                              .describe(
                                "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                              )
                              .optional(),
                            optionChoiceNames: z
                              .object({
                                optionName: z
                                  .string()
                                  .describe('Option name.')
                                  .min(1)
                                  .optional(),
                                choiceName: z
                                  .string()
                                  .describe('Choice name.')
                                  .min(1)
                                  .optional(),
                                renderType: z
                                  .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                  .describe(
                                    'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                          })
                        )
                        .min(0)
                        .max(6)
                        .optional(),
                      price: z
                        .object({
                          actualPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              "Variant's current selling price. Must be greater than or equal to 0."
                            )
                            .optional(),
                          compareAtPrice: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                            )
                            .optional(),
                        })
                        .describe('Variant price.')
                        .optional(),
                      revenueDetails: z
                        .object({
                          cost: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe('Item cost.')
                            .optional(),
                          profit: z
                            .object({
                              amount: z
                                .string()
                                .describe(
                                  'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                )
                                .optional(),
                              formattedAmount: z
                                .string()
                                .describe(
                                  'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                )
                                .max(20)
                                .optional()
                                .nullable(),
                            })
                            .describe(
                              'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                            )
                            .optional(),
                          profitMargin: z
                            .number()
                            .describe(
                              'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                            )
                            .min(0)
                            .max(1)
                            .optional(),
                        })
                        .describe(
                          'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                        )
                        .optional(),
                      media: z
                        .intersection(
                          z.object({
                            altText: z
                              .string()
                              .describe('Image alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                            displayName: z
                              .string()
                              .describe(
                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                              )
                              .max(80)
                              .optional()
                              .nullable(),
                            mediaType: z
                              .enum(['IMAGE', 'VIDEO'])
                              .describe('Media type.')
                              .optional(),
                            thumbnail: z
                              .object({
                                url: z
                                  .string()
                                  .describe('Thumbnail url.')
                                  .url()
                                  .optional(),
                                height: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail height.')
                                  .optional(),
                                width: z
                                  .number()
                                  .int()
                                  .describe('Thumbnail width.')
                                  .optional(),
                                altText: z
                                  .string()
                                  .describe('Thumbnail alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                              )
                              .optional(),
                            uploadId: z
                              .string()
                              .describe(
                                'ID used to upload media to Wix Media Manager.'
                              )
                              .regex(
                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                'Must be a valid GUID'
                              )
                              .optional(),
                          }),
                          z.intersection(
                            z.xor([
                              z.object({
                                _id: z.never().optional(),
                                url: z.never().optional(),
                              }),
                              z.object({
                                url: z.never().optional(),
                                _id: z
                                  .string()
                                  .describe(
                                    'Set media by ID of an existing file in Wix Media Manager.'
                                  )
                                  .min(1)
                                  .max(200),
                              }),
                              z.object({
                                _id: z.never().optional(),
                                url: z
                                  .string()
                                  .describe(
                                    'Set media using an external media URL.'
                                  )
                                  .url(),
                              }),
                            ]),
                            z.xor([
                              z.object({
                                image: z.never().optional(),
                                video: z.never().optional(),
                              }),
                              z.object({
                                video: z.never().optional(),
                                image: z
                                  .string()
                                  .describe(
                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                  ),
                              }),
                              z.object({
                                image: z.never().optional(),
                                video: z
                                  .string()
                                  .describe(
                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                  ),
                              }),
                            ])
                          )
                        )
                        .describe('Variant media.')
                        .optional(),
                      subscriptionPricesInfo: z
                        .object({
                          subscriptionPrices: z
                            .array(
                              z.object({
                                subscriptionId: z
                                  .string()
                                  .describe('Subscription ID.')
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                                price: z
                                  .object({
                                    amount: z
                                      .string()
                                      .describe(
                                        'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                      )
                                      .optional(),
                                    formattedAmount: z
                                      .string()
                                      .describe(
                                        'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                  )
                                  .optional(),
                                pricePerUnit: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(20)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe('Price per unit info.')
                                  .optional(),
                              })
                            )
                            .max(6)
                            .optional(),
                        })
                        .describe(
                          'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                        )
                        .optional(),
                      inventoryStatus: z
                        .object({
                          inStock: z
                            .boolean()
                            .describe('Whether the variant is in stock.')
                            .optional(),
                          preorderEnabled: z
                            .boolean()
                            .describe(
                              'Whether preorder is enabled for this variant.'
                            )
                            .optional(),
                        })
                        .describe('Variant inventory status.')
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z.never().optional(),
                      }),
                      z.object({
                        digitalProperties: z.never().optional(),
                        physicalProperties: z
                          .object({
                            weight: z
                              .number()
                              .describe(
                                'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                              )
                              .min(0)
                              .max(999999999.99)
                              .optional()
                              .nullable(),
                            pricePerUnit: z
                              .object({
                                settings: z
                                  .object({
                                    quantity: z
                                      .number()
                                      .describe(
                                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                      )
                                      .min(0.01)
                                      .max(999999999.99)
                                      .optional(),
                                    measurementUnit: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'ML',
                                        'CL',
                                        'L',
                                        'CBM',
                                        'MG',
                                        'G',
                                        'KG',
                                        'MM',
                                        'CM',
                                        'M',
                                        'SQM',
                                        'OZ',
                                        'LB',
                                        'FLOZ',
                                        'PT',
                                        'QT',
                                        'GAL',
                                        'IN',
                                        'FT',
                                        'YD',
                                        'SQFT',
                                      ])
                                      .describe(
                                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                  )
                                  .optional(),
                                value: z
                                  .string()
                                  .describe(
                                    "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                  )
                                  .optional(),
                                description: z
                                  .string()
                                  .describe(
                                    'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                  )
                                  .max(100)
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                              )
                              .optional(),
                            productDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Product dimensions (length, width, height of the physical product).'
                              )
                              .optional(),
                            packageDimensions: z
                              .object({
                                length: z
                                  .string()
                                  .describe(
                                    'Length. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                width: z
                                  .string()
                                  .describe(
                                    'Width. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                height: z
                                  .string()
                                  .describe(
                                    'Height. Measurement unit is handled at the system level (metric/imperial).'
                                  )
                                  .optional()
                                  .nullable(),
                                unit: z
                                  .enum([
                                    'UNKNOWN_DIMENSIONS_UNIT',
                                    'MM',
                                    'CM',
                                    'M',
                                    'IN',
                                    'FT',
                                    'YD',
                                  ])
                                  .describe(
                                    'Unit of measurement for dimensions (length, width, height).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Package dimensions (length, width, height of the shipping package).'
                              )
                              .optional(),
                          })
                          .describe(
                            'Physical properties. Must be passed when `productType: PHYSICAL`'
                          ),
                      }),
                      z.object({
                        physicalProperties: z.never().optional(),
                        digitalProperties: z
                          .object({
                            digitalFile: z
                              .object({
                                _id: z
                                  .string()
                                  .describe('Media ID in media manager.')
                                  .min(1)
                                  .max(100)
                                  .optional(),
                                fileName: z
                                  .string()
                                  .describe('Original file name.')
                                  .min(1)
                                  .max(1000)
                                  .optional(),
                                fileSize: z
                                  .string()
                                  .describe('Original file size.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                fileType: z
                                  .enum([
                                    'UNSPECIFIED',
                                    'SECURE_PICTURE',
                                    'SECURE_VIDEO',
                                    'SECURE_DOCUMENT',
                                    'SECURE_MUSIC',
                                    'SECURE_ARCHIVE',
                                    'SECURE_RAW',
                                  ])
                                  .describe('File type.')
                                  .optional(),
                              })
                              .describe(
                                'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                              )
                              .optional(),
                          })
                          .describe(
                            'Digital properties. Must be passed when `productType: DIGITAL`'
                          ),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(1000)
                .optional(),
            })
            .describe(
              'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
            )
            .optional(),
          purchaseEligibility: z
            .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
            .describe('Purchase eligibility settings for the product.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              publicTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe('Tags')
            .optional(),
          subscriptionDetails: z
            .object({
              subscriptions: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe('Subscription ID.')
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      title: z
                        .string()
                        .describe('Subscription title.')
                        .min(1)
                        .max(20)
                        .optional(),
                      description: z
                        .string()
                        .describe('Subscription description.')
                        .max(60)
                        .optional()
                        .nullable(),
                      visible: z
                        .boolean()
                        .describe(
                          'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                        )
                        .optional()
                        .nullable(),
                      frequency: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe(
                          'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                        )
                        .optional(),
                      interval: z
                        .number()
                        .int()
                        .describe(
                          'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                        )
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      discount: z
                        .intersection(
                          z.object({
                            type: z
                              .enum(['AMOUNT', 'PERCENT'])
                              .describe('Discount type.')
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z.never().optional(),
                            }),
                            z.object({
                              percentOff: z.never().optional(),
                              amountOff: z
                                .string()
                                .describe(
                                  'Amount to discount from the variant discounted_price.'
                                ),
                            }),
                            z.object({
                              amountOff: z.never().optional(),
                              percentOff: z
                                .number()
                                .describe(
                                  'Percentage to discount from variant discounted_price.'
                                )
                                .min(0.1)
                                .max(99.9),
                            }),
                          ])
                        )
                        .describe(
                          'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                        )
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z.never().optional(),
                      }),
                      z.object({
                        billingCycles: z.never().optional(),
                        autoRenewal: z
                          .boolean()
                          .describe(
                            'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                          ),
                      }),
                      z.object({
                        autoRenewal: z.never().optional(),
                        billingCycles: z
                          .number()
                          .int()
                          .describe(
                            'Number of billing cycles before subscription ends.'
                          )
                          .min(2)
                          .max(999),
                      }),
                    ])
                  )
                )
                .min(1)
                .max(6)
                .optional(),
              allowOneTimePurchases: z
                .boolean()
                .describe(
                  'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
            )
            .optional(),
          variantSummary: z
            .object({
              variantCount: z
                .number()
                .int()
                .describe('The total number of variants for the product.')
                .optional(),
            })
            .describe('The total number of variants for the product.')
            .optional(),
        }),
        z.xor([
          z.object({ physicalProperties: z.never().optional() }),
          z.object({
            physicalProperties: z
              .object({
                pricePerUnit: z
                  .object({
                    quantity: z
                      .number()
                      .describe(
                        'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                      )
                      .min(0.01)
                      .max(999999999.99)
                      .optional(),
                    measurementUnit: z
                      .enum([
                        'UNSPECIFIED',
                        'ML',
                        'CL',
                        'L',
                        'CBM',
                        'MG',
                        'G',
                        'KG',
                        'MM',
                        'CM',
                        'M',
                        'SQM',
                        'OZ',
                        'LB',
                        'FLOZ',
                        'PT',
                        'QT',
                        'GAL',
                        'IN',
                        'FT',
                        'YD',
                        'SQFT',
                      ])
                      .describe(
                        'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                      )
                      .optional(),
                  })
                  .describe(
                    'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                  )
                  .optional(),
                fulfillerId: z
                  .string()
                  .describe('Fulfiller ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                shippingWeightRange: z
                  .object({
                    minValue: z
                      .number()
                      .describe(
                        'Minimum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                    maxValue: z
                      .number()
                      .describe(
                        'Maximum weight across all variants associated with this product.'
                      )
                      .min(0)
                      .max(999999999.99)
                      .optional(),
                  })
                  .describe(
                    'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                  )
                  .optional(),
                pricePerUnitRange: z
                  .object({
                    minValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Minimum price per unit across all variants.')
                      .optional(),
                    maxValue: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                          )
                          .max(100)
                          .optional()
                          .nullable(),
                      })
                      .describe('Maximum price per unit across all variants.')
                      .optional(),
                  })
                  .describe(
                    'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                  )
                  .optional(),
                weightMeasurementUnitInfo: z
                  .object({
                    weightMeasurementUnit: z
                      .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                      .describe('Weight measurement unit.')
                      .optional(),
                  })
                  .describe(
                    'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                deliveryProfileId: z
                  .string()
                  .describe('Delivery profile ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const CountProductsRequest = z.object({
  options: z
    .object({
      filter: z
        .record(z.string(), z.any())
        .describe('Filter object.')
        .optional()
        .nullable(),
      search: z
        .object({
          mode: z.enum(['OR', 'AND']).optional(),
          expression: z
            .string()
            .describe('Search term or expression')
            .max(100)
            .optional()
            .nullable(),
          fields: z.array(z.string()).max(20).optional(),
          fuzzy: z
            .boolean()
            .describe(
              'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
            )
            .optional(),
        })
        .describe('Free text to match in searchable fields.')
        .optional(),
      returnNonVisibleProducts: z
        .boolean()
        .describe(
          "Whether to return non-visible products (`visible:false`). Your app must have the 'Product v3 read admin' (`SCOPE.STORES.PRODUCT_READ_ADMIN`) permission scope.\n\nDefault: `false`"
        )
        .optional(),
    })
    .optional(),
});
export const CountProductsResponse = z.object({
  count: z.number().int().describe('Total number of products.').optional(),
});
export const BulkUpdateProductVariantsByFilterRequest = z.object({
  filter: z.record(z.string(), z.any()).describe('Filter object.'),
  options: z.object({
    variant: z
      .intersection(
        z.object({
          _id: z
            .string()
            .describe('Variant ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          visible: z
            .boolean()
            .describe(
              "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
            )
            .optional()
            .nullable(),
          sku: z
            .string()
            .describe('Variant SKU (stock keeping unit).')
            .min(1)
            .max(40)
            .optional()
            .nullable(),
          barcode: z
            .string()
            .describe('Variant barcode.')
            .min(1)
            .max(40)
            .optional()
            .nullable(),
          choices: z
            .array(
              z.object({
                optionChoiceIds: z
                  .object({
                    optionId: z
                      .string()
                      .describe('Option ID.')
                      .min(1)
                      .max(36)
                      .optional(),
                    choiceId: z
                      .string()
                      .describe('Choice ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                  })
                  .describe(
                    "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                  )
                  .optional(),
                optionChoiceNames: z
                  .object({
                    optionName: z
                      .string()
                      .describe('Option name.')
                      .min(1)
                      .optional(),
                    choiceName: z
                      .string()
                      .describe('Choice name.')
                      .min(1)
                      .optional(),
                    renderType: z
                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                      .optional(),
                  })
                  .describe(
                    'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
              })
            )
            .min(0)
            .max(6)
            .optional(),
          price: z
            .object({
              actualPrice: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Variant's current selling price. Must be greater than or equal to 0."
                )
                .optional(),
              compareAtPrice: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                )
                .optional(),
            })
            .describe('Variant price.')
            .optional(),
          revenueDetails: z
            .object({
              cost: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe('Item cost.')
                .optional(),
              profit: z
                .object({
                  amount: z
                    .string()
                    .describe(
                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                    )
                    .optional(),
                  formattedAmount: z
                    .string()
                    .describe(
                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                    )
                    .max(20)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                )
                .optional(),
              profitMargin: z
                .number()
                .describe(
                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                )
                .min(0)
                .max(1)
                .optional(),
            })
            .describe(
              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
            )
            .optional(),
          media: z
            .intersection(
              z.object({
                altText: z
                  .string()
                  .describe('Image alt text.')
                  .min(1)
                  .max(1000)
                  .optional()
                  .nullable(),
                displayName: z
                  .string()
                  .describe(
                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                  )
                  .max(80)
                  .optional()
                  .nullable(),
                mediaType: z.enum(['IMAGE', 'VIDEO']).optional(),
                thumbnail: z
                  .object({
                    url: z.string().describe('Thumbnail url.').url().optional(),
                    height: z
                      .number()
                      .int()
                      .describe('Thumbnail height.')
                      .optional(),
                    width: z
                      .number()
                      .int()
                      .describe('Thumbnail width.')
                      .optional(),
                    altText: z
                      .string()
                      .describe('Thumbnail alt text.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                  )
                  .optional(),
                uploadId: z
                  .string()
                  .describe('ID used to upload media to Wix Media Manager.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              }),
              z.intersection(
                z.xor([
                  z.object({
                    _id: z.never().optional(),
                    url: z.never().optional(),
                  }),
                  z.object({
                    url: z.never().optional(),
                    _id: z
                      .string()
                      .describe(
                        'Set media by ID of an existing file in Wix Media Manager.'
                      )
                      .min(1)
                      .max(200),
                  }),
                  z.object({
                    _id: z.never().optional(),
                    url: z
                      .string()
                      .describe('Set media using an external media URL.')
                      .url(),
                  }),
                ]),
                z.xor([
                  z.object({
                    image: z.never().optional(),
                    video: z.never().optional(),
                  }),
                  z.object({
                    video: z.never().optional(),
                    image: z
                      .string()
                      .describe(
                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                      ),
                  }),
                  z.object({
                    image: z.never().optional(),
                    video: z
                      .string()
                      .describe(
                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                      ),
                  }),
                ])
              )
            )
            .describe('Variant media.')
            .optional(),
          subscriptionPricesInfo: z
            .object({
              subscriptionPrices: z
                .array(
                  z.object({
                    subscriptionId: z
                      .string()
                      .describe('Subscription ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional(),
                    price: z
                      .object({
                        amount: z
                          .string()
                          .describe(
                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                          )
                          .optional(),
                        formattedAmount: z
                          .string()
                          .describe(
                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe(
                        'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                      )
                      .optional(),
                    pricePerUnit: z
                      .object({
                        value: z
                          .string()
                          .describe(
                            'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                          )
                          .optional(),
                        description: z
                          .string()
                          .describe(
                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                          )
                          .max(20)
                          .optional()
                          .nullable(),
                      })
                      .describe('Price per unit info.')
                      .optional(),
                  })
                )
                .max(6)
                .optional(),
            })
            .describe(
              'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
            )
            .optional(),
          inventoryStatus: z
            .object({
              inStock: z
                .boolean()
                .describe('Whether the variant is in stock.')
                .optional(),
              preorderEnabled: z
                .boolean()
                .describe('Whether preorder is enabled for this variant.')
                .optional(),
            })
            .describe('Variant inventory status.')
            .optional(),
        }),
        z.xor([
          z.object({
            physicalProperties: z.never().optional(),
            digitalProperties: z.never().optional(),
          }),
          z.object({
            digitalProperties: z.never().optional(),
            physicalProperties: z
              .object({
                weight: z
                  .number()
                  .describe(
                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                  )
                  .min(0)
                  .max(999999999.99)
                  .optional()
                  .nullable(),
                pricePerUnit: z
                  .object({
                    settings: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                      )
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                      )
                      .optional(),
                    description: z
                      .string()
                      .describe(
                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                  )
                  .optional(),
                productDimensions: z
                  .object({
                    length: z
                      .string()
                      .describe(
                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                      )
                      .optional()
                      .nullable(),
                    width: z
                      .string()
                      .describe(
                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                      )
                      .optional()
                      .nullable(),
                    height: z
                      .string()
                      .describe(
                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                      )
                      .optional()
                      .nullable(),
                    unit: z
                      .enum([
                        'UNKNOWN_DIMENSIONS_UNIT',
                        'MM',
                        'CM',
                        'M',
                        'IN',
                        'FT',
                        'YD',
                      ])
                      .describe(
                        'Unit of measurement for dimensions (length, width, height).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Product dimensions (length, width, height of the physical product).'
                  )
                  .optional(),
                packageDimensions: z
                  .object({
                    length: z
                      .string()
                      .describe(
                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                      )
                      .optional()
                      .nullable(),
                    width: z
                      .string()
                      .describe(
                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                      )
                      .optional()
                      .nullable(),
                    height: z
                      .string()
                      .describe(
                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                      )
                      .optional()
                      .nullable(),
                    unit: z
                      .enum([
                        'UNKNOWN_DIMENSIONS_UNIT',
                        'MM',
                        'CM',
                        'M',
                        'IN',
                        'FT',
                        'YD',
                      ])
                      .describe(
                        'Unit of measurement for dimensions (length, width, height).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Package dimensions (length, width, height of the shipping package).'
                  )
                  .optional(),
              })
              .describe(
                'Physical properties. Must be passed when `productType: PHYSICAL`'
              ),
          }),
          z.object({
            physicalProperties: z.never().optional(),
            digitalProperties: z
              .object({
                digitalFile: z
                  .object({
                    _id: z
                      .string()
                      .describe('Media ID in media manager.')
                      .min(1)
                      .max(100)
                      .optional(),
                    fileName: z
                      .string()
                      .describe('Original file name.')
                      .min(1)
                      .max(1000)
                      .optional(),
                    fileSize: z
                      .string()
                      .describe('Original file size.')
                      .min(1)
                      .max(1000)
                      .optional()
                      .nullable(),
                    fileType: z
                      .enum([
                        'UNSPECIFIED',
                        'SECURE_PICTURE',
                        'SECURE_VIDEO',
                        'SECURE_DOCUMENT',
                        'SECURE_MUSIC',
                        'SECURE_ARCHIVE',
                        'SECURE_RAW',
                      ])
                      .describe('File type.')
                      .optional(),
                  })
                  .describe(
                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                  )
                  .optional(),
              })
              .describe(
                'Digital properties. Must be passed when `productType: DIGITAL`'
              ),
          }),
        ])
      )
      .describe('Variant to update.'),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkUpdateProductVariantsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkAdjustProductVariantsByFilterRequest = z.object({
  filter: z.record(z.string(), z.any()).describe('Filter object.'),
  options: z
    .object({
      cost: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              amount: z.never().optional(),
              percentage: z.never().optional(),
            }),
            z.object({
              percentage: z.never().optional(),
              amount: z
                .string()
                .describe(
                  'A decimal value to increase or reduce from the original value, can be negative.'
                ),
            }),
            z.object({
              amount: z.never().optional(),
              percentage: z
                .number()
                .int()
                .describe(
                  'The percentage value to increase or reduce from the current value, can be negative.'
                )
                .min(-100)
                .max(1000),
            }),
          ])
        )
        .describe('Cost adjustment.')
        .optional(),
      rounding: z
        .enum(['NO_ROUNDING', 'CURRENCY_PRECISION', 'NEAREST_WHOLE_NUMBER'])
        .optional(),
      search: z
        .object({
          mode: z.enum(['OR', 'AND']).optional(),
          expression: z
            .string()
            .describe('Search term or expression')
            .max(100)
            .optional()
            .nullable(),
          fields: z.array(z.string()).max(20).optional(),
          fuzzy: z
            .boolean()
            .describe(
              'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
            )
            .optional(),
        })
        .describe('Free text to match in searchable fields.')
        .optional(),
      actualPrice: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              amount: z.never().optional(),
              percentage: z.never().optional(),
            }),
            z.object({
              percentage: z.never().optional(),
              amount: z
                .string()
                .describe(
                  'A decimal value to increase or reduce from the original value, can be negative.'
                ),
            }),
            z.object({
              amount: z.never().optional(),
              percentage: z
                .number()
                .int()
                .describe(
                  'The percentage value to increase or reduce from the current value, can be negative.'
                )
                .min(-100)
                .max(1000),
            }),
          ])
        )
        .describe('Actual price adjustment.')
        .optional(),
      compareAtPrice: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              amount: z.never().optional(),
              percentage: z.never().optional(),
            }),
            z.object({
              percentage: z.never().optional(),
              amount: z
                .string()
                .describe(
                  'A decimal value to increase or reduce from the original value, can be negative.'
                ),
            }),
            z.object({
              amount: z.never().optional(),
              percentage: z
                .number()
                .int()
                .describe(
                  'The percentage value to increase or reduce from the current value, can be negative.'
                )
                .min(-100)
                .max(1000),
            }),
          ])
        )
        .describe('Compare at price adjustment.')
        .optional(),
      compareAtPriceDiscount: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              amount: z.never().optional(),
              percentage: z.never().optional(),
            }),
            z.object({
              percentage: z.never().optional(),
              amount: z
                .string()
                .describe('A decimal value to reduce from the original value.'),
            }),
            z.object({
              amount: z.never().optional(),
              percentage: z
                .number()
                .int()
                .describe(
                  'The percentage value to reduce from the original value.'
                )
                .min(0)
                .max(100),
            }),
          ])
        )
        .describe(
          "Set variant actualPrice from compareAtPrice by applying provided discount to it.\nif compare-at-price doesn't exist, actualPrice will be set to compareAtPrice and the discount will be calculated from it.\nFor example variant compareAtPrice 100$, variant actualPrice is 95$, requested `compareAtPriceDiscount.percentage` is 10, then old actual price ignored and new actual price set to 90 (100$ - 10%)."
        )
        .optional(),
    })
    .optional(),
});
export const BulkAdjustProductVariantsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkAddInfoSectionsToProductsByFilterRequest = z.object({
  filter: z.record(z.string(), z.any()).describe('Filter object.'),
  options: z.object({
    infoSectionIds: z.array(z.string()).min(1).max(10),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkAddInfoSectionsToProductsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkAddInfoSectionsToProductsRequest = z.object({
  products: z
    .array(
      z.object({
        productId: z.string().describe('Product ID.').min(1).max(36).optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z.object({
    infoSectionIds: z.array(z.string()).min(1).max(10),
    returnEntity: z
      .boolean()
      .describe(
        'Whether to return the full updated product entities in the response.\n\nDefault: `false`'
      )
      .optional(),
    fields: z
      .array(
        z.enum([
          'URL',
          'CURRENCY',
          'INFO_SECTION',
          'MERCHANT_DATA',
          'PLAIN_DESCRIPTION',
          'INFO_SECTION_PLAIN_DESCRIPTION',
          'SUBSCRIPTION_PRICES_INFO',
          'BREADCRUMBS_INFO',
          'WEIGHT_MEASUREMENT_UNIT_INFO',
          'VARIANT_OPTION_CHOICE_NAMES',
          'MEDIA_ITEMS_INFO',
          'DESCRIPTION',
          'DIRECT_CATEGORIES_INFO',
          'ALL_CATEGORIES_INFO',
          'INFO_SECTION_DESCRIPTION',
          'THUMBNAIL',
          'PRODUCT_CHOICES_MEDIA_REFERENCES',
        ])
      )
      .max(100)
      .optional(),
  }),
});
export const BulkAddInfoSectionsToProductsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Information about successful action or error for failure.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the product was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the product was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              description: z
                .any()
                .describe(
                  'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z
                                .enum(['IMAGE', 'VIDEO'])
                                .describe('Media type.')
                                .optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe(
                  'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Option render type.')
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ choicesSettings: z.never().optional() }),
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Modifier render type.')
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z.never().optional(),
                      }),
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              directCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              allCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              costRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                )
                .optional(),
              inventory: z
                .object({
                  availabilityStatus: z
                    .enum([
                      'IN_STOCK',
                      'OUT_OF_STOCK',
                      'PARTIALLY_OUT_OF_STOCK',
                    ])
                    .describe('Current availability status.')
                    .optional(),
                  preorderStatus: z
                    .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                    .describe('Current preorder status.')
                    .optional(),
                  preorderAvailability: z
                    .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                    .describe('Preorder availability status.')
                    .optional(),
                })
                .describe(
                  'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                )
                .optional(),
              productType: z
                .enum(['PHYSICAL', 'DIGITAL'])
                .describe(
                  'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
                )
                .optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              currency: z
                .string()
                .describe(
                  'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                )
                .optional()
                .nullable(),
              breadcrumbsInfo: z
                .object({
                  breadcrumbs: z
                    .array(
                      z.object({
                        categoryId: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        categoryName: z
                          .string()
                          .describe('Category name.')
                          .min(1)
                          .max(80)
                          .optional(),
                        categorySlug: z
                          .string()
                          .describe('Category slug.')
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe(
                  'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              actualPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum current selling prices across all product variants.'
                )
                .optional(),
              compareAtPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                )
                .optional(),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36)
                                      .optional(),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                  )
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .describe(
                                        'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                )
                                .optional(),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.')
                            .optional(),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          media: z
                            .intersection(
                              z.object({
                                altText: z
                                  .string()
                                  .describe('Image alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                displayName: z
                                  .string()
                                  .describe(
                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                  )
                                  .max(80)
                                  .optional()
                                  .nullable(),
                                mediaType: z
                                  .enum(['IMAGE', 'VIDEO'])
                                  .describe('Media type.')
                                  .optional(),
                                thumbnail: z
                                  .object({
                                    url: z
                                      .string()
                                      .describe('Thumbnail url.')
                                      .url()
                                      .optional(),
                                    height: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail height.')
                                      .optional(),
                                    width: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail width.')
                                      .optional(),
                                    altText: z
                                      .string()
                                      .describe('Thumbnail alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                                uploadId: z
                                  .string()
                                  .describe(
                                    'ID used to upload media to Wix Media Manager.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              }),
                              z.intersection(
                                z.xor([
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z.never().optional(),
                                  }),
                                  z.object({
                                    url: z.never().optional(),
                                    _id: z
                                      .string()
                                      .describe(
                                        'Set media by ID of an existing file in Wix Media Manager.'
                                      )
                                      .min(1)
                                      .max(200),
                                  }),
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z
                                      .string()
                                      .describe(
                                        'Set media using an external media URL.'
                                      )
                                      .url(),
                                  }),
                                ]),
                                z.xor([
                                  z.object({
                                    image: z.never().optional(),
                                    video: z.never().optional(),
                                  }),
                                  z.object({
                                    video: z.never().optional(),
                                    image: z
                                      .string()
                                      .describe(
                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                      ),
                                  }),
                                  z.object({
                                    image: z.never().optional(),
                                    video: z
                                      .string()
                                      .describe(
                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('Variant media.')
                            .optional(),
                          subscriptionPricesInfo: z
                            .object({
                              subscriptionPrices: z
                                .array(
                                  z.object({
                                    subscriptionId: z
                                      .string()
                                      .describe('Subscription ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                    price: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                      )
                                      .optional(),
                                    pricePerUnit: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Price per unit info.')
                                      .optional(),
                                  })
                                )
                                .max(6)
                                .optional(),
                            })
                            .describe(
                              'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          inventoryStatus: z
                            .object({
                              inStock: z
                                .boolean()
                                .describe('Whether the variant is in stock.')
                                .optional(),
                              preorderEnabled: z
                                .boolean()
                                .describe(
                                  'Whether preorder is enabled for this variant.'
                                )
                                .optional(),
                            })
                            .describe('Variant inventory status.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20)
                            .optional(),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe(
                              'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                            )
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({
                                type: z
                                  .enum(['AMOUNT', 'PERCENT'])
                                  .describe('Discount type.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z.never().optional(),
                                }),
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Amount to discount from the variant discounted_price.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage to discount from variant discounted_price.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z.never().optional(),
                          }),
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              variantSummary: z
                .object({
                  variantCount: z
                    .number()
                    .int()
                    .describe('The total number of variants for the product.')
                    .optional(),
                })
                .describe('The total number of variants for the product.')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full product entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkRemoveInfoSectionsFromProductsByFilterRequest = z.object({
  filter: z.record(z.string(), z.any()).describe('Filter object.'),
  options: z.object({
    infoSectionIds: z.array(z.string()).min(1).max(100),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkRemoveInfoSectionsFromProductsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkRemoveInfoSectionsFromProductsRequest = z.object({
  products: z
    .array(
      z.object({
        productId: z.string().describe('Product ID.').min(1).max(36).optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z.object({
    infoSectionIds: z.array(z.string()).min(1).max(100),
    returnEntity: z
      .boolean()
      .describe(
        'Whether to return the full updated product entities in the response.\n\nDefault: `false`'
      )
      .optional(),
    fields: z
      .array(
        z.enum([
          'URL',
          'CURRENCY',
          'INFO_SECTION',
          'MERCHANT_DATA',
          'PLAIN_DESCRIPTION',
          'INFO_SECTION_PLAIN_DESCRIPTION',
          'SUBSCRIPTION_PRICES_INFO',
          'BREADCRUMBS_INFO',
          'WEIGHT_MEASUREMENT_UNIT_INFO',
          'VARIANT_OPTION_CHOICE_NAMES',
          'MEDIA_ITEMS_INFO',
          'DESCRIPTION',
          'DIRECT_CATEGORIES_INFO',
          'ALL_CATEGORIES_INFO',
          'INFO_SECTION_DESCRIPTION',
          'THUMBNAIL',
          'PRODUCT_CHOICES_MEDIA_REFERENCES',
        ])
      )
      .max(100)
      .optional(),
  }),
});
export const BulkRemoveInfoSectionsFromProductsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Information about successful action or error for failure.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the product is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the product.\n\nIgnored when creating a product.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the product was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the product was updated.')
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Product name. Translatable.')
                .min(1)
                .max(80)
                .optional()
                .nullable(),
              slug: z
                .string()
                .describe(
                  'Product slug.\n\nIf not provided, the slug is autogenerated based on the product name.'
                )
                .optional()
                .nullable(),
              url: z
                .string()
                .describe(
                  'URL to the site\'s product page.\n\n> **Note:** Returned only when you pass `"URL"` to the `fields` array in Products API requests.'
                )
                .optional(),
              description: z
                .any()
                .describe(
                  'Product description using rich content.\n> **Note:** Returned only when you pass `"DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                )
                .optional(),
              plainDescription: z
                .string()
                .describe(
                  'Product description in HTML.\n\n+ When provided on create/update, this string must be valid HTML. It\'s then converted to rich content.\n+ `plainDescription` is ignored when a value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                )
                .max(16000)
                .optional()
                .nullable(),
              visible: z
                .boolean()
                .describe(
                  "Whether the product is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the default variant's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                )
                .optional()
                .nullable(),
              visibleInPos: z
                .boolean()
                .describe(
                  'Whether the product is visible in POS (point of sale).\n\nDefault: `true`\n> **Note:** Always `false` for `productType: DIGITAL`.'
                )
                .optional()
                .nullable(),
              media: z
                .object({
                  main: z
                    .intersection(
                      z.object({
                        altText: z
                          .string()
                          .describe('Image alt text.')
                          .min(1)
                          .max(1000)
                          .optional()
                          .nullable(),
                        displayName: z
                          .string()
                          .describe(
                            "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                          )
                          .max(80)
                          .optional()
                          .nullable(),
                        mediaType: z
                          .enum(['IMAGE', 'VIDEO'])
                          .describe('Media type.')
                          .optional(),
                        thumbnail: z
                          .object({
                            url: z
                              .string()
                              .describe('Thumbnail url.')
                              .url()
                              .optional(),
                            height: z
                              .number()
                              .int()
                              .describe('Thumbnail height.')
                              .optional(),
                            width: z
                              .number()
                              .int()
                              .describe('Thumbnail width.')
                              .optional(),
                            altText: z
                              .string()
                              .describe('Thumbnail alt text.')
                              .min(1)
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                          )
                          .optional(),
                        uploadId: z
                          .string()
                          .describe(
                            'ID used to upload media to Wix Media Manager.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                      }),
                      z.intersection(
                        z.xor([
                          z.object({
                            _id: z.never().optional(),
                            url: z.never().optional(),
                          }),
                          z.object({
                            url: z.never().optional(),
                            _id: z
                              .string()
                              .describe(
                                'Set media by ID of an existing file in Wix Media Manager.'
                              )
                              .min(1)
                              .max(200),
                          }),
                          z.object({
                            _id: z.never().optional(),
                            url: z
                              .string()
                              .describe(
                                'Set media using an external media URL.'
                              )
                              .url(),
                          }),
                        ]),
                        z.xor([
                          z.object({
                            image: z.never().optional(),
                            video: z.never().optional(),
                          }),
                          z.object({
                            video: z.never().optional(),
                            image: z
                              .string()
                              .describe(
                                "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                              ),
                          }),
                          z.object({
                            image: z.never().optional(),
                            video: z
                              .string()
                              .describe(
                                "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                              ),
                          }),
                        ])
                      )
                    )
                    .describe(
                      'Main media (image, video, etc.) associated with this product.\nAutomatically set to the first item in the media list.'
                    )
                    .optional(),
                  itemsInfo: z
                    .object({
                      items: z
                        .array(
                          z.intersection(
                            z.object({
                              altText: z
                                .string()
                                .describe('Image alt text.')
                                .min(1)
                                .max(1000)
                                .optional()
                                .nullable(),
                              displayName: z
                                .string()
                                .describe(
                                  "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                )
                                .max(80)
                                .optional()
                                .nullable(),
                              mediaType: z
                                .enum(['IMAGE', 'VIDEO'])
                                .describe('Media type.')
                                .optional(),
                              thumbnail: z
                                .object({
                                  url: z
                                    .string()
                                    .describe('Thumbnail url.')
                                    .url()
                                    .optional(),
                                  height: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail height.')
                                    .optional(),
                                  width: z
                                    .number()
                                    .int()
                                    .describe('Thumbnail width.')
                                    .optional(),
                                  altText: z
                                    .string()
                                    .describe('Thumbnail alt text.')
                                    .min(1)
                                    .max(1000)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                )
                                .optional(),
                              uploadId: z
                                .string()
                                .describe(
                                  'ID used to upload media to Wix Media Manager.'
                                )
                                .regex(
                                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                  'Must be a valid GUID'
                                )
                                .optional(),
                            }),
                            z.intersection(
                              z.xor([
                                z.object({
                                  _id: z.never().optional(),
                                  url: z.never().optional(),
                                }),
                                z.object({
                                  url: z.never().optional(),
                                  _id: z
                                    .string()
                                    .describe(
                                      'Set media by ID of an existing file in Wix Media Manager.'
                                    )
                                    .min(1)
                                    .max(200),
                                }),
                                z.object({
                                  _id: z.never().optional(),
                                  url: z
                                    .string()
                                    .describe(
                                      'Set media using an external media URL.'
                                    )
                                    .url(),
                                }),
                              ]),
                              z.xor([
                                z.object({
                                  image: z.never().optional(),
                                  video: z.never().optional(),
                                }),
                                z.object({
                                  video: z.never().optional(),
                                  image: z
                                    .string()
                                    .describe(
                                      "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                    ),
                                }),
                                z.object({
                                  image: z.never().optional(),
                                  video: z
                                    .string()
                                    .describe(
                                      "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                    ),
                                }),
                              ])
                            )
                          )
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'All media items.\n> **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.'
                    )
                    .optional(),
                })
                .describe(
                  'Product media items. For a detailed explanation of product media, see [About Product Media](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/about-product-media).'
                )
                .optional(),
              seoData: z
                .object({
                  tags: z
                    .array(
                      z.object({
                        type: z
                          .string()
                          .describe(
                            'SEO tag type.\n\n\nSupported values: `title`, `meta`, `script`, `link`.'
                          )
                          .optional(),
                        props: z
                          .record(z.string(), z.any())
                          .describe(
                            'A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.\nFor example: `{"name": "description", "content": "the description itself"}`.'
                          )
                          .optional()
                          .nullable(),
                        meta: z
                          .record(z.string(), z.any())
                          .describe(
                            'SEO tag metadata. For example, `{"height": 300, "width": 240}`.'
                          )
                          .optional()
                          .nullable(),
                        children: z
                          .string()
                          .describe(
                            'SEO tag inner content. For example, `<title> inner content </title>`.'
                          )
                          .optional(),
                        custom: z
                          .boolean()
                          .describe(
                            'Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages).'
                          )
                          .optional(),
                        disabled: z
                          .boolean()
                          .describe(
                            "Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines."
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  settings: z
                    .object({
                      preventAutoRedirect: z
                        .boolean()
                        .describe(
                          'Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.\n\n\nDefault: `false` (automatical redirect is enabled).'
                        )
                        .optional(),
                      keywords: z
                        .array(
                          z.object({
                            term: z
                              .string()
                              .describe('Keyword value.')
                              .optional(),
                            isMain: z
                              .boolean()
                              .describe(
                                'Whether the keyword is the main focus keyword.'
                              )
                              .optional(),
                            origin: z
                              .string()
                              .describe(
                                'The source that added the keyword terms to the SEO settings.'
                              )
                              .max(1000)
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(5)
                        .optional(),
                    })
                    .describe('SEO general settings.')
                    .optional(),
                })
                .describe('Product SEO data.')
                .optional(),
              taxGroupId: z
                .string()
                .describe(
                  '[Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              options: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: PRODUCT_OPTION`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Option name.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      optionRenderType: z
                        .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Option render type.')
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the option name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ choicesSettings: z.never().optional() }),
                      z.object({
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe(
                                        'Choice name. For text choices, this field also contains the choice value.'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    inStock: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is in stock in the default location of the store.\n\nFor example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.'
                                      )
                                      .optional(),
                                    visible: z
                                      .boolean()
                                      .describe(
                                        'Whether at least one variant with this choice is visible.\n\nDefault: `false`'
                                      )
                                      .optional(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choices settings.'),
                      }),
                    ])
                  )
                )
                .max(6)
                .optional(),
              modifiers: z
                .array(
                  z.intersection(
                    z.object({
                      _id: z
                        .string()
                        .describe(
                          'ID of a customization with `customizationType: MODIFIER`.'
                        )
                        .min(1)
                        .max(36)
                        .optional()
                        .nullable(),
                      name: z
                        .string()
                        .describe('Modifier title.')
                        .min(1)
                        .max(50)
                        .optional()
                        .nullable(),
                      modifierRenderType: z
                        .enum(['FREE_TEXT', 'TEXT_CHOICES', 'SWATCH_CHOICES'])
                        .describe('Modifier render type.')
                        .optional(),
                      mandatory: z
                        .boolean()
                        .describe(
                          'Whether customer input is required for this modifier.'
                        )
                        .optional(),
                      key: z
                        .string()
                        .describe(
                          'A read-only identifier generated from the modifier name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                        )
                        .min(1)
                        .max(50)
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z.never().optional(),
                      }),
                      z.object({
                        choicesSettings: z.never().optional(),
                        freeTextSettings: z
                          .object({
                            minCharCount: z
                              .number()
                              .int()
                              .describe('Minimum number of characters.')
                              .optional(),
                            maxCharCount: z
                              .number()
                              .int()
                              .describe('Maximum number of characters.')
                              .max(500)
                              .optional(),
                            defaultAddedPrice: z
                              .string()
                              .describe(
                                "Default amount to be added to the product's price."
                              )
                              .optional()
                              .nullable(),
                            title: z
                              .string()
                              .describe(
                                'Title of the text to be input by the customer.'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                            key: z
                              .string()
                              .describe(
                                'A read-only identifier generated from the title.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                              )
                              .min(1)
                              .max(150)
                              .optional(),
                          })
                          .describe('Free text modifier settings.'),
                      }),
                      z.object({
                        freeTextSettings: z.never().optional(),
                        choicesSettings: z
                          .object({
                            choices: z
                              .array(
                                z.intersection(
                                  z.object({
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional()
                                      .nullable(),
                                    linkedMedia: z
                                      .array(
                                        z.intersection(
                                          z.object({
                                            altText: z
                                              .string()
                                              .describe('Image alt text.')
                                              .min(1)
                                              .max(1000)
                                              .optional()
                                              .nullable(),
                                            displayName: z
                                              .string()
                                              .describe(
                                                "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                              )
                                              .max(80)
                                              .optional()
                                              .nullable(),
                                            mediaType: z
                                              .enum(['IMAGE', 'VIDEO'])
                                              .describe('Media type.')
                                              .optional(),
                                            thumbnail: z
                                              .object({
                                                url: z
                                                  .string()
                                                  .describe('Thumbnail url.')
                                                  .url()
                                                  .optional(),
                                                height: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail height.')
                                                  .optional(),
                                                width: z
                                                  .number()
                                                  .int()
                                                  .describe('Thumbnail width.')
                                                  .optional(),
                                                altText: z
                                                  .string()
                                                  .describe(
                                                    'Thumbnail alt text.'
                                                  )
                                                  .min(1)
                                                  .max(1000)
                                                  .optional()
                                                  .nullable(),
                                              })
                                              .describe(
                                                'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                              )
                                              .optional(),
                                            uploadId: z
                                              .string()
                                              .describe(
                                                'ID used to upload media to Wix Media Manager.'
                                              )
                                              .regex(
                                                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                                'Must be a valid GUID'
                                              )
                                              .optional(),
                                          }),
                                          z.intersection(
                                            z.xor([
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z.never().optional(),
                                              }),
                                              z.object({
                                                url: z.never().optional(),
                                                _id: z
                                                  .string()
                                                  .describe(
                                                    'Set media by ID of an existing file in Wix Media Manager.'
                                                  )
                                                  .min(1)
                                                  .max(200),
                                              }),
                                              z.object({
                                                _id: z.never().optional(),
                                                url: z
                                                  .string()
                                                  .describe(
                                                    'Set media using an external media URL.'
                                                  )
                                                  .url(),
                                              }),
                                            ]),
                                            z.xor([
                                              z.object({
                                                image: z.never().optional(),
                                                video: z.never().optional(),
                                              }),
                                              z.object({
                                                video: z.never().optional(),
                                                image: z
                                                  .string()
                                                  .describe(
                                                    "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                                  ),
                                              }),
                                              z.object({
                                                image: z.never().optional(),
                                                video: z
                                                  .string()
                                                  .describe(
                                                    "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                                  ),
                                              }),
                                            ])
                                          )
                                        )
                                      )
                                      .max(15)
                                      .optional(),
                                    choiceType: z
                                      .enum(['CHOICE_TEXT', 'ONE_COLOR'])
                                      .describe('Choice type.')
                                      .optional(),
                                    key: z
                                      .string()
                                      .describe(
                                        'A read-only identifier generated from the choice name.\n\nUse `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).'
                                      )
                                      .min(1)
                                      .max(50)
                                      .optional(),
                                    name: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .max(50)
                                      .optional()
                                      .nullable(),
                                    addedPrice: z
                                      .string()
                                      .describe('Added price.')
                                      .optional()
                                      .nullable(),
                                  }),
                                  z.xor([
                                    z.object({
                                      colorCode: z.never().optional(),
                                    }),
                                    z.object({
                                      colorCode: z
                                        .string()
                                        .describe(
                                          'Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).'
                                        )
                                        .min(3)
                                        .max(20),
                                    }),
                                  ])
                                )
                              )
                              .min(1)
                              .max(100)
                              .optional(),
                          })
                          .describe('Choice settings.'),
                      }),
                    ])
                  )
                )
                .max(10)
                .optional(),
              brand: z
                .object({
                  _id: z
                    .string()
                    .describe('Brand ID.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Brand name.')
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [brand](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/brands-v3/introduction).\n\n+ Pass `brand.name` to add a new brand while creating a product.\n+ Pass an existing brand's `id` to assign that brand to the product."
                )
                .optional(),
              infoSections: z
                .array(
                  z.object({
                    _id: z
                      .string()
                      .describe('Info section ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    uniqueName: z
                      .string()
                      .describe(
                        'Info section unique name.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .max(100)
                      .optional()
                      .nullable(),
                    title: z
                      .string()
                      .describe(
                        'Info section title.\n> **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.'
                      )
                      .min(1)
                      .max(50)
                      .optional()
                      .nullable(),
                    description: z
                      .any()
                      .describe(
                        'Info section description using rich content.\n> **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.\n\n<widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">\n<a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>\n</widget>'
                      )
                      .optional(),
                    plainDescription: z
                      .string()
                      .describe(
                        'Info section description in HTML.\n\nWhen provided on create/update, this string must be a valid HTML. It will then be converted to rich content.\n`plainDescription` is ignored when value is also passed to the `description` field.\n> **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.'
                      )
                      .max(16000)
                      .optional()
                      .nullable(),
                  })
                )
                .max(10)
                .optional(),
              ribbon: z
                .object({
                  _id: z
                    .string()
                    .describe('Ribbon ID.')
                    .min(1)
                    .max(36)
                    .optional()
                    .nullable(),
                  name: z
                    .string()
                    .describe('Ribbon name.')
                    .max(30)
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product [ribbon](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction).\n\n+ Pass `ribbon.name` to add a new ribbon while creating a product.\n+ Pass an existing ribbon's `id` or `name` to assign that ribbon to the product."
                )
                .optional(),
              directCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product.\n\nUpdated automatically when a product is added/removed from a category, when an item is moved within a category, or when a category is deleted.\n> **Note:** Returned only when you pass `"DIRECT_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              allCategoriesInfo: z
                .object({
                  categories: z
                    .array(
                      z.object({
                        _id: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        index: z
                          .number()
                          .int()
                          .describe(
                            'Index location of the product within the category, used for sorting products in a specific category. You can manually arrange up to 100 products per category. For detailed instructions, refer to the [Add and arrange products in a category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/products-v3/sample-use-cases-and-flows#add-and-arrange-products-in-a-category) sample flow.'
                          )
                          .min(0)
                          .max(200)
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(2000)
                    .optional(),
                })
                .describe(
                  'List of [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction) that directly contain this product, as well as their parent categories.\n> **Note:** Returned only when you pass `"ALL_CATEGORIES_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              mainCategoryId: z
                .string()
                .describe(
                  'The ID of the product\'s primary direct [category](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction), which defines the product\'s breadcrumbs path. For example, if the product\'s main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              costRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Product cost range - minimum and maximum costs of all product variants.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                )
                .optional(),
              inventory: z
                .object({
                  availabilityStatus: z
                    .enum([
                      'IN_STOCK',
                      'OUT_OF_STOCK',
                      'PARTIALLY_OUT_OF_STOCK',
                    ])
                    .describe('Current availability status.')
                    .optional(),
                  preorderStatus: z
                    .enum(['ENABLED', 'DISABLED', 'PARTIALLY_ENABLED'])
                    .describe('Current preorder status.')
                    .optional(),
                  preorderAvailability: z
                    .enum(['ALL_VARIANTS', 'NO_VARIANTS', 'SOME_VARIANTS'])
                    .describe('Preorder availability status.')
                    .optional(),
                })
                .describe(
                  'Product inventory info.\n\n> **Note:** This field reflects the aggregated inventory status from the default location only. Use the [Inventory Items API](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/inventory-items-v3/introduction) to manage inventory for specific locations. Learn more about [inventory management](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-inventory-management).'
                )
                .optional(),
              productType: z
                .enum(['PHYSICAL', 'DIGITAL'])
                .describe(
                  'Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.\n\nWhen passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, you can optionally pass `digitalProperties` in each variant.'
                )
                .optional(),
              handle: z
                .string()
                .describe(
                  "A unique human-friendly identifier for the product.\n\nUnlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation."
                )
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              currency: z
                .string()
                .describe(
                  'Currency used for the pricing of this product, in [ISO-4217](https://en.wikipedia.org/wiki/ISO_4217#List_of_ISO_4217_currency_codes) format.\n\nDefaults to the currency defined in the site settings, unless specified in the request\'s `x-wix-currency` header.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                )
                .optional()
                .nullable(),
              breadcrumbsInfo: z
                .object({
                  breadcrumbs: z
                    .array(
                      z.object({
                        categoryId: z
                          .string()
                          .describe('Category ID.')
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional(),
                        categoryName: z
                          .string()
                          .describe('Category name.')
                          .min(1)
                          .max(80)
                          .optional(),
                        categorySlug: z
                          .string()
                          .describe('Category slug.')
                          .min(1)
                          .max(100)
                          .optional(),
                      })
                    )
                    .max(5)
                    .optional(),
                })
                .describe(
                  'Breadcrumbs of the `mainCategoryId`. Used to navigate to parent [categories](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/categories/introduction). Useful for building navigation UI and for SEO purposes.\n> **Note:** Returned only when you pass `"BREADCRUMBS_INFO"` to the `fields` array in Products API requests.'
                )
                .optional(),
              actualPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum current selling prices across all product variants.'
                )
                .optional(),
              compareAtPriceRange: z
                .object({
                  minValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Minimum value.')
                    .optional(),
                  maxValue: z
                    .object({
                      amount: z
                        .string()
                        .describe(
                          'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                        )
                        .optional(),
                      formattedAmount: z
                        .string()
                        .describe(
                          'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                        )
                        .max(20)
                        .optional()
                        .nullable(),
                    })
                    .describe('Maximum value.')
                    .optional(),
                })
                .describe(
                  'Minimum and maximum compare-at prices (original prices before discounts) across all product variants. Used to show savings to customers.'
                )
                .optional(),
              variantsInfo: z
                .object({
                  variants: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Variant ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              "Whether the variant is visible to site visitors.\n\nDefault: `true`\n\n> **Note:** For products without options, updating this field automatically\n> updates the product's visibility to match.\n> For products with options, the product and variant visibility values are independent."
                            )
                            .optional()
                            .nullable(),
                          sku: z
                            .string()
                            .describe('Variant SKU (stock keeping unit).')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          barcode: z
                            .string()
                            .describe('Variant barcode.')
                            .min(1)
                            .max(40)
                            .optional()
                            .nullable(),
                          choices: z
                            .array(
                              z.object({
                                optionChoiceIds: z
                                  .object({
                                    optionId: z
                                      .string()
                                      .describe('Option ID.')
                                      .min(1)
                                      .max(36)
                                      .optional(),
                                    choiceId: z
                                      .string()
                                      .describe('Choice ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Option and choice IDs. These match the IDs of the option and choice from the product's options field."
                                  )
                                  .optional(),
                                optionChoiceNames: z
                                  .object({
                                    optionName: z
                                      .string()
                                      .describe('Option name.')
                                      .min(1)
                                      .optional(),
                                    choiceName: z
                                      .string()
                                      .describe('Choice name.')
                                      .min(1)
                                      .optional(),
                                    renderType: z
                                      .enum(['TEXT_CHOICES', 'SWATCH_CHOICES'])
                                      .describe(
                                        'Render type of the option. Required when using `optionChoiceNames`. Must match the `optionRenderType` of the referenced option.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Option and choice names.\n> **Note:** Returned only when you pass `"VARIANT_OPTION_CHOICE_NAMES"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                              })
                            )
                            .min(0)
                            .max(6)
                            .optional(),
                          price: z
                            .object({
                              actualPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Variant's current selling price. Must be greater than or equal to 0."
                                )
                                .optional(),
                              compareAtPrice: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings.'
                                )
                                .optional(),
                            })
                            .describe('Variant price.')
                            .optional(),
                          revenueDetails: z
                            .object({
                              cost: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe('Item cost.')
                                .optional(),
                              profit: z
                                .object({
                                  amount: z
                                    .string()
                                    .describe(
                                      'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                    )
                                    .optional(),
                                  formattedAmount: z
                                    .string()
                                    .describe(
                                      'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                    )
                                    .max(20)
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  'Profit. Calculated by reducing `cost` from `discountedPrice`.'
                                )
                                .optional(),
                              profitMargin: z
                                .number()
                                .describe(
                                  'Profit Margin. Calculated by dividing `profit` by `discountedPrice`.\nThe result is rounded to 4 decimal places.'
                                )
                                .min(0)
                                .max(1)
                                .optional(),
                            })
                            .describe(
                              'Variant revenue details.\n\n> **Note:** Returned only when the following conditions are met:\n> + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.\n> + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.'
                            )
                            .optional(),
                          media: z
                            .intersection(
                              z.object({
                                altText: z
                                  .string()
                                  .describe('Image alt text.')
                                  .min(1)
                                  .max(1000)
                                  .optional()
                                  .nullable(),
                                displayName: z
                                  .string()
                                  .describe(
                                    "Media display name.\n\nOverrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field."
                                  )
                                  .max(80)
                                  .optional()
                                  .nullable(),
                                mediaType: z
                                  .enum(['IMAGE', 'VIDEO'])
                                  .describe('Media type.')
                                  .optional(),
                                thumbnail: z
                                  .object({
                                    url: z
                                      .string()
                                      .describe('Thumbnail url.')
                                      .url()
                                      .optional(),
                                    height: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail height.')
                                      .optional(),
                                    width: z
                                      .number()
                                      .int()
                                      .describe('Thumbnail width.')
                                      .optional(),
                                    altText: z
                                      .string()
                                      .describe('Thumbnail alt text.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    'Media thumbnail.\n> **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.'
                                  )
                                  .optional(),
                                uploadId: z
                                  .string()
                                  .describe(
                                    'ID used to upload media to Wix Media Manager.'
                                  )
                                  .regex(
                                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                    'Must be a valid GUID'
                                  )
                                  .optional(),
                              }),
                              z.intersection(
                                z.xor([
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z.never().optional(),
                                  }),
                                  z.object({
                                    url: z.never().optional(),
                                    _id: z
                                      .string()
                                      .describe(
                                        'Set media by ID of an existing file in Wix Media Manager.'
                                      )
                                      .min(1)
                                      .max(200),
                                  }),
                                  z.object({
                                    _id: z.never().optional(),
                                    url: z
                                      .string()
                                      .describe(
                                        'Set media using an external media URL.'
                                      )
                                      .url(),
                                  }),
                                ]),
                                z.xor([
                                  z.object({
                                    image: z.never().optional(),
                                    video: z.never().optional(),
                                  }),
                                  z.object({
                                    video: z.never().optional(),
                                    image: z
                                      .string()
                                      .describe(
                                        "Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this istem's `id` field."
                                      ),
                                  }),
                                  z.object({
                                    image: z.never().optional(),
                                    video: z
                                      .string()
                                      .describe(
                                        "Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field."
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('Variant media.')
                            .optional(),
                          subscriptionPricesInfo: z
                            .object({
                              subscriptionPrices: z
                                .array(
                                  z.object({
                                    subscriptionId: z
                                      .string()
                                      .describe('Subscription ID.')
                                      .regex(
                                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                                        'Must be a valid GUID'
                                      )
                                      .optional(),
                                    price: z
                                      .object({
                                        amount: z
                                          .string()
                                          .describe(
                                            'Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.'
                                          )
                                          .optional(),
                                        formattedAmount: z
                                          .string()
                                          .describe(
                                            'Formatted monetary amount. For example, `"$3.99"`.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Subscription price calculated by applying subscription discount to the variant `price.actual_price`'
                                      )
                                      .optional(),
                                    pricePerUnit: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, `pricePerUnit` settings of this variant, and the variant subscription price.'
                                          )
                                          .optional(),
                                        description: z
                                          .string()
                                          .describe(
                                            'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                          )
                                          .max(20)
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Price per unit info.')
                                      .optional(),
                                  })
                                )
                                .max(6)
                                .optional(),
                            })
                            .describe(
                              'Subscription prices calculated by applying subscription discount to the variant `price.actual_price`.\n> **Note:** Returned only when you pass `"SUBSCRIPTION_PRICES_INFO"` to the `fields` array in Products API requests.'
                            )
                            .optional(),
                          inventoryStatus: z
                            .object({
                              inStock: z
                                .boolean()
                                .describe('Whether the variant is in stock.')
                                .optional(),
                              preorderEnabled: z
                                .boolean()
                                .describe(
                                  'Whether preorder is enabled for this variant.'
                                )
                                .optional(),
                            })
                            .describe('Variant inventory status.')
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z.never().optional(),
                          }),
                          z.object({
                            digitalProperties: z.never().optional(),
                            physicalProperties: z
                              .object({
                                weight: z
                                  .number()
                                  .describe(
                                    'Variant shipping weight.\nproduct.physicalProperties.shipping_weight_range values are taken from here.'
                                  )
                                  .min(0)
                                  .max(999999999.99)
                                  .optional()
                                  .nullable(),
                                pricePerUnit: z
                                  .object({
                                    settings: z
                                      .object({
                                        quantity: z
                                          .number()
                                          .describe(
                                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                                          )
                                          .min(0.01)
                                          .max(999999999.99)
                                          .optional(),
                                        measurementUnit: z
                                          .enum([
                                            'UNSPECIFIED',
                                            'ML',
                                            'CL',
                                            'L',
                                            'CBM',
                                            'MG',
                                            'G',
                                            'KG',
                                            'MM',
                                            'CM',
                                            'M',
                                            'SQM',
                                            'OZ',
                                            'LB',
                                            'FLOZ',
                                            'PT',
                                            'QT',
                                            'GAL',
                                            'IN',
                                            'FT',
                                            'YD',
                                            'SQFT',
                                          ])
                                          .describe(
                                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'Price per unit data for this variant.\n`measurementUnit` value must correspond to the measurement unit set on the product.'
                                      )
                                      .optional(),
                                    value: z
                                      .string()
                                      .describe(
                                        "Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                                      )
                                      .optional(),
                                    description: z
                                      .string()
                                      .describe(
                                        'Price per unit description.\n> **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.'
                                      )
                                      .max(100)
                                      .optional()
                                      .nullable(),
                                  })
                                  .describe(
                                    "Price per unit info, in order to show price per unit on the product page.\nFor example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.\nBut on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated."
                                  )
                                  .optional(),
                                productDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Product dimensions (length, width, height of the physical product).'
                                  )
                                  .optional(),
                                packageDimensions: z
                                  .object({
                                    length: z
                                      .string()
                                      .describe(
                                        'Length. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    width: z
                                      .string()
                                      .describe(
                                        'Width. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    height: z
                                      .string()
                                      .describe(
                                        'Height. Measurement unit is handled at the system level (metric/imperial).'
                                      )
                                      .optional()
                                      .nullable(),
                                    unit: z
                                      .enum([
                                        'UNKNOWN_DIMENSIONS_UNIT',
                                        'MM',
                                        'CM',
                                        'M',
                                        'IN',
                                        'FT',
                                        'YD',
                                      ])
                                      .describe(
                                        'Unit of measurement for dimensions (length, width, height).'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'Package dimensions (length, width, height of the shipping package).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Physical properties. Must be passed when `productType: PHYSICAL`'
                              ),
                          }),
                          z.object({
                            physicalProperties: z.never().optional(),
                            digitalProperties: z
                              .object({
                                digitalFile: z
                                  .object({
                                    _id: z
                                      .string()
                                      .describe('Media ID in media manager.')
                                      .min(1)
                                      .max(100)
                                      .optional(),
                                    fileName: z
                                      .string()
                                      .describe('Original file name.')
                                      .min(1)
                                      .max(1000)
                                      .optional(),
                                    fileSize: z
                                      .string()
                                      .describe('Original file size.')
                                      .min(1)
                                      .max(1000)
                                      .optional()
                                      .nullable(),
                                    fileType: z
                                      .enum([
                                        'UNSPECIFIED',
                                        'SECURE_PICTURE',
                                        'SECURE_VIDEO',
                                        'SECURE_DOCUMENT',
                                        'SECURE_MUSIC',
                                        'SECURE_ARCHIVE',
                                        'SECURE_RAW',
                                      ])
                                      .describe('File type.')
                                      .optional(),
                                  })
                                  .describe(
                                    'Digital file which will be downloaded by the buyer after successful purchase.\nEach variant can have a different digital file, allowing you to offer different content per variant.'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Digital properties. Must be passed when `productType: DIGITAL`'
                              ),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(1000)
                    .optional(),
                })
                .describe(
                  'Product variants. Each variant represents a specific purchasable version of a product defined by option choices.\n\nEach variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.\nYou must explicitly provide each variant when creating products with options.\n\nFor more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).'
                )
                .optional(),
              purchaseEligibility: z
                .object({ userCriteria: z.enum(['MEMBERS_ONLY']).optional() })
                .describe('Purchase eligibility settings for the product.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom extended fields for the product object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  publicTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe('Tags')
                .optional(),
              subscriptionDetails: z
                .object({
                  subscriptions: z
                    .array(
                      z.intersection(
                        z.object({
                          _id: z
                            .string()
                            .describe('Subscription ID.')
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          title: z
                            .string()
                            .describe('Subscription title.')
                            .min(1)
                            .max(20)
                            .optional(),
                          description: z
                            .string()
                            .describe('Subscription description.')
                            .max(60)
                            .optional()
                            .nullable(),
                          visible: z
                            .boolean()
                            .describe(
                              'Whether the subscription is visible to site visitors.\n\nDefault: `true`'
                            )
                            .optional()
                            .nullable(),
                          frequency: z
                            .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                            .describe(
                              'Frequency of recurring payment.\nFor example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.'
                            )
                            .optional(),
                          interval: z
                            .number()
                            .int()
                            .describe(
                              'Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.'
                            )
                            .min(1)
                            .max(50)
                            .optional()
                            .nullable(),
                          discount: z
                            .intersection(
                              z.object({
                                type: z
                                  .enum(['AMOUNT', 'PERCENT'])
                                  .describe('Discount type.')
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z.never().optional(),
                                }),
                                z.object({
                                  percentOff: z.never().optional(),
                                  amountOff: z
                                    .string()
                                    .describe(
                                      'Amount to discount from the variant discounted_price.'
                                    ),
                                }),
                                z.object({
                                  amountOff: z.never().optional(),
                                  percentOff: z
                                    .number()
                                    .describe(
                                      'Percentage to discount from variant discounted_price.'
                                    )
                                    .min(0.1)
                                    .max(99.9),
                                }),
                              ])
                            )
                            .describe(
                              'Discount info (optional).\nFor example, a $20 discount would be `amount: 20`, `type: AMOUNT`.'
                            )
                            .optional(),
                        }),
                        z.xor([
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z.never().optional(),
                          }),
                          z.object({
                            billingCycles: z.never().optional(),
                            autoRenewal: z
                              .boolean()
                              .describe(
                                'Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`.'
                              ),
                          }),
                          z.object({
                            autoRenewal: z.never().optional(),
                            billingCycles: z
                              .number()
                              .int()
                              .describe(
                                'Number of billing cycles before subscription ends.'
                              )
                              .min(2)
                              .max(999),
                          }),
                        ])
                      )
                    )
                    .min(1)
                    .max(6)
                    .optional(),
                  allowOneTimePurchases: z
                    .boolean()
                    .describe(
                      'Whether to allow one-time purchases in addition to subscription-based purchases.\n\nDefault: `false`'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Product subscriptions.\n\nSubscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price."
                )
                .optional(),
              variantSummary: z
                .object({
                  variantCount: z
                    .number()
                    .int()
                    .describe('The total number of variants for the product.')
                    .optional(),
                })
                .describe('The total number of variants for the product.')
                .optional(),
            }),
            z.xor([
              z.object({ physicalProperties: z.never().optional() }),
              z.object({
                physicalProperties: z
                  .object({
                    pricePerUnit: z
                      .object({
                        quantity: z
                          .number()
                          .describe(
                            'Quantity.\nFor example, to define price per per 100 grams, set this field to `100`.'
                          )
                          .min(0.01)
                          .max(999999999.99)
                          .optional(),
                        measurementUnit: z
                          .enum([
                            'UNSPECIFIED',
                            'ML',
                            'CL',
                            'L',
                            'CBM',
                            'MG',
                            'G',
                            'KG',
                            'MM',
                            'CM',
                            'M',
                            'SQM',
                            'OZ',
                            'LB',
                            'FLOZ',
                            'PT',
                            'QT',
                            'GAL',
                            'IN',
                            'FT',
                            'YD',
                            'SQFT',
                          ])
                          .describe(
                            'Measurement unit.\nFor example, to define price per 100 grams, set this field to "G".'
                          )
                          .optional(),
                      })
                      .describe(
                        'Price per unit settings.\n> **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.'
                      )
                      .optional(),
                    fulfillerId: z
                      .string()
                      .describe('Fulfiller ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    shippingWeightRange: z
                      .object({
                        minValue: z
                          .number()
                          .describe(
                            'Minimum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                        maxValue: z
                          .number()
                          .describe(
                            'Maximum weight across all variants associated with this product.'
                          )
                          .min(0)
                          .max(999999999.99)
                          .optional(),
                      })
                      .describe(
                        'Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.'
                      )
                      .optional(),
                    pricePerUnitRange: z
                      .object({
                        minValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Minimum price per unit across all variants.'
                          )
                          .optional(),
                        maxValue: z
                          .object({
                            value: z
                              .string()
                              .describe(
                                "Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.\nFor example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$)."
                              )
                              .optional(),
                            description: z
                              .string()
                              .describe(
                                'Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.\n> **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.'
                              )
                              .max(100)
                              .optional()
                              .nullable(),
                          })
                          .describe(
                            'Maximum price per unit across all variants.'
                          )
                          .optional(),
                      })
                      .describe(
                        'Product price per unit range. The minimum and maximum price per unit of all the variants.'
                      )
                      .optional(),
                    weightMeasurementUnitInfo: z
                      .object({
                        weightMeasurementUnit: z
                          .enum(['UNSPECIFIED_WEIGHT_UNIT', 'KG', 'LB'])
                          .describe('Weight measurement unit.')
                          .optional(),
                      })
                      .describe(
                        'Weight measurement unit.\n> **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.'
                      )
                      .optional(),
                    deliveryProfileId: z
                      .string()
                      .describe('Delivery profile ID.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Physical properties.\n\nRequired when `productType: PHYSICAL`.'
                  ),
              }),
            ])
          )
          .describe(
            'Full product entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkAddProductsToCategoriesByFilterRequest = z.object({
  options: z.object({
    filter: z
      .record(z.string(), z.any())
      .describe('Filter object.')
      .optional()
      .nullable(),
    categoryIds: z.array(z.string()).min(1).max(5),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkAddProductsToCategoriesByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const BulkRemoveProductsFromCategoriesByFilterRequest = z.object({
  options: z.object({
    filter: z
      .record(z.string(), z.any())
      .describe('Filter object.')
      .optional()
      .nullable(),
    categoryIds: z.array(z.string()).min(1).max(5),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkRemoveProductsFromCategoriesByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata..'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const GetAllProductsCategoryRequest = z.object({});
export const GetAllProductsCategoryResponse = z.object({
  categoryId: z
    .string()
    .describe(
      'ID of the "All Products" category automatically assigned to all products.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  treeReference: z
    .object({
      appNamespace: z
        .string()
        .describe(
          'Namespace of the app whose catalog this category tree belongs to.\n\nCurrently, only `"@wix/stores"` is supported.'
        )
        .min(4)
        .max(150)
        .optional(),
      treeKey: z
        .string()
        .describe(
          'Key to differentiate between multiple trees managed by the same app.\n\nFor Wix Stores, pass `null` or omit this field, as each store has only one category tree.'
        )
        .min(1)
        .max(44)
        .optional()
        .nullable(),
    })
    .describe(
      'Category tree reference. For Wix Stores, `appNamespace` is `"@wix/stores"` and `treeKey` is `null`.'
    )
    .optional(),
});
export const BulkUpdateProductTagsRequest = z.object({
  productIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to assign.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to unassign.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateProductTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .min(1)
              .max(36)
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single update operation.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Aggregated success/failure counts.')
    .optional(),
});
export const BulkUpdateProductTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter object. Pass an empty object `{}` to target all products.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to assign.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to unassign.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateProductTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
